import Conditions from '../../../../../resources/conditions';
import { UnreachableCode } from '../../../../../resources/not_reached';
import Outputs from '../../../../../resources/outputs';
import { callOverlayHandler } from '../../../../../resources/overlay_plugin_api';
import { Responses } from '../../../../../resources/responses';
import { DirectionOutputIntercard, Directions } from '../../../../../resources/util';
import ZoneId from '../../../../../resources/zone_id';
import { RaidbossData } from '../../../../../types/data';
import { OutputStrings, TriggerSet } from '../../../../../types/trigger';

// TODO: Verify EU/JP strategy configurations and Emergency Meeting (NA) strategy
// TODO: Replication 4 strategy use for stack/defamation bait locations?

export type Phase =
  | 'doorboss'
  | 'curtainCall'
  | 'slaughtershed'
  | 'replication1'
  | 'replication2'
  | 'reenactment1'
  | 'idyllic'
  | 'reenactment2';

export interface Data extends RaidbossData {
  readonly triggerSetConfig: {
    curtainCallStrat: 'ns' | 'none';
    uptimeKnockbackStrat: true | false;
    portentStrategy: 'dn' | 'zenith' | 'nukemaru' | 'none';
    replication2Strategy: 'dn' | 'banana' | 'nukemaru' | 'none';
    replication4Strategy: 'dn' | 'em' | 'caro' | 'nukemaru' | 'none';
  };
  phase: Phase;
  // Phase 1
  mortalSlayerGreenLeft: number;
  mortalSlayerGreenRight: number;
  mortalSlayerPurpleIsLeft?: boolean;
  ravenousReach1SafeSide?: 'east' | 'west';
  act1SafeCorner?: 'northeast' | 'northwest';
  curtainCallSafeCorner?: 'northeast' | 'northwest';
  splattershedStackDir?: 'northeast' | 'northwest';
  grotesquerieCleave?:
    | 'rightCleave'
    | 'leftCleave'
    | 'frontCleave'
    | 'rearCleave';
  myFleshBonds?: 'alpha' | 'beta';
  inLine: { [name: string]: number };
  blobTowerDirs: DirectionOutputIntercard[];
  cursedCoilDirNum?: number;
  skinsplitterCount: number;
  cellChainCount: number;
  myMitoticPhase?: string;
  hasRot: boolean;
  myCurtainCallSafeSpot?: 'northeast' | 'southeast' | 'southwest' | 'northwest';
  slaughtershed?: 'left' | 'right' | 'northeastKnockback' | 'northwestKnockback';
  // Phase 2
  actorPositions: { [id: string]: { x: number; y: number; heading: number } };
  replicationCounter: number;
  replication1Debuff?: 'fire' | 'dark';
  replication1FireDebuffCounter: number;
  replication1DarkDebuffCounter: number;
  replication1FireActor?: string;
  replication1FireActor2?: string;
  replication1FollowUp: boolean;
  replication2CloneDirNumPlayers: { [dirNum: number]: string };
  replication2DirNumAbility: { [dirNum: number]: string };
  replication2PlayerAbilities: { [player: string]: string };
  replication2BossId?: string;
  replication2PlayerOrder: string[];
  replication2AbilityOrder: string[];
  replication2StrategyDetected?: 'dn' | 'banana' | 'nukemaru' | 'unknown';
  netherwrathFollowup: boolean;
  myMutation?: 'alpha' | 'beta';
  manaSpheres: {
    [id: string]: 'lightning' | 'fire' | 'water' | 'wind' | 'blackHole';
  };
  westManaSpheres: { [id: string]: { x: number; y: number } };
  eastManaSpheres: { [id: string]: { x: number; y: number } };
  closeManaSphereIds: string[];
  firstBlackHole?: 'east' | 'west';
  manaSpherePopSide?: 'east' | 'west';
  twistedVisionCounter: number;
  replication3CloneOrder: number[];
  replication3CloneDirNumPlayers: { [dirNum: number]: string };
  idyllicVision2NorthSouthCleaveSpot?: 'north' | 'south';
  idyllicDreamActorEW?: string;
  idyllicDreamActorNS?: string;
  idyllicDreamActorSnaking?: string;
  replication4DirNumAbility: { [dirNum: number]: string };
  replication4PlayerAbilities: { [player: string]: string };
  replication4BossCloneDirNumPlayers: { [dirNum: number]: string };
  replication4PlayerOrder: string[];
  replication4AbilityOrder: string[];
  cosmicKissPattern: string[];
  hasLightResistanceDown: boolean;
  twistedVision4MechCounter: number;
  myCosmicKiss?: string;
  myPlatform?: 'east' | 'west';
  doomPlayers: string[];
  hasPyretic: boolean;
  idyllicVision8SafeSides?: 'frontBack' | 'sides';
  idyllicVision7SafeSides?: 'frontBack' | 'sides';
  idyllicVision7SafePlatform?: 'east' | 'west';
}

const headMarkerData = {
  // Phase 1
  // VFX: com_share3t
  'stack': '00A1',
  // VFX: tank_lockonae_6m_5s_01t
  'tankbuster': '0158',
  // VFX: VFX: x6rc_cellchain_01x
  'cellChain': '0291',
  // VFX: com_share3_7s0p
  'slaughterStack': '013D',
  // VFX: target_ae_s7k1
  'slaughterSpread': '0177',
  'cellChainTether': '016E',
  // Phase 2
  // VFX: sharelaser2tank5sec_c0k1, used by Double Sobat (B520)
  'sharedTankbuster': '0256',
  // Staging and Replication 2/4 Tethers
  'lockedTether': '0175', // Clone tethers
  'projectionTether': '016F', // Comes from Lindschrat, B4EA Grotesquerie + B4EB Hemorrhagic Projection cleave based on player facing
  'manaBurstTether': '0170', // Comes from Lindschrat, B4E7 Mana Burst defamation
  'heavySlamTether': '0171', // Comes from Lindschrat, B4E8 Heavy Slam stack with projection followup
  'fireballSplashTether': '0176', // Comes from the boss, B4E4 Fireball Splash baited jump
} as const;

const replication2OutputStrings: OutputStrings = {
  getTether: {
    en: 'Get Tether',
    de: 'Nimm Verbindung',
    cn: '接线',
    ko: '선 가져가기',
  },
  getBossTether: {
    en: 'Get Boss Tether',
    de: 'Nimm Boss-Verbindung',
    cn: '接 BOSS 线',
    ko: '보스 선 가져가기',
  },
  getConeTetherCW: {
    en: 'Get Clockwise Cone Tether',
    de: 'Nimm Kegel-Verbindung im Uhrzeigersinn',
    cn: '顺时针接扇形线',
    ko: '시계방향 부채꼴 선 가져가기',
  },
  getConeTetherCCW: {
    en: 'Get Counterclock Cone Tether',
    de: 'Nimm Kegel-Verbindung gegen den Uhrzeigersinn',
    cn: '逆时针接扇形线',
    ko: '반시계방향 부채꼴 선 가져가기',
  },
  getStackTetherCW: {
    en: 'Get Clockwise Stack Tether',
    de: 'Nimm Sammel-Verbindung im Uhrzeigersinn',
    cn: '顺时针接分摊线',
    ko: '시계방향 쉐어징 선 가져가기',
  },
  getStackTetherCCW: {
    en: 'Get Counterclock Stack Tether',
    de: 'Nimm Sammel-Verbindung gegen den Uhrzeigersinn',
    cn: '逆时针接分摊线',
    ko: '반시계방향 쉐어징 선 가져가기',
  },
  getDefamationTetherCW: {
    en: 'Get Clockwise Defamation Tether',
    de: 'Nimm Ehrenstrafe-Verbindung im Uhrzeigersinn',
    cn: '顺时针接大圈线',
    ko: '시계방향 광역징 선 가져가기',
  },
  getDefamationTetherCCW: {
    en: 'Get Counterclock Defamation Tether',
    de: 'Nimm Ehrenstrafe-Verbindung gegen den Uhrzeigersinn',
    cn: '逆时针接大圈线',
    ko: '반시계방향 광역징 선 가져가기',
  },
  getNoTether: {
    en: 'Get Nothing',
    de: 'Nichts nehmen',
    cn: '不接线',
    ko: '아무것도 가져가지 않기',
  },
  getTetherNClone: {
    en: '${tether}',
    de: '${tether}',
    cn: '${tether}',
    ko: '${tether}',
  },
  getTetherNEClone: {
    en: '${tether}',
    de: '${tether}',
    cn: '${tether}',
    ko: '${tether}',
  },
  getTetherEClone: {
    en: '${tether}',
    de: '${tether}',
    cn: '${tether}',
    ko: '${tether}',
  },
  getTetherSEClone: {
    en: '${tether}',
    de: '${tether}',
    cn: '${tether}',
    ko: '${tether}',
  },
  getTetherSClone: {
    en: '${tether}',
    de: '${tether}',
    cn: '${tether}',
    ko: '${tether}',
  },
  getTetherSWClone: {
    en: '${tether}',
    de: '${tether}',
    cn: '${tether}',
    ko: '${tether}',
  },
  getTetherWClone: {
    en: '${tether}',
    de: '${tether}',
    cn: '${tether}',
    ko: '${tether}',
  },
  getTetherNWClone: {
    en: '${tether}',
    de: '${tether}',
    cn: '${tether}',
    ko: '${tether}',
  },
};

// Replication 2: Map for retreiving index by direction
// Abilities are stored in replication2AbilityOrder with the following dirNum order:
// 0, 4, 1, 5, 2, 6, 3, 7
const rep2DirIndexMap = {
  'north': 0,
  'south': 1,
  'northeast': 2,
  'southwest': 3,
  'east': 4,
  'west': 5,
  'southeast': 6,
  'northwest': 7,
};

const center = {
  x: 100,
  y: 100,
} as const;

const phaseMap: { [id: string]: Phase } = {
  'BEC0': 'curtainCall',
  'B4C6': 'slaughtershed',
  'B509': 'idyllic',
};

const triggerSet: TriggerSet<Data> = {
  id: 'AacHeavyweightM4Savage',
  zoneId: ZoneId.AacHeavyweightM4Savage,
  config: [
    {
      id: 'curtainCallStrat',
      name: {
        en: 'Curtain Call Strategy',
        de: 'Telophase Strategie',
        cn: '门神末期策略',
        ko: '세포 부착: 말기 전략',
      },
      type: 'select',
      options: {
        en: {
          'North/Side Relative Strategy: North players go Northeast/Northwest, South players go relative to side.':
            'ns',
          'No strategy: Calls both safe spots.': 'none',
        },
        de: {
          'Nord-/Seiten Relative Strategie: Nordspieler gehen nach Nordosten/Nordwesten, Südspieler gehen relativ zur Seite.':
            'ns',
          'Keine Strategie: Ruft beide sicheren Stellen aus.': 'none',
        },
        cn: {
          '北/侧边相对策略: 北边玩家去东北/西北, 南边玩家以侧边为基准相对移动。': 'ns',
          '无策略: 仅播报两个安全区位置。': 'none',
        },
        ko: {
          '북쪽/양 옆 상대 전략: 북쪽 플레이어는 북동/북서, 남쪽 플레이어는 양 옆 기준으로 이동': 'ns',
          '전략 없음: 안전한 위치 둘 다 알림': 'none',
        },
      },
      default: 'none',
    },
    {
      id: 'uptimeKnockbackStrat',
      name: {
        en: 'Enable uptime knockback strat',
        de: 'Aktiviere Uptime Rückstoß Strategie',
        fr: 'Activer la strat Poussée-Uptime',
        ja: 'エデン零式共鳴編４層：cactbot「ヘヴンリーストライク (ノックバック)」ギミック', // FIXME
        cn: '启用击退 uptime 策略',
        ko: '정확한 타이밍 넉백방지 공략 사용',
        tc: '啟用擊退鏡 uptime 策略',
      },
      comment: {
        en: `If you want cactbot to callout Raptor Knuckles double knockback, enable this option.
             Callout happens during/after first animation and requires <1.8s reaction time
             to avoid both Northwest and Northeast knockbacks.
             NOTE: This will call for each set.`,
        de:
          `Wenn Sie möchten, dass Cactbot Berstender Knöchel Doppel Rückstoß ansagt, aktivieren Sie diese Option.
             Die Ansage erfolgt während/nach der ersten Animation und erfordert eine Reaktionszeit von <1,8 Sekunden,
             um sowohl Nordwesten- als auch Nordosten-Rückstoß zu vermeiden.
             HINWEIS: Dies wird für jeden Satz aufgerufen.`,
        cn: `如需 cactbot 播报追猎重击双击退, 请启用此选项。
             播报时机为第一次动画期间/之后, 需要<1.8秒反应时间
             才能同时躲避西北和东北两次击退。
             注意: 每轮播报一次。`,
        ko: `cactbot이 맹수의 주먹 이중 넉백 알람을 불러주게 하려면 이 옵션을 활성화하세요.
             첫 번째 애니메이션 중/후에 알림이 발생하며,
             북서쪽과 북동쪽 넉백을 모두 피하려면 1.8초 미만의 반응 시간이 필요합니다.
             참고: 각 세트마다 호출됩니다.`,
      },
      type: 'checkbox',
      default: false,
    },
    {
      id: 'replication2Strategy',
      name: {
        en: 'Replication 2 Strategy',
        de: 'Zellteilung 2 Strategie',
        cn: '本体二运策略',
        ko: '자가 복제 2 전략',
      },
      type: 'select',
      options: {
        en: {
          'DN Strategy: Boss North, Cones NE/NW, Stacks E/W, Defamations SE/SW, Nothing South':
            'dn',
          'Banana Codex Strategy: Boss West, Stacks NW/SW, Cones N/S, Defamations NE/SE, Nothing E':
            'banana',
          'Nukemaru Strategy: Boss East, Stacks NE/SE, Cones N/S, Defamations NW/SW, Nothing W':
            'nukemaru',
          'No strategy: Calls the tether you may have and to get a tether.': 'none',
        },
        de: {
          'DN-Strategie: Boss Nord, Kegel NO/NW, Sammeln O/W, Ehrenstrafe SO/SW, nichts Süd': 'dn',
          'Banana Codex-Strategie: Boss West, Sammeln NW/SW, Kegel N/S, Ehrenstrafe NO/SO, nichts O':
            'banana',
          'Nukemaru-Strategie: Boss Ost, Sammeln NO/SO, Kegel N/S, Ehrenstrafe NW/SW, nichts W':
            'nukemaru',
          'Keine Strategie: Ruft den Tether, den Sie möglicherweise haben, und fordert, einen Tether zu holen.':
            'none',
        },
        cn: {
          'DN 策略: Boss北侧, 扇形东北/西北, 分摊东/西, 大圈东南/西南, 无点名南侧': 'dn',
          'Banana Codex 策略: Boss 西侧, 分摊西北/西南, 扇形北/南, 大圈东北/东南, 无点名东侧': 'banana',
          'Nukemaru 策略: Boss 东侧, 分摊东北/东南, 扇形北/南, 大圈西北/西南, 无点名西侧': 'nukemaru',
          '无策略: 播报你可能获得的连线, 并提示获取连线': 'none',
        },
        ko: {
          'DN: 보스 북, 부채꼴 북동/북서, 쉐어 동/서, 광역징 남동/남서, 무징 남': 'dn',
          '바나나 코덱스: 보스 서, 쉐어 북서/남서, 부채꼴 북/남, 광역징 북동/남동, 무징 동': 'banana',
          '누케마루: 보스 동, 쉐어 북동/남동, 부채꼴 북/남, 광역징 북서/남서, 무징 서': 'nukemaru',
          '전략 없음: 자신에게 걸린 선과 선 가져가기만 알림': 'none',
        },
      },
      default: 'none',
    },
    {
      id: 'replication4Strategy',
      name: {
        en: 'Replication 4 (Idyllic Dream) Strategy',
        de: 'Zellteilung 4 (Arkadischer Traum) Strategie',
        cn: '本体四运 (境中奇梦) 策略',
        ko: '자가 복제 4 (아르카디아의 꿈) 전략',
      },
      type: 'select',
      options: {
        en: {
          'DN Strategy: N, NE, E, SE Staging 2 Tethers Grab Stacks, S, SW, W, NW Staging 2 Tethers Grab Defamations. Split party into 4 intercardinal quadrants.':
            'dn',
          'Emergency Meeting Strategy: N, NE, E, NW Stacks, SE, S, SW, W Defamations. Split party Red/Purple + Yellow/Blue markers and swaps based on Stack or Defamation being first':
            'em',
          'Caro Strategy: NE, E, SW, W Staging 2 Tethers Grab Stacks, N, SE, S, NW Staging 2 Tethers Grab Defamations. Split party into 4 intercardinal quadrants.':
            'caro',
          'Nukemaru Strategy: N, SW, W, NW Staging 2 Tethers Grab Stacks, NE, E, SE, S Staging 2 Tethers Grab Defamations. Split party into 4 intercardinal quadrants.':
            'nukemaru',
          'No strategy: Calls the tether you may have and to get a tether.': 'none',
        },
        cn: {
          'DN 策略: 北、东北、东、东南两线一组接分摊, 南、西南、西、西北两线一组接大圈。队伍分为4个斜向象限': 'dn',
          'Emergency Meeting 策略: 北、东北、东、西北接分摊, 东南、南、西南、西接大圈。队伍按红/紫+黄/蓝标记分组, 根据先分摊还是先大圈进行交换': 'em',
          'Caro 策略: 东北、东、西南、西两线一组接分摊, 北、东南、南、西北两线一组接大圈。队伍分为4个斜向象限': 'caro',
          'Nukemaru 策略: 北、西南、西、西北两线一组接分摊, 东北、东、东南、南两线一组接大圈。队伍分为4个斜向象限': 'nukemaru',
          '无策略: 播报你可能获得的连线, 并提示获取连线': 'none',
        },
        ko: {
          'DN: 북, 북동, 동, 남동 분신 대상자 쉐어징, 남, 남서, 서, 북서 분신 대상자 광역징. 파티를 4개의 대각선 구역으로 나눕니다.': 'dn',
          'Emergency Meeting: 북, 북동, 동, 북서 쉐어, 남동, 남, 남서, 서 광역징. 파티를 빨강/보라 + 노랑/파랑 징으로 나누고 쉐어 또는 광역징이 먼저인지에 따라 위치를 바꿉니다.':
            'em',
          '카로: 북동, 동, 남서, 서 분신 대상자 쉐어징, 북, 남동, 남, 북서 분신 대상자 광역징. 파티를 4개의 대각선 구역으로 나눕니다.': 'caro',
          '누케마루: 북, 남서, 서, 북서 분신 대상자 쉐어징, 북동, 동, 남동, 남 분신 대상자 광역징. 파티를 4개의 대각선 구역으로 나눕니다.':
            'nukemaru',
          '전략 없음: 자신에게 걸린 선과 선 가져가기만 알림': 'none',
        },
      },
      default: 'none',
    },
    {
      id: 'portentStrategy',
      name: {
        en: 'Phase 2 Tower Portent Resolution Strategy',
        de: 'Phase 2 Turm Omenschlag Lösungs Strategy',
        cn: '本体预兆塔解决策略',
        ko: '2페이즈 탑 디버프 해결 전략',
      },
      type: 'select',
      options: {
        en: {
          'DN Strategy: Dark N Hitbox, Wind Middle Hitbox, Earth/Fire Melee N of Platform (Between Dark/Wind), Fire/Earth Range S Edge of Platform':
            'dn',
          'Zenith Strategy: Wind N Max Melee, Earth/Dark Middle (Lean North), Fire S Max Melee':
            'zenith',
          'Nukemaru Strategy: Near S (corner of numbered marker), Far S on Boss Hitbox, Earth/Fire Melee S Max Melee, Fire/Earth Range N of Platform':
            'nukemaru',
          'No strategy: call element and debuff': 'none',
        },
        de: {
          'DN-Strategie: Dunkelheit N Hitbox, Wind Mitte Hitbox, Erde/Feuer N/S Max Nahkampf': 'dn',
          'Zenith-Strategie: Wind N Max Nahkampf, Erde/Dunkelheit Mitte (Lehnen nach Norden), Feuer S Max Nahkampf':
            'zenith',
          'Keine Strategie: Element und Debuff ausrufen': 'none',
        },
        cn: {
          'DN 策略: 暗北判定点, 风中间判定点, 土/火平台北侧近战位(暗与风之间), 火/土平台南侧边缘远程位': 'dn',
          'Zenith 策略: 风北最远近战位, 土/暗中间位 (偏北), 火南最远近战位': 'zenith',
          'Nukemaru 策略: 近线南侧(数字标记角落), 远线 Boss 南侧判定点, 土/火南最远近战位, 火/土平台北侧远程位': 'nukemaru',
          '无策略: 播报元素与 debuff': 'none',
        },
        ko: {
          'DN: 어둠 - 북 히트박스, 바람 - 중앙 히트박스, 땅/불 근딜 - 플랫폼 북쪽 (어둠/바람 사이), 불/땅 원딜 - 플랫폼 남쪽 가장자리': 'dn',
          'Zenith: 바람 - 북 칼끝딜, 땅/어둠 - 중앙 (약간 북쪽), 불 - 남 칼끝딜': 'zenith',
          '누케마루: 가까이 - 남쪽 (숫자징 구석), 멀리 - 보스 히트박스, 땅/불 - 남쪽 칼끝딜, 불/땅 - 플랫폼 북쪽': 'nukemaru',
          '전략 없음: 속성과 디버프만 알림': 'none',
        },
      },
      default: 'none',
    },
  ],
  timelineFile: 'r12s.txt',
  initData: () => ({
    phase: 'doorboss',
    // Phase 1
    mortalSlayerGreenLeft: 0,
    mortalSlayerGreenRight: 0,
    inLine: {},
    blobTowerDirs: [],
    skinsplitterCount: 0,
    cellChainCount: 0,
    hasRot: false,
    // Phase 2
    actorPositions: {},
    replicationCounter: 0,
    replication1FireDebuffCounter: 0,
    replication1DarkDebuffCounter: 0,
    replication1FollowUp: false,
    replication2CloneDirNumPlayers: {},
    replication2DirNumAbility: {},
    replication2PlayerAbilities: {},
    replication2PlayerOrder: [],
    replication2AbilityOrder: [],
    netherwrathFollowup: false,
    manaSpheres: {},
    westManaSpheres: {},
    eastManaSpheres: {},
    closeManaSphereIds: [],
    twistedVisionCounter: 0,
    replication3CloneOrder: [],
    replication3CloneDirNumPlayers: {},
    replication4DirNumAbility: {},
    replication4PlayerAbilities: {},
    replication4BossCloneDirNumPlayers: {},
    replication4PlayerOrder: [],
    replication4AbilityOrder: [],
    cosmicKissPattern: [],
    hasLightResistanceDown: false,
    twistedVision4MechCounter: 0,
    doomPlayers: [],
    hasPyretic: false,
  }),
  triggers: [
    {
      id: 'R12S Phase Tracker',
      type: 'StartsUsing',
      netRegex: { id: Object.keys(phaseMap), source: 'Lindwurm' },
      suppressSeconds: 1,
      run: (data, matches) => {
        const phase = phaseMap[matches.id];
        if (phase === undefined)
          throw new UnreachableCode();

        data.phase = phase;
      },
    },
    {
      id: 'R12S Phase Two Staging Tracker',
      // Due to the way the combatants are added in prior to the cast of Staging, this is used to set the phase
      type: 'AddedCombatant',
      netRegex: { name: 'Understudy', capture: false },
      condition: (data) => data.phase === 'replication1',
      run: (data) => data.phase = 'replication2',
    },
    {
      id: 'R12S Phase Two Replication Tracker',
      type: 'StartsUsing',
      netRegex: { id: 'B4D8', source: 'Lindwurm', capture: false },
      run: (data) => {
        if (data.replicationCounter === 0)
          data.phase = 'replication1';
        data.replicationCounter = data.replicationCounter + 1;
      },
    },
    {
      id: 'R12S Phase Two Boss ID Collect',
      // Store the boss' id later for checking against tether
      // Using first B4E1 Staging
      type: 'StartsUsing',
      netRegex: { id: 'B4E1', source: 'Lindwurm', capture: true },
      condition: (data) => data.phase === 'replication2',
      suppressSeconds: 9999,
      run: (data, matches) => data.replication2BossId = matches.sourceId,
    },
    {
      id: 'R12S Phase Two Reenactment Tracker',
      type: 'StartsUsing',
      netRegex: { id: 'B4EC', source: 'Lindwurm', capture: false },
      run: (data) => {
        if (data.phase === 'replication2') {
          data.phase = 'reenactment1';
          return;
        }
        data.phase = 'reenactment2';
      },
    },
    {
      id: 'R12S Phase Two Twisted Vision Tracker',
      // Used for keeping track of phases in idyllic
      type: 'StartsUsing',
      netRegex: { id: 'BBE2', source: 'Lindwurm', capture: false },
      run: (data) => {
        data.twistedVisionCounter = data.twistedVisionCounter + 1;
      },
    },
    {
      id: 'R12S ActorSetPos Tracker',
      // Only in use for replication 1, 2, and idyllic phases
      type: 'ActorSetPos',
      netRegex: { id: '4[0-9A-Fa-f]{7}', capture: true },
      run: (data, matches) =>
        data.actorPositions[matches.id] = {
          x: parseFloat(matches.x),
          y: parseFloat(matches.y),
          heading: parseFloat(matches.heading),
        },
    },
    {
      id: 'R12S ActorMove Tracker',
      // Only in use for replication 1, 2, and idyllic phases
      type: 'ActorMove',
      netRegex: { id: '4[0-9A-Fa-f]{7}', capture: true },
      run: (data, matches) =>
        data.actorPositions[matches.id] = {
          x: parseFloat(matches.x),
          y: parseFloat(matches.y),
          heading: parseFloat(matches.heading),
        },
    },
    {
      id: 'R12S AddedCombatant Tracker',
      // Only in use for replication 1, 2, and idyllic phases
      type: 'AddedCombatant',
      netRegex: { id: '4[0-9A-Fa-f]{7}', capture: true },
      run: (data, matches) =>
        data.actorPositions[matches.id] = {
          x: parseFloat(matches.x),
          y: parseFloat(matches.y),
          heading: parseFloat(matches.heading),
        },
    },
    {
      id: 'R12S The Fixer',
      type: 'StartsUsing',
      netRegex: { id: 'B4D7', source: 'Lindwurm', capture: false },
      durationSeconds: 4.7,
      response: Responses.bigAoe('alert'),
    },
    {
      id: 'R12S Mortal Slayer Collect',
      // 19200 Purple Orb
      // 19201 Green Orb
      type: 'AddedCombatant',
      netRegex: { name: 'Lindwurm', npcBaseId: ['19200', '19201'], capture: true },
      run: (data, matches) => {
        const npcBaseId = matches.npcBaseId;
        const x = parseFloat(matches.x);

        // 4 Green Orbs on one side = we know where purple will be
        if (npcBaseId === '19201') {
          if (x < 100) {
            data.mortalSlayerGreenLeft = data.mortalSlayerGreenLeft + 1;
            if (
              data.mortalSlayerGreenLeft === 4 &&
              data.mortalSlayerPurpleIsLeft === undefined
            )
              data.mortalSlayerPurpleIsLeft = false;
          } else if (x > 100) {
            data.mortalSlayerGreenRight = data.mortalSlayerGreenRight + 1;
            if (
              data.mortalSlayerGreenRight === 4 &&
              data.mortalSlayerPurpleIsLeft === undefined
            )
              data.mortalSlayerPurpleIsLeft = true;
          }
        } else if (
          npcBaseId === '19200' &&
          data.mortalSlayerPurpleIsLeft === undefined
        )
          data.mortalSlayerPurpleIsLeft = x < 100 ? true : false;
      },
    },
    {
      id: 'R12S Mortal Slayer Tank Side',
      type: 'AddedCombatant',
      netRegex: { name: 'Lindwurm', npcBaseId: ['19200', '19201'], capture: false },
      condition: (data) => {
        if (data.mortalSlayerPurpleIsLeft !== undefined)
          return true;
        return false;
      },
      suppressSeconds: 12, // castTime of Mortal Slayer B495
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          tanksLeft: {
            en: 'Tanks Left',
            de: 'Tanks Links',
            cn: 'T 左',
            ko: '탱커 왼쪽',
          },
          tanksRight: {
            en: 'Tanks Right',
            de: 'Tanks Rechts',
            cn: 'T 右',
            ko: '탱커 오른쪽',
          },
        };
        const severity = data.role === 'tank' ? 'alertText' : 'infoText';
        return {
          [severity]: data.mortalSlayerPurpleIsLeft
            ? output.tanksLeft!()
            : output.tanksRight!(),
        };
      },
    },
    {
      id: 'R12S Mortal Slayer Cleanup',
      // Reset trackers for second Mortal Slayer
      type: 'Ability',
      netRegex: { id: 'B495', capture: false },
      suppressSeconds: 9999,
      run: (data) => {
        data.mortalSlayerGreenLeft = 0;
        data.mortalSlayerGreenRight = 0;
        delete data.mortalSlayerPurpleIsLeft;
      },
    },
    {
      id: 'R12S CombatantMemory Blob Tracker',
      // Appears in Act 1, Curtain Call, and Slaughtershed phases
      // 1EBF29 are the blobs
      //
      // Act 1 Pattern 1 (NW/E):
      //                      (112.5, 88.63)
      //        (95.93, 91.36)
      //    (89.93, 100.13)
      //                   (109.18, 109.63)
      //     (90.67, 112.13)
      //
      // Act 1 Pattern 2 (NE/W) + a sliver of space along NE to E:
      //    (88.42, 89.19)
      //                 (107.02, 92.19)
      //                      (112.5, 105.69)
      //         (94.02, 108.53)
      // (82.34, 113.53)
      //
      // Curtain Call Pattern 1 (NW/SE):
      //                  (110, 87)
      // (85, 100) (100, 100) (115, 100)
      //      (91, 113)
      //
      // Curtain Call Pattern 2 (NE/SW):
      //      (91, 87)
      // (85, 100) (100, 100) (115, 100)
      //                 (109, 113)
      //
      // Splattershed (Two Patterns):
      //   Blob at (110.75, 96.50) => Spreads Northwest, Stacks Northeast
      //   Blob at (89.25, 96.40) => Spreads Northeast, Stacks Northwest
      //   The remaining blobs are always in these locations:
      //                   (100.8, 92)
      //   (86.5, 105.9) (102.5, 106.4) (118.5, 106.16)
      type: 'CombatantMemory',
      netRegex: {
        change: 'Add',
        pair: [{ key: 'BNpcID', value: '1EBF29' }],
        capture: true,
      },
      run: (data, matches) => {
        // No need to check remaining blobs if we already found safe spot
        if (data.splattershedStackDir)
          return;
        const x = parseFloat(matches.pairPosX ?? '0');
        const y = parseFloat(matches.pairPosY ?? '0');

        // The following are unique coordinates for each phase
        // Undefined checks to skip additional position checking
        if (data.act1SafeCorner === undefined && y > 87.9 && y < 89.7) {
          // Act 1 Safe Corner
          // Most strategies have tanks move and stack tankbusters regardless of pattern
          // Defining safe corner for purpose of labelling the pattern
          if (x > 112)
            data.act1SafeCorner = 'northwest';
          else if (x < 89)
            data.act1SafeCorner = 'northeast';
        } else if (
          data.act1SafeCorner !== undefined &&
          data.curtainCallSafeCorner === undefined &&
          y > 86.5 && y < 87.5
        ) {
          // Curtain Call Safe Spots
          if (x < 92)
            data.curtainCallSafeCorner = 'northeast';
          else if (x > 109)
            data.curtainCallSafeCorner = 'northwest';
        } else if (
          data.act1SafeCorner !== undefined &&
          data.curtainCallSafeCorner !== undefined &&
          y > 96 && y < 97
        ) {
          // Splattershed Stack Spot
          if (x > 88.75 && x < 89.75)
            data.splattershedStackDir = 'northwest';
          else if (x > 110.25 && x < 111.25)
            data.splattershedStackDir = 'northeast';
        }
      },
    },
    {
      id: 'R12S Directed Grotesquerie Direction Collect',
      // Unknown_DE6 spell contains data in its count:
      // 40C, Front Cone
      // 40D, Right Cone
      // 40E, Rear Cone
      // 40F, Left Cone
      type: 'GainsEffect',
      netRegex: { effectId: 'DE6', capture: true },
      condition: Conditions.targetIsYou(),
      run: (data, matches) => {
        switch (matches.count) {
          case '40C':
            data.grotesquerieCleave = 'frontCleave';
            return;
          case '40D':
            data.grotesquerieCleave = 'rightCleave';
            return;
          case '40E':
            data.grotesquerieCleave = 'rearCleave';
            return;
          case '40F':
            data.grotesquerieCleave = 'leftCleave';
            return;
        }
      },
    },
    {
      id: 'R12S Shared Grotesquerie',
      type: 'GainsEffect',
      netRegex: { effectId: '129A', capture: true },
      delaySeconds: 0.2,
      durationSeconds: 17,
      infoText: (data, matches, output) => {
        const cleave = data.grotesquerieCleave;
        const target = matches.target;
        if (target === data.me) {
          if (cleave === undefined)
            return output.baitThenStack!({ stack: output.stackOnYou!() });
          return output.baitThenStackCleave!({
            stack: output.stackOnYou!(),
            cleave: output[cleave]!(),
          });
        }

        const player = data.party.member(target);
        const isDPS = data.party.isDPS(target);
        if (isDPS && data.role === 'dps') {
          if (cleave === undefined)
            return output.baitThenStack!({
              stack: output.stackOnPlayer!({ player: player }),
            });
          return output.baitThenStackCleave!({
            stack: output.stackOnPlayer!({ player: player }),
            cleave: output[cleave]!(),
          });
        }
        if (!isDPS && data.role !== 'dps') {
          if (cleave === undefined)
            return output.baitThenStack!({
              stack: output.stackOnPlayer!({ player: player }),
            });
          return output.baitThenStackCleave!({
            stack: output.stackOnPlayer!({ player: player }),
            cleave: output[cleave]!(),
          });
        }
      },
      outputStrings: {
        stackOnYou: Outputs.stackOnYou,
        stackOnPlayer: Outputs.stackOnPlayer,
        frontCleave: {
          en: 'Front Cleave',
          de: 'Kegel Aoe nach Vorne',
          fr: 'Cleave Avant',
          ja: '口からおくび',
          cn: '向前射',
          ko: '전방 부채꼴',
          tc: '前方扇形',
        },
        rearCleave: {
          en: 'Rear Cleave',
          de: 'Kegel Aoe nach Hinten',
          fr: 'Cleave Arrière',
          ja: '尻からおなら',
          cn: '向后射',
          ko: '후방 부채꼴',
          tc: '背後扇形',
        },
        leftCleave: {
          en: 'Left Cleave',
          de: 'Linker Cleave',
          fr: 'Cleave gauche',
          ja: '左半面へ攻撃',
          cn: '向左射',
          ko: '왼쪽 부채꼴',
          tc: '左刀',
        },
        rightCleave: {
          en: 'Right Cleave',
          de: 'Rechter Cleave',
          fr: 'Cleave droit',
          ja: '右半面へ攻撃',
          cn: '向右射',
          ko: '오른쪽 부채꼴',
          tc: '右刀',
        },
        baitThenStack: {
          en: 'Bait 4x Puddles => ${stack}',
          de: 'Ködere Fläche x4 => ${stack}',
          cn: '诱导4轮黄圈 => ${stack}',
          ko: '장판 유도 4x => ${stack}',
        },
        baitThenStackCleave: {
          en: 'Bait 4x Puddles => ${stack} + ${cleave}',
          de: 'Ködere Fläche x4 => ${stack} + ${cleave}',
          cn: '诱导4轮黄圈 + ${stack} + ${cleave}',
          ko: '장판 유도 4x => ${stack} + ${cleave}',
        },
      },
    },
    {
      id: 'R12S Bursting Grotesquerie',
      type: 'GainsEffect',
      netRegex: { effectId: '1299', capture: true },
      condition: Conditions.targetIsYou(),
      delaySeconds: 0.2,
      durationSeconds: 17,
      infoText: (data, _matches, output) => {
        const cleave = data.grotesquerieCleave;
        if (cleave === undefined)
          return data.phase === 'doorboss'
            ? output.baitThenSpread!()
            : output.spreadCurtain!();
        return data.phase === 'doorboss'
          ? output.baitThenSpreadCleave!({ cleave: output[cleave]!() })
          : output.spreadCurtain!();
      },
      outputStrings: {
        frontCleave: {
          en: 'Front Cleave',
          de: 'Kegel Aoe nach Vorne',
          fr: 'Cleave Avant',
          ja: '口からおくび',
          cn: '向前射',
          ko: '전방 부채꼴',
          tc: '前方扇形',
        },
        rearCleave: {
          en: 'Rear Cleave',
          de: 'Kegel Aoe nach Hinten',
          fr: 'Cleave Arrière',
          ja: '尻からおなら',
          cn: '向后射',
          ko: '후방 부채꼴',
          tc: '背後扇形',
        },
        leftCleave: {
          en: 'Left Cleave',
          de: 'Linker Cleave',
          fr: 'Cleave gauche',
          ja: '左半面へ攻撃',
          cn: '向左射',
          ko: '왼쪽 부채꼴',
          tc: '左刀',
        },
        rightCleave: {
          en: 'Right Cleave',
          de: 'Rechter Cleave',
          fr: 'Cleave droit',
          ja: '右半面へ攻撃',
          cn: '向右射',
          ko: '오른쪽 부채꼴',
          tc: '右刀',
        },
        baitThenSpread: {
          en: 'Bait 4x Puddles => Spread',
          de: 'Ködere Fläche x4 => Verteilen',
          cn: '诱导4轮黄圈 => 分散',
          ko: '장판 유도 4x => 산개',
        },
        baitThenSpreadCleave: {
          en: 'Bait 4x Puddles => Spread + ${cleave}',
          de: 'Ködere Fläche x4 => Verteilen + ${cleave}',
          cn: '诱导4轮黄圈 => 分散 + ${cleave}',
          ko: '장판 유도 4x => 산개 + ${cleave}',
        },
        spreadCurtain: {
          en: 'Spread Debuff on YOU',
          de: 'Verteilen Debuff auf DIR',
          cn: '分散 Debuff 点名',
          ko: '산개징 대상자',
        },
      },
    },
    {
      id: 'R12S Ravenous Reach 1 Safe Side Collect',
      // These two syncs indicate the animation of where the head will go to cleave
      // B49A => West Safe
      // B49B => East Safe
      type: 'Ability',
      netRegex: { id: ['B49A', 'B49B'], source: 'Lindwurm', capture: true },
      condition: (data) => data.phase === 'doorboss',
      run: (data, matches) => {
        data.ravenousReach1SafeSide = matches.id === 'B49A' ? 'west' : 'east';
      },
    },
    {
      id: 'R12S Ravenous Reach 1 Safe Side',
      // These two syncs indicate the animation of where the head will go to cleave
      // B49A => West Safe
      // B49B => East Safe
      type: 'Ability',
      netRegex: { id: ['B49A', 'B49B'], source: 'Lindwurm', capture: true },
      condition: (data) => data.phase === 'doorboss',
      infoText: (_data, matches, output) => {
        if (matches.id === 'B49A')
          return output.goWest!();
        return output.goEast!();
      },
      outputStrings: {
        goEast: Outputs.east,
        goWest: Outputs.west,
      },
    },
    {
      id: 'R12S Act 1 Blob Safe Spots (early)',
      // Activating on B49D Ravenous Reach conclusion
      type: 'Ability',
      netRegex: { id: 'B49D', source: 'Lindwurm', capture: false },
      delaySeconds: 0.3, // Delay until debuffs ended
      suppressSeconds: 9999, // In case players are hit by the ability
      infoText: (data, _matches, output) => {
        const reach = data.ravenousReach1SafeSide;
        const dir1 = data.act1SafeCorner;
        const dir2 = dir1 === 'northwest' ? 'east' : 'west'; // NOTE: Not checking undefined here

        // Safe spot of side party is assumed to be on
        if (data.role !== 'tank') {
          const dir = dir1 === undefined
            ? dir1
            : reach === dir2
            ? dir2
            : reach === dir1.slice(5)
            ? dir1
            : undefined;

          if (dir1) {
            if (dir) {
              return output.safeSpot!({
                safe: output[dir]!(),
              });
            }
            return output.safeSpot!({
              safe: output.safeDirs!({
                dir1: output[dir1]!(),
                dir2: output[dir2]!(),
              }),
            });
          }
        }

        // Safe spot of opposite assumed side party will be
        const dir = dir1 === undefined
          ? dir1
          : reach === dir2
          ? dir1
          : reach === dir1.slice(5)
          ? dir2
          : undefined;
        if (dir1) {
          if (dir) {
            return output.safeSpot!({
              safe: output[dir]!(),
            });
          }
          return output.safeSpot!({
            safe: output.safeDirs!({
              dir1: output[dir1]!(),
              dir2: output[dir2]!(),
            }),
          });
        }
      },
      outputStrings: {
        northeast: Outputs.northeast,
        east: Outputs.east,
        west: Outputs.west,
        northwest: Outputs.northwest,
        safeSpot: {
          en: '${safe} (later)',
          de: '${safe} (später)',
          cn: '${safe} (稍后)',
          ko: '${safe} (나중에)',
        },
        safeDirs: {
          en: '${dir1}/${dir2}',
          de: '${dir1}/${dir2}',
          cn: '${dir1}/${dir2}',
          ko: '${dir1}/${dir2}',
        },
      },
    },
    {
      id: 'R12S Fourth-wall Fusion Stack',
      type: 'HeadMarker',
      netRegex: { id: headMarkerData['stack'], capture: true },
      condition: (data) => {
        if (data.role === 'tank')
          return false;
        return true;
      },
      durationSeconds: 5.1,
      alertText: (data, matches, output) => {
        const reach = data.ravenousReach1SafeSide;
        const dir1 = data.act1SafeCorner;
        const dir2 = dir1 === 'northwest' ? 'east' : 'west'; // NOTE: Not checking undefined here
        const target = matches.target;

        // Safe spot of side party is assumed to be on
        const dir = dir1 === undefined
          ? dir1
          : reach === dir2
          ? dir2
          : reach === dir1.slice(5)
          ? dir1
          : undefined;

        if (target === data.me) {
          if (dir1) {
            if (dir) {
              return output.stackSafe!({
                stack: output.stackOnYou!(),
                safe: output[dir]!(),
              });
            }
            return output.stackSafe!({
              stack: output.stackOnYou!(),
              safe: output.stackDirs!({
                dir1: output[dir1]!(),
                dir2: output[dir2]!(),
              }),
            });
          }
          return output.stackOnYou!();
        }
        const player = data.party.member(target);
        if (dir1) {
          if (dir) {
            return output.stackSafe!({
              stack: output.stackOnTarget!({ player: player }),
              safe: output[dir]!(),
            });
          }
          return output.stackSafe!({
            stack: output.stackOnTarget!({ player: player }),
            safe: output.stackDirs!({
              dir1: output[dir1]!(),
              dir2: output[dir2]!(),
            }),
          });
        }
        return output.stackOnTarget!({ player: player });
      },
      outputStrings: {
        northeast: Outputs.northeast,
        east: Outputs.east,
        west: Outputs.west,
        northwest: Outputs.northwest,
        stackOnYou: Outputs.stackOnYou,
        stackOnTarget: Outputs.stackOnPlayer,
        stackSafe: {
          en: '${stack} ${safe}',
          de: '${stack} ${safe}',
          cn: '${stack} ${safe}',
          ko: '${stack} ${safe}',
        },
        stackDirs: {
          en: '${dir1}/${dir2}',
          de: '${dir1}/${dir2}',
          cn: '${dir1}/${dir2}',
          ko: '${dir1}/${dir2}',
        },
      },
    },
    {
      id: 'R12S Tankbuster',
      type: 'HeadMarker',
      netRegex: { id: headMarkerData['tankbuster'], capture: true },
      condition: Conditions.targetIsYou(),
      durationSeconds: 5.1,
      alertText: (data, _matches, output) => {
        const reach = data.ravenousReach1SafeSide;
        const dir1 = data.act1SafeCorner;
        const dir2 = dir1 === 'northwest' ? 'east' : 'west'; // NOTE: Not checking undefined here

        // Safe spot of opposite assumed side party will be
        const dir = dir1 === undefined
          ? dir1
          : reach === dir2
          ? dir1
          : reach === dir1.slice(5)
          ? dir2
          : undefined;
        if (dir1) {
          if (dir) {
            return output.busterSafe!({
              buster: output.busterOnYou!(),
              safe: output[dir]!(),
            });
          }
          return output.busterSafe!({
            buster: output.busterOnYou!(),
            safe: output.busterDirs!({
              dir1: output[dir1]!(),
              dir2: output[dir2]!(),
            }),
          });
        }
        return output.busterOnYou!();
      },
      outputStrings: {
        northeast: Outputs.northeast,
        east: Outputs.east,
        west: Outputs.west,
        northwest: Outputs.northwest,
        busterOnYou: Outputs.tankBusterOnYou,
        busterSafe: {
          en: '${buster} + ${safe}',
          de: '${buster} + ${safe}',
          cn: '${buster} + ${safe}',
          ko: '${buster} + ${safe}',
        },
        busterDirs: {
          en: '${dir1}/${dir2}',
          de: '${dir1}/${dir2}',
          cn: '${dir1}/${dir2}',
          ko: '${dir1}/${dir2}',
        },
      },
    },
    {
      id: 'R12S In Line Debuff Collector',
      type: 'GainsEffect',
      netRegex: { effectId: ['BBC', 'BBD', 'BBE', 'D7B'] },
      run: (data, matches) => {
        const effectToNum: { [effectId: string]: number } = {
          BBC: 1,
          BBD: 2,
          BBE: 3,
          D7B: 4,
        } as const;
        const num = effectToNum[matches.effectId];
        if (num === undefined)
          return;
        data.inLine[matches.target] = num;
      },
    },
    {
      id: 'R12S Bonds of Flesh Flesh α/β Collect',
      // Bonds of Flesh has the following timings:
      // 1st -  26s
      // 2nd - 31s
      // 3rd - 36s
      // 4rth - 41s
      type: 'GainsEffect',
      netRegex: { effectId: ['1290', '1292'], capture: true },
      condition: Conditions.targetIsYou(),
      run: (data, matches) => {
        data.myFleshBonds = matches.effectId === '1290' ? 'alpha' : 'beta';
      },
    },
    {
      id: 'R12S In Line Debuff',
      type: 'GainsEffect',
      netRegex: { effectId: ['BBC', 'BBD', 'BBE', 'D7B'], capture: false },
      delaySeconds: 0.5,
      durationSeconds: 10,
      suppressSeconds: 1,
      infoText: (data, _matches, output) => {
        const myNum = data.inLine[data.me];
        if (myNum === undefined)
          return;
        const flesh = data.myFleshBonds;
        if (flesh === undefined)
          return output.order!({ num: myNum });
        if (flesh === 'alpha') {
          switch (myNum) {
            case 1:
              return output.alpha1!();
            case 2:
              return output.alpha2!();
            case 3:
              return output.alpha3!();
            case 4:
              return output.alpha4!();
          }
        }
        switch (myNum) {
          case 1:
            return output.beta1!();
          case 2:
            return output.beta2!();
          case 3:
            return output.beta3!();
          case 4:
            return output.beta4!();
        }
      },
      tts: (data, _matches, output) => {
        // Greek characters may be the best TTS
        const myNum = data.inLine[data.me];
        if (myNum === undefined)
          return;
        const flesh = data.myFleshBonds;
        if (flesh === undefined)
          return output.order!({ num: myNum });
        if (flesh === 'alpha') {
          switch (myNum) {
            case 1:
              return output.alpha1Tts!();
            case 2:
              return output.alpha2Tts!();
            case 3:
              return output.alpha3Tts!();
            case 4:
              return output.alpha4Tts!();
          }
        }
        switch (myNum) {
          case 1:
            return output.beta1Tts!();
          case 2:
            return output.beta2Tts!();
          case 3:
            return output.beta3Tts!();
          case 4:
            return output.beta4Tts!();
        }
      },
      outputStrings: {
        alpha1: {
          en: '1α: Wait for Tether 1',
          de: '1α: Warte auf Verbindung 1',
          cn: '1α: 拉 1 线',
          ko: '1α: 선 1 기다리기',
        },
        alpha2: {
          en: '2α: Wait for Tether 2',
          de: '2α: Warte auf Verbindung 2',
          cn: '2α: 拉 2 线',
          ko: '2α: 선 2 기다리기',
        },
        alpha3: {
          en: '3α: Blob Tower 1',
          de: '3α: Blob Turm 1',
          cn: '3α: 踩场地 1 塔',
          ko: '3α: 살점 탑 1',
        },
        alpha4: {
          en: '4α: Blob Tower 2',
          de: '4α: Blob Turm 2',
          cn: '4α: 踩场地 2 塔',
          ko: '4α: 살점 탑 2',
        },
        beta1: {
          en: '1β: Wait for Tether 1',
          de: '1β: Warte auf Verbindung 1',
          cn: '1β: 反拉 1 线',
          ko: '1β: 선 1 기다리기',
        },
        beta2: {
          en: '2β: Wait for Tether 2',
          de: '2β: Warte auf Verbindung 2',
          cn: '2β: 反拉 2 线',
          ko: '2β: 선 2 기다리기',
        },
        beta3: {
          en: '3β: Chain Tower 1',
          de: '3β: Ketten Turm 1',
          cn: '3β: 踩玩家 1 塔',
          ko: '3β: 설치한 탑 1',
        },
        beta4: {
          en: '4β: Chain Tower 2',
          de: '4β: Ketten Turm 2',
          cn: '4β: 踩玩家 2 塔',
          ko: '4β: 설치한 탑 2',
        },
        alpha1Tts: {
          en: '1α: Wait for Tether 1',
          de: '1α: WWarte auf Verbindung 1',
          cn: '1α: 拉 1 线',
          ko: '알파 1: 선 1 기다리기',
        },
        alpha2Tts: {
          en: '2α: Wait for Tether 2',
          de: '2α: Warte auf Verbindung 2',
          cn: '2α: 拉 2 线',
          ko: '알파 2: 선 2 기다리기',
        },
        alpha3Tts: {
          en: '3α: Blob Tower 1',
          de: '3α: Blob Turm 1',
          cn: '3α: 踩场地 1 塔',
          ko: '알파 3: 살점 탑 1',
        },
        alpha4Tts: {
          en: '4α: Blob Tower 2',
          de: '4α: Blob Turm 2',
          cn: '4α: 踩场地 2 塔',
          ko: '알파 4: 살점 탑 2',
        },
        beta1Tts: {
          en: '1β: Wait for Tether 1',
          de: '1β: Warte auf Verbindung 1',
          cn: '1β: 反拉 1 线',
          ko: '베타 1: 선 1 기다리기',
        },
        beta2Tts: {
          en: '2β: Wait for Tether 2',
          de: '2β: Warte auf Verbindung 2',
          cn: '2β: 反拉 2 线',
          ko: '베타 2: 선 2 기다리기',
        },
        beta3Tts: {
          en: '3β: Chain Tower 1',
          de: '3β: Ketten Turm 1',
          cn: '3β: 踩玩家 1 塔',
          ko: '베타 3: 설치한 탑 1',
        },
        beta4Tts: {
          en: '4β: Chain Tower 2',
          de: '4β: Ketten Turm 2',
          cn: '4β: 踩玩家 2 塔',
          ko: '베타 4: 설치한 탑 2',
        },
        order: {
          en: '${num}',
          de: '${num}',
          fr: '${num}',
          ja: '${num}',
          cn: '${num}',
          ko: '${num}',
          tc: '${num}',
        },
        unknown: Outputs.unknown,
      },
    },
    {
      id: 'R12S Phagocyte Spotlight Blob Tower Location Collect',
      // StartsUsing can have bad data
      // Pattern 1
      // Blob 1: (104, 104) SE Inner
      // Blob 2: (96, 96) NW Inner
      // Blob 3: (85, 110) SW Outer
      // Blob 4: (115, 90) NE Outer
      // Pattern 2
      // Blob 1: (104, 96) NE Inner
      // Blob 2: (96, 104) SW Inner
      // Blob 3: (85, 90) NW Outer
      // Blob 4: (115, 110) SE Outer
      // Pattern 3
      // Blob 1: (96, 96) NW Inner
      // Blob 2: (104, 104) SE Inner
      // Blob 3: (115, 90) NE Outer
      // Blob 4: (85, 110) SW Outer
      // Pattern 4
      // Blob 1: (96, 104) SW Inner
      // Blob 2: (104, 96) NE Inner
      // Blob 3: (115, 110) SE Outer
      // Blob 4: (86, 90) NW Outer
      type: 'StartsUsingExtra',
      netRegex: { id: 'B4B6', capture: true },
      suppressSeconds: 10,
      run: (data, matches) => {
        const x = parseFloat(matches.x);
        const y = parseFloat(matches.y);
        const dir = Directions.xyToIntercardDirOutput(x, y, center.x, center.y);
        data.blobTowerDirs.push(dir);

        if (dir === 'dirSE') {
          data.blobTowerDirs.push('dirNW');
          data.blobTowerDirs.push('dirSW');
          data.blobTowerDirs.push('dirNE');
        } else if (dir === 'dirNE') {
          data.blobTowerDirs.push('dirSW');
          data.blobTowerDirs.push('dirNW');
          data.blobTowerDirs.push('dirSE');
        } else if (dir === 'dirNW') {
          data.blobTowerDirs.push('dirSE');
          data.blobTowerDirs.push('dirNE');
          data.blobTowerDirs.push('dirSW');
        } else if (dir === 'dirSW') {
          data.blobTowerDirs.push('dirNE');
          data.blobTowerDirs.push('dirSE');
          data.blobTowerDirs.push('dirNW');
        }
      },
    },
    {
      id: 'R12S Phagocyte Spotlight Blob Tower Location (Early)',
      // 23.8s until B4B7 Rolling Mass Blob Tower Hit
      // Only need to know first blob location
      type: 'StartsUsingExtra',
      netRegex: { id: 'B4B6', capture: false },
      condition: (data) => data.myFleshBonds === 'alpha',
      delaySeconds: 0.1,
      durationSeconds: (data) => {
        const myNum = data.inLine[data.me];
        // Timings based on next trigger
        switch (myNum) {
          case 1:
            return 20;
          case 2:
            return 25;
          case 3:
            return 21;
          case 4:
            return 21;
        }
      },
      suppressSeconds: 10,
      infoText: (data, _matches, output) => {
        const myNum = data.inLine[data.me];
        if (myNum === undefined)
          return;

        type index = {
          [key: number]: number;
        };
        const myNumToDirIndex: index = {
          1: 2,
          2: 3,
          3: 0,
          4: 1,
        };
        const dirIndex = myNumToDirIndex[myNum];
        if (dirIndex === undefined)
          return;
        const towerNum = dirIndex + 1;

        const dir = data.blobTowerDirs[dirIndex];
        if (dir === undefined)
          return;

        if (myNum > 2)
          return output.innerBlobTower!({
            num: towerNum,
            dir: output[dir]!(),
          });
        return output.outerBlobTower!({ num: towerNum, dir: output[dir]!() });
      },
      outputStrings: {
        ...Directions.outputStringsIntercardDir,
        innerBlobTower: {
          en: 'Blob Tower ${num} Inner ${dir} (later)',
          de: 'Blob Turm ${num} Innen ${dir} (später)',
          cn: '踩场内${dir}场地${num}塔 (稍后)',
          ko: '살점 탑 ${num} 안쪽 ${dir} (나중에)',
        },
        outerBlobTower: {
          en: 'Blob Tower ${num} Outer ${dir} (later)',
          de: 'Blob Turm ${num} Außen ${dir} (später)',
          cn: '踩场外${dir}场地${num}塔 (稍后)',
          ko: '살점 탑 ${num} 바깥쪽 ${dir} (나중에)',
        },
      },
    },
    {
      id: 'R12S Cursed Coil Bind Draw-in',
      // Using Phagocyte Spotlight, 1st one happens 7s before bind
      // Delayed additionally to reduce overlap with alpha tower location calls
      type: 'Ability',
      netRegex: { id: 'B4B6', capture: false },
      delaySeconds: 3, // 5s warning
      suppressSeconds: 10,
      response: Responses.drawIn(),
    },
    {
      id: 'R12S Cursed Coil Initial Direction Collect',
      // B4B8 Cruel Coil: Starts east, turns counterclock
      // B4B9 Cruel Coil: Starts west, turns counterclock
      // B4BA Cruel Coil: Starts north, turns counterclock
      // B4BB Cruel Coil: Starts south, turns counterclock
      type: 'StartsUsing',
      netRegex: { id: ['B4B8', 'B4B9', 'B4BA', 'B4BB'], source: 'Lindwurm', capture: true },
      run: (data, matches) => {
        switch (matches.id) {
          case 'B4B8':
            data.cursedCoilDirNum = 1;
            return;
          case 'B4B9':
            data.cursedCoilDirNum = 3;
            return;
          case 'B4BA':
            data.cursedCoilDirNum = 0;
            return;
          case 'B4BB':
            data.cursedCoilDirNum = 2;
        }
      },
    },
    {
      id: 'R12S Skinsplitter Counter',
      // These occur every 5s
      // Useful for blob tower tracking that happen 2s after
      // 2: Tether 1
      // 3: Tether 2 + Blob Tower 1
      // 4: Tether 3 + Blob Tower 2
      // 5: Tether 4 + Blob Tower 3
      // 6: Blob Tower 4
      // 7: Last time to exit
      type: 'Ability',
      netRegex: { id: 'B4BC', capture: false },
      suppressSeconds: 1,
      run: (data) => data.skinsplitterCount = data.skinsplitterCount + 1,
    },
    {
      id: 'R12S Cell Chain Counter',
      type: 'Tether',
      netRegex: { id: headMarkerData['cellChainTether'], capture: false },
      condition: (data) => data.phase === 'doorboss',
      run: (data) => data.cellChainCount = data.cellChainCount + 1,
    },
    {
      id: 'R12S Cell Chain Tether Number',
      // Helpful for players to keep track of which chain tower is next
      // Does not output when it is their turn to break the tether
      type: 'Tether',
      netRegex: { id: headMarkerData['cellChainTether'], capture: false },
      condition: (data) => {
        if (data.phase === 'doorboss' && data.myFleshBonds === 'beta')
          return true;
        return false;
      },
      infoText: (data, _matches, output) => {
        const myNum = data.inLine[data.me];
        const num = data.cellChainCount;
        if (myNum !== num) {
          if (myNum === 1 && num === 3)
            return output.beta1Tower!({
              tether: output.tether!({ num: num }),
            });
          if (myNum === 2 && num === 4)
            return output.beta2Tower!({
              tether: output.tether!({ num: num }),
            });
          if (myNum === 3 && num === 1)
            return output.beta3Tower!({
              tether: output.tether!({ num: num }),
            });
          if (myNum === 4 && num === 2)
            return output.beta4Tower!({
              tether: output.tether!({ num: num }),
            });

          return output.tether!({ num: num });
        }

        if (myNum === undefined)
          return output.tether!({ num: num });
      },
      outputStrings: {
        tether: {
          en: 'Tether ${num}',
          de: 'Verbindung ${num}',
          fr: 'Lien ${num}',
          ja: '線 ${num}',
          cn: '拉${num}线',
          ko: '선 ${num}',
          tc: '線 ${num}',
        },
        beta1Tower: {
          en: '${tether} => Chain Tower 3',
          de: '${tether} => Ketten Turm 3',
          cn: '${tether} => 玩家3塔',
          ko: '${tether} => 설치한 탑 3',
        },
        beta2Tower: {
          en: '${tether} => Chain Tower 4',
          de: '${tether} => Ketten Turm 4',
          cn: '${tether} => 玩家4塔',
          ko: '${tether} => 설치한 탑 4',
        },
        beta3Tower: {
          en: '${tether} => Chain Tower 1',
          de: '${tether} => Ketten Turm 1',
          cn: '${tether} => 玩家1塔',
          ko: '${tether} => 설치한 탑 1',
        },
        beta4Tower: {
          en: '${tether} => Chain Tower 2',
          de: '${tether} => Ketten Turm 2',
          cn: '${tether} => 玩家2塔',
          ko: '${tether} => 설치한 탑 2',
        },
      },
    },
    {
      id: 'R12S Chain Tower Number',
      // Using B4B4 Dramatic Lysis to detect chain broken
      type: 'Ability',
      netRegex: { id: 'B4B4', capture: false },
      condition: (data) => {
        if (data.phase === 'doorboss' && data.myFleshBonds === 'beta')
          return true;
        return false;
      },
      suppressSeconds: 1,
      alertText: (data, _matches, output) => {
        const mechanicNum = data.cellChainCount;
        const myNum = data.inLine[data.me];
        if (myNum === undefined)
          return;

        type index = {
          [key: number]: number;
        };
        const myNumToOrder: index = {
          1: 3,
          2: 4,
          3: 1,
          4: 2,
        };

        const myOrder = myNumToOrder[myNum];
        if (myOrder === undefined)
          return;

        if (myOrder === mechanicNum)
          return output.tower!({ num: mechanicNum });
      },
      outputStrings: {
        tower: {
          en: 'Get Chain Tower ${num}',
          de: 'Nimm Ketten Turm ${num}',
          cn: '踩玩家${num}塔',
          ko: '설치한 탑 ${num} 밟기',
        },
      },
    },
    {
      id: 'R12S Bonds of Flesh Flesh α First Two Towers',
      // These are not dependent on player timings and so can be hard coded by duration
      type: 'GainsEffect',
      netRegex: { effectId: '1290', capture: true },
      condition: (data, matches) => {
        if (matches.target === data.me) {
          const duration = parseFloat(matches.duration);
          if (duration < 35)
            return false;
          return true;
        }
        return false;
      },
      delaySeconds: (_data, matches) => {
        const duration = parseFloat(matches.duration);
        // The following gives 5s warning to take tower
        if (duration > 37)
          return 31; // Alpha4 Time
        return 26; // Alpha3 Time
      },
      alertText: (data, matches, output) => {
        const duration = parseFloat(matches.duration);
        const dir = data.blobTowerDirs[duration > 40 ? 1 : 0];
        if (duration > 40) {
          if (dir !== undefined)
            return output.alpha4Dir!({ dir: output[dir]!() });
          return output.alpha4!();
        }
        if (dir !== undefined)
          return output.alpha3Dir!({ dir: output[dir]!() });
        return output.alpha3!();
      },
      outputStrings: {
        ...Directions.outputStringsIntercardDir,
        alpha3: {
          en: 'Get Blob Tower 1',
          de: 'Nimm Blob Turm 1',
          cn: '踩场地1塔',
          ko: '살점 탑 1 밟기',
        },
        alpha4: {
          en: 'Get Blob Tower 2',
          de: 'Nimm Blob Turm 2',
          cn: '踩场地2塔',
          ko: '살점 탑 2 밟기',
        },
        alpha3Dir: {
          en: 'Get Blob Tower 1 (Inner ${dir})',
          de: 'Nimm Blob Turm 1 (Innen ${dir})',
          cn: '踩场地1塔 (内${dir})',
          ko: '살점 탑 1 (안쪽 ${dir}) 밟기',
        },
        alpha4Dir: {
          en: 'Get Blob Tower 2 (Inner ${dir})',
          de: 'Nimm Blob Turm 2 (Innen ${dir})',
          cn: '踩场地2塔 (内${dir})',
          ko: '살점 탑 2 (안쪽 ${dir}) 밟기',
        },
      },
    },
    {
      id: 'R12S Unbreakable Flesh α/β Chains and Last Two Towers',
      type: 'GainsEffect',
      netRegex: { effectId: ['1291', '1293'], capture: true },
      condition: (data, matches) => {
        if (matches.target === data.me && data.phase === 'doorboss')
          return true;
        return false;
      },
      alertText: (data, matches, output) => {
        const myNum = data.inLine[data.me];
        const flesh = matches.effectId === '1291' ? 'alpha' : 'beta';
        // As the coil is moving in reverse, the modulo will have negative values
        // 8 has to be used as that is the next number after 7 (number of spins) that divides evenly by 4
        const coilDirNum = data.cursedCoilDirNum !== undefined
          ? ((data.cursedCoilDirNum - data.skinsplitterCount) + 8) % 4
          : undefined;

        if (flesh === 'alpha') {
          const exit = Directions.outputCardinalDir[coilDirNum ?? 4] ?? 'unknown'; // Return 'unknown' if undefined
          if (myNum === 1) {
            const dir = data.blobTowerDirs[2];
            if (dir !== undefined)
              return output.alpha1Dir!({
                chains: output.breakChains!(),
                exit: output[exit]!(),
                dir: output[dir]!(),
              });
          }
          if (myNum === 2) {
            const dir = data.blobTowerDirs[3];
            if (dir !== undefined)
              return output.alpha2Dir!({
                chains: output.breakChains!(),
                exit: output[exit]!(),
                dir: output[dir]!(),
              });
          }

          // dir undefined or 3rd/4rth in line
          switch (myNum) {
            case 1:
              return output.alpha1!({
                chains: output.breakChains!(),
                exit: output[exit]!(),
              });
            case 2:
              return output.alpha2!({
                chains: output.breakChains!(),
                exit: output[exit]!(),
              });
            case 3:
              return output.alpha3!({
                chains: output.breakChains!(),
                exit: output[exit]!(),
              });
            case 4:
              return output.alpha4!({
                chains: output.breakChains!(),
                exit: output[exit]!(),
              });
          }
        }

        const dir = coilDirNum !== undefined
          ? Directions.outputCardinalDir[(coilDirNum + 2) % 4] ?? 'unknown'
          : 'unknown';

        switch (myNum) {
          case 1:
            return output.beta1!({
              chains: output.breakChains!(),
              dir: output[dir]!(),
            });
          case 2:
            return output.beta2!({
              chains: output.breakChains!(),
              dir: output[dir]!(),
            });
          case 3:
            return output.beta3!({
              chains: output.breakChains!(),
              dir: output[dir]!(),
            });
          case 4:
            return output.beta4!({
              chains: output.breakChains!(),
              dir: output[dir]!(),
            });
        }
        return output.getTowers!();
      },
      outputStrings: {
        ...Directions.outputStrings8Dir,
        breakChains: Outputs.breakChains,
        getTowers: Outputs.getTowers,
        alpha1: {
          en: '${chains} 1 (${exit}) + Blob Tower 3 (Outer)',
          de: '${chains} 1 (${exit}) + Blob Turm 3 (Außen)',
          cn: '${chains} 1 (${exit}) + 场地3塔 (场外)',
          ko: '${chains} 1 (${exit}) + 살점 탑 3 (바깥쪽)',
        },
        alpha1Dir: {
          en: '${chains} 1 (${exit}) + Blob Tower 3 (Outer ${dir})',
          de: '${chains} 1 (${exit}) + Blob Turm 3 (Außen ${dir})',
          cn: '${chains} 1 (${exit}) + 场地3塔 (场外 ${dir})',
          ko: '${chains} 1 (${exit}) + 살점 탑 3 (바깥쪽 ${dir})',
        },
        alpha1ExitDir: {
          en: '${chains} 1 (${exit}) + Blob Tower 3 (Outer ${dir})',
          de: '${chains} 1 (${exit}) + Blob Turm 3 (Außen ${dir})',
          cn: '${chains} 1 (${exit}) + 场地3塔 (场外 ${dir})',
          ko: '${chains} 1 (${exit}) + 살점 탑 3 (바깥쪽 ${dir})',
        },
        alpha2: {
          en: '${chains} 2 (${exit}) + Blob Tower 4 (Outer)',
          de: '${chains} 2 (${exit}) + Blob Turm 4 (Außen)',
          cn: '${chains} 2 (${exit}) + 场地4塔 (场外)',
          ko: '${chains} 2 (${exit}) + 살점 탑 4 (바깥쪽)',
        },
        alpha2Dir: {
          en: '${chains} 2 (${exit}) + Blob Tower 4 (Outer ${dir})',
          de: '${chains} 2 (${exit}) + Blob Turm 4 (Außen ${dir})',
          cn: '${chains} 2 (${exit}) + 场地4塔 (场外 ${dir})',
          ko: '${chains} 2 (${exit}) + 살점 탑 4 (바깥쪽 ${dir})',
        },
        alpha3: {
          en: '${chains} 3 (${exit}) + Get Out',
          de: '${chains} 3 (${exit}) + Geh Raus',
          cn: '${chains} 3 (${exit}) + 出去',
          ko: '${chains} 3 (${exit}) + 밖으로',
        },
        alpha4: {
          en: '${chains} 4 (${exit}) + Get Out',
          de: '${chains} 4 (${exit}) + Geh Raus',
          cn: '${chains} 4 (${exit}) + 出去',
          ko: '${chains} 4 (${exit}) + 밖으로',
        },
        beta1: {
          en: '${chains} 1 (${dir}) => Get Middle',
          de: '${chains} 1 (${dir}) => Geh Mitte',
          cn: '${chains} 1 (${dir}) => 中间',
          ko: '${chains} 1 (${dir}) => 중앙으로',
        },
        beta2: {
          en: '${chains} 2 (${dir}) => Get Middle',
          de: '${chains} 2 (${dir}) => Geh Mitte',
          cn: '${chains} 2 (${dir}) => 中间',
          ko: '${chains} 2 (${dir}) => 중앙으로',
        },
        beta3: {
          en: '${chains} 3 (${dir}) => Wait for last pair',
          de: '${chains} 3 (${dir}) => Warte fürs letzte Paar',
          cn: '${chains} 3 (${dir}) => 等待最后一组',
          ko: '${chains} 3 (${dir}) => 마지막 쌍 기다리기',
        },
        beta4: {
          en: '${chains} 4 (${dir}) => Get Out',
          de: '${chains} 4 (${dir}) => Geh Raus',
          cn: '${chains} 4 (${dir}) => 出去',
          ko: '${chains} 4 (${dir}) => 밖으로',
        },
      },
    },
    {
      id: 'R12S Chain Tower Followup',
      // Using B4B3 Roiling Mass to detect chain tower soak
      // Beta player leaving early may get hit by alpha's chain break aoe
      type: 'Ability',
      netRegex: { id: 'B4B3', capture: true },
      condition: (data, matches) => {
        if (data.myFleshBonds === 'beta' && data.me === matches.target)
          return true;
        return false;
      },
      infoText: (data, _matches, output) => {
        // Possibly the count could be off if break late (giving damage and damage down)
        // Ideal towers are soaked:
        // Beta 1 at 5th Skinsplitter
        // Beta 2 at 6th Skinsplitter
        // Beta 3 at 3rd Skinsplitter
        // Beta 4 at 4rth Skinsplitter
        const mechanicNum = data.skinsplitterCount;
        const myNum = data.inLine[data.me];
        if (myNum === undefined) {
          // This can be corrected by the player later
          if (mechanicNum < 5)
            return output.goIntoMiddle!();
          return output.getOut!();
        }

        if (mechanicNum < 5) {
          if (myNum === 1)
            return output.beta1Middle!();
          if (myNum === 2)
            return output.beta2Middle!();
          if (myNum === 3)
            return output.beta3Middle!();
          if (myNum === 4)
            return output.beta4Middle!();
        }
        if (myNum === 1)
          return output.beta1Out!();
        if (myNum === 2)
          return output.beta2Out!();
        if (myNum === 3)
          return output.beta3Out!();
        if (myNum === 4)
          return output.beta4Out!();
      },
      outputStrings: {
        getOut: {
          en: 'Get Out',
          de: 'Raus da',
          fr: 'Sortez',
          ja: '外へ',
          cn: '出去',
          ko: '밖으로',
          tc: '遠離',
        },
        goIntoMiddle: Outputs.goIntoMiddle,
        beta1Middle: Outputs.goIntoMiddle,
        beta2Middle: Outputs.goIntoMiddle, // Should not happen under ideal situation
        beta3Middle: Outputs.goIntoMiddle,
        beta4Middle: Outputs.goIntoMiddle,
        beta1Out: { // Should not happen under ideal situation
          en: 'Get Out',
          de: 'Raus da',
          fr: 'Sortez',
          ja: '外へ',
          cn: '出去',
          ko: '밖으로',
          tc: '遠離',
        },
        beta2Out: {
          en: 'Get Out',
          de: 'Raus da',
          fr: 'Sortez',
          ja: '外へ',
          cn: '出去',
          ko: '밖으로',
          tc: '遠離',
        },
        beta3Out: { // Should not happen under ideal situation
          en: 'Get Out',
          de: 'Raus da',
          fr: 'Sortez',
          ja: '外へ',
          cn: '出去',
          ko: '밖으로',
          tc: '遠離',
        },
        beta4Out: { // Should not happen under ideal situation
          en: 'Get Out',
          de: 'Raus da',
          fr: 'Sortez',
          ja: '外へ',
          cn: '出去',
          ko: '밖으로',
          tc: '遠離',
        },
      },
    },
    {
      id: 'R12S Blob Tower Followup',
      // Using B4B7 Roiling Mass to detect chain tower soak
      // Alpha 3 and Alpha 4 get the inner towers before their chains
      type: 'Ability',
      netRegex: { id: 'B4B7', capture: true },
      condition: (data, matches) => {
        if (data.myFleshBonds === 'alpha' && data.me === matches.target)
          return true;
        return false;
      },
      infoText: (data, _matches, output) => {
        const mechanicNum = data.skinsplitterCount;
        const myNum = data.inLine[data.me];
        if (myNum === undefined)
          return;

        if (myNum === mechanicNum)
          return output.goIntoMiddle!();
      },
      outputStrings: {
        goIntoMiddle: Outputs.goIntoMiddle,
      },
    },
    {
      id: 'R12S Skinsplitter Out of Coil Reminder',
      type: 'Ability',
      netRegex: { id: 'B4BC', capture: false },
      condition: (data) => data.skinsplitterCount === 7,
      suppressSeconds: 1,
      alertText: (_data, _matches, output) => output.outOfCoil!(),
      outputStrings: {
        outOfCoil: {
          en: 'Out of Coil',
          de: 'Raus aus dem Kreis',
          cn: '出圈',
          ko: '몸통 밖으로',
        },
      },
    },
    {
      id: 'R12S Splattershed',
      type: 'StartsUsing',
      netRegex: { id: ['B9C3', 'B9C4'], source: 'Lindwurm', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'R12S Mitotic Phase Direction Collect',
      // Unknown_DE6 spell contains data in its count
      type: 'GainsEffect',
      netRegex: { effectId: 'DE6', capture: true },
      condition: Conditions.targetIsYou(),
      durationSeconds: 10,
      infoText: (data, matches, output) => {
        data.myMitoticPhase = matches.count;
        switch (matches.count) {
          case '436':
            return output.frontTower!();
          case '437':
            return output.rightTower!();
          case '438':
            return output.rearTower!();
          case '439':
            return output.leftTower!();
        }
      },
      outputStrings: {
        frontTower: {
          en: 'Tower (S/SW)',
          de: 'Turm (S/SW)',
          cn: '塔 (下/左下)',
          ko: '탑 (남/남서)',
        },
        rearTower: {
          en: 'Tower (N/NE)',
          de: 'Turm (N/NO)',
          cn: '塔 (上/右上)',
          ko: '탑 (북/북동)',
        },
        leftTower: {
          en: 'Tower (E/SE)',
          de: 'Turm (O/SO)',
          cn: '塔 (右/右下)',
          ko: '탑 (동/남동)',
        },
        rightTower: {
          en: 'Tower (W/NW)',
          de: 'Turm (W/NW)',
          cn: '塔 (左/左上)',
          ko: '탑 (서/북서)',
        },
      },
    },
    {
      id: 'R12S Grand Entrance Intercards/Cardinals',
      // B4A1 is only cast when cardinals are safe
      // B4A2 is only cast when intercardinals are safe
      // These casts more than once, so just capture first event
      type: 'StartsUsing',
      netRegex: { id: ['B4A1', 'B4A2'], capture: true },
      suppressSeconds: 5,
      infoText: (data, matches, output) => {
        const count = data.myMitoticPhase;
        if (count === undefined)
          return;
        if (matches.id === 'B4A1') {
          switch (count) {
            case '436':
              return output.frontCardinals!();
            case '437':
              return output.rightCardinals!();
            case '438':
              return output.rearCardinals!();
            case '439':
              return output.leftCardinals!();
          }
        }
        switch (count) {
          case '436':
            return output.frontIntercards!();
          case '437':
            return output.rightIntercards!();
          case '438':
            return output.rearIntercards!();
          case '439':
            return output.leftIntercards!();
        }
      },
      outputStrings: {
        frontIntercards: Outputs.southwest,
        rearIntercards: Outputs.northeast,
        leftIntercards: Outputs.southeast,
        rightIntercards: Outputs.northwest,
        frontCardinals: Outputs.south,
        rearCardinals: Outputs.north,
        leftCardinals: Outputs.east,
        rightCardinals: Outputs.west,
      },
    },
    {
      id: 'R12S Rotting Flesh',
      type: 'GainsEffect',
      netRegex: { effectId: '129B', capture: true },
      condition: Conditions.targetIsYou(),
      durationSeconds: 10,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Rotting Flesh on YOU',
          de: 'Todeszellen auf DIR',
          cn: '致死细胞点名',
          ko: '치사세포 대상자',
        },
      },
    },
    {
      id: 'R12S Rotting Flesh Collect',
      type: 'GainsEffect',
      netRegex: { effectId: '129B', capture: true },
      condition: Conditions.targetIsYou(),
      run: (data) => data.hasRot = true,
    },
    {
      id: 'R12S Ravenous Reach 2',
      // These two syncs indicate the animation of where the head will go to cleave
      // B49A => West Safe
      // B49B => East Safe
      type: 'Ability',
      netRegex: { id: ['B49A', 'B49B'], source: 'Lindwurm', capture: true },
      condition: (data) => data.phase === 'curtainCall',
      alertText: (data, matches, output) => {
        if (matches.id === 'B49A') {
          return data.hasRot ? output.getHitEast!() : output.safeWest!();
        }
        return data.hasRot ? output.getHitWest!() : output.safeEast!();
      },
      outputStrings: {
        getHitWest: {
          en: 'Spread in West Cleave',
          de: 'Verteilen im Westen Cleave',
          cn: '左侧扇形内分散',
          ko: '서쪽 부채꼴에서 산개',
        },
        getHitEast: {
          en: 'Spread in East Cleave',
          de: 'Verteilen im Osten Cleave',
          cn: '右侧扇形内分散',
          ko: '동쪽 부채꼴에서 산개',
        },
        safeEast: {
          en: 'Spread East + Avoid Cleave',
          de: 'Verteilen im Osten + vermeide Cleave',
          cn: '右侧分散 + 避开扇形',
          ko: '동쪽에서 산개 + 부채꼴 피하기',
        },
        safeWest: {
          en: 'Spread West + Avoid Cleave',
          de: 'Verteilen im Westen + vermeide Cleave',
          cn: '左侧分散 + 避开扇形',
          ko: '서쪽에서 산개 + 부채꼴 피하기',
        },
      },
    },
    {
      id: 'R12S Split Scourge and Venomous Scourge',
      // B4AB Split Scourge and B4A8 Venomous Scourge are instant casts
      // Split Scourge happens first:
      // Each head will target the nearest player with a tankbuster line AoE
      //
      // This actor control happens along with boss becoming targetable
      // Seems there are two different data0 values possible:
      // 1E01: Coming back from Cardinal platforms
      // 1E001: Coming back from Intercardinal platforms
      type: 'ActorControl',
      netRegex: { command: '8000000D', data0: ['1E01', '1E001'], capture: false },
      durationSeconds: 9,
      suppressSeconds: 9999,
      infoText: (data, _matches, output) => {
        if (data.role === 'tank')
          return output.tank!();
        return output.party!();
      },
      outputStrings: {
        tank: {
          en: 'Bait Line AoE from Heads => Get Middle (Avoid Far AoEs)',
          de: 'Köder Linien-AoE von den Köpfen => Geh Mitte (vermeide entfernte AoEs)',
          cn: '诱导龙头直线AoE => 去中间 (避开远AoE)',
          ko: '머리의 직선 장판 유도 => 중앙으로 (원거리 장판 피하기)',
        },
        party: {
          en: 'Away from Heads (Avoid Tank Lines) => Spread near Heads',
          de: 'Weg von den Köpfen (Vermeide Tank-Linien) => Verteilen nahe der Köpfe',
          cn: '远离头 (避开坦克直线) => 龙头附近分散',
          ko: '머리에서 멀어지기 (탱커 직선장판 피하기) => 머리 근처에서 산개',
        },
      },
    },
    {
      id: 'R12S Venomous Scourge',
      // 2.4s after Split Scourge, Venomous Scourge AoEs happen on 3 furthest players from each head
      type: 'Ability',
      netRegex: { id: 'B4AB', capture: false },
      durationSeconds: 2.4,
      suppressSeconds: 9999,
      alertText: (data, _matches, output) => {
        if (data.role === 'tank')
          return output.tank!();
        return output.party!();
      },
      outputStrings: {
        tank: {
          en: 'Get Middle (Avoid Far AoEs)',
          de: 'Geh Mittig (Vermeide entfernte AoEs)',
          cn: '去中间 (避开远AoE)',
          ko: '중앙으로 (원거리 장판 피하기)',
        },
        party: {
          en: 'Spread near Heads',
          de: 'Nahe der Köpfe verteilen',
          cn: '龙头附近分散',
          ko: '머리 근처에서 산개',
        },
      },
    },
    {
      id: 'R12S Grotesquerie: Curtain Call Spreads',
      type: 'StartsUsing',
      netRegex: { id: 'BEC0', source: 'Lindwurm', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Bait 5x Puddles',
          de: 'Köder Flächen x5 ',
          cn: '诱导5轮黄圈',
          ko: '장판 유도 5x',
        },
      },
    },
    {
      id: 'R12S Curtain Call: Unbreakable Flesh α Chains',
      // All players, including dead, receive α debuffs
      type: 'GainsEffect',
      netRegex: { effectId: '1291', capture: true },
      condition: (data, matches) => {
        if (matches.target === data.me && data.phase === 'curtainCall')
          return true;
        return false;
      },
      infoText: (data, _matches, output) => {
        const dir1 = data.curtainCallSafeCorner;
        const dir2 = dir1 === 'northwest' ? 'southeast' : 'southwest'; // NOTE: Not checking for undefined

        if (dir1) {
          return output.alphaChains!({
            chains: output.breakChains!(),
            safe: output.safeSpots!({
              dir1: output[dir1]!(),
              dir2: output[dir2]!(),
            }),
          });
        }
        return output.alphaChains!({
          chains: output.breakChains!(),
          safe: output.avoidBlobs!(),
        });
      },
      outputStrings: {
        northeast: Outputs.northeast,
        southeast: Outputs.southeast,
        southwest: Outputs.southwest,
        northwest: Outputs.northwest,
        breakChains: Outputs.breakChains,
        safeSpots: {
          en: '${dir1}/${dir2}',
          de: '${dir1}/${dir2}',
          cn: '${dir1}/${dir2}',
          ko: '${dir1}/${dir2}',
        },
        avoidBlobs: {
          en: 'Avoid Blobs',
          de: 'Vermeide Blobs',
          cn: '避开危险区域',
          ko: '살점 피하기',
        },
        alphaChains: {
          en: '${chains} => ${safe}',
          de: '${chains} => ${safe}',
          cn: '${chains} => ${safe}',
          ko: '${chains} => ${safe}',
        },
      },
    },
    {
      id: 'R12S Curtain Call Safe Spot',
      type: 'LosesEffect',
      netRegex: { effectId: '1291', capture: true },
      condition: (data, matches) => {
        if (matches.target === data.me && data.phase === 'curtainCall')
          return true;
        return false;
      },
      promise: async (data) => {
        if (data.triggerSetConfig.curtainCallStrat !== 'ns')
          return;
        const dir1 = data.curtainCallSafeCorner;
        const dir2 = dir1 === 'northwest' ? 'southeast' : 'southwest'; // NOTE: Not checking for undefined
        const combatants = (await callOverlayHandler({
          call: 'getCombatants',
          names: [data.me],
        })).combatants;
        const me = combatants[0];
        if (combatants.length !== 1 || me === undefined) {
          console.error(
            `R12S Curtain Call Safe Spot: Wrong combatants count ${combatants.length}`,
          );
          return;
        }

        const x = me.PosX;
        const y = me.PosY;
        // Loose detection for "closeness"
        if (y > 100) {
          // Southern most players, check if they should run north or south
          if (x < 100)
            data.myCurtainCallSafeSpot = dir1 === 'northeast' ? dir2 : dir1;
          else if (x >= 100)
            data.myCurtainCallSafeSpot = dir1 === 'northeast' ? dir1 : dir2;
        } else if (y <= 100) {
          // Northern most players run to the northern most safe spot
          data.myCurtainCallSafeSpot = dir1;
        }
      },
      alertText: (data, _matches, output) => {
        if (data.triggerSetConfig.curtainCallStrat === 'none') {
          const dir1 = data.curtainCallSafeCorner;
          const dir2 = dir1 === 'northwest' ? 'southeast' : 'southwest'; // NOTE: Not checking for undefined
          if (dir1 === undefined)
            return output.avoidBlobs!();
          return output.safeSpots!({
            dir1: output[dir1]!(),
            dir2: output[dir2]!(),
          });
        }

        const myCurtainCallSafeSpot = data.myCurtainCallSafeSpot;
        if (myCurtainCallSafeSpot === undefined)
          return output.avoidBlobs!();
        return output[myCurtainCallSafeSpot]!();
      },
      outputStrings: {
        northeast: Outputs.northeast,
        southeast: Outputs.southeast,
        southwest: Outputs.southwest,
        northwest: Outputs.northwest,
        avoidBlobs: {
          en: 'Avoid Blobs',
          de: 'Vermeide Blobs',
          cn: '避开危险区域',
          ko: '살점 피하기',
        },
        safeSpots: {
          en: '${dir1}/${dir2}',
          de: '${dir1}/${dir2}',
          cn: '${dir1}/${dir2}',
          ko: '${dir1}/${dir2}',
        },
      },
    },
    {
      id: 'R12S Slaughtershed',
      type: 'StartsUsing',
      netRegex: { id: ['B4C6', 'B4C3'], source: 'Lindwurm', capture: false },
      response: Responses.bigAoe('alert'),
    },
    {
      id: 'R12S Slaughershed Stack/Spread Spots (Early)',
      // Data available by StartsUsing, trigger on ability 3s after to avoid conflict
      type: 'Ability',
      netRegex: { id: ['B4C6', 'B4C3'], source: 'Lindwurm', capture: false },
      suppressSeconds: 1,
      infoText: (data, _matches, output) => {
        const slaughtershed = data.splattershedStackDir;
        if (slaughtershed === undefined)
          return;
        return output[slaughtershed]!();
      },
      outputStrings: {
        northeast: {
          en: 'Stack NE/Spread NW (later)',
          de: 'Sammel NO/Verteilen NW (Später)',
          cn: '右上分摊/左上分散 (稍后)',
          ko: '쉐어 북동쪽/산개 북서쪽 (나중에)',
        },
        northwest: {
          en: 'Spread NE/Stack NW (later)',
          de: 'Verteilen NO/Sammel NW (Später)',
          cn: '右上分散/左上分摊 (稍后)',
          ko: '산개 북동쪽/쉐어 북서쪽 (나중에)',
        },
      },
    },
    {
      id: 'R12S Serpintine Scourge/Raptor Knuckles Collect',
      // B4CB Serpintine Scourge Left Hand first, then Right Hand
      // B4CD Serpintine Scourge Right Hand first, then Left Hand
      // B4CC Raptor Knuckles Right Hand first, then Left Hand
      // B4CE Raptor Knuckles Left Hand first, then Right Hand
      type: 'Ability',
      netRegex: {
        id: ['B4CB', 'B4CD', 'B4CC', 'B4CE'],
        source: 'Lindwurm',
        capture: true,
      },
      condition: (data) => data.phase === 'slaughtershed',
      run: (data, matches) => {
        switch (matches.id) {
          case 'B4CB':
            data.slaughtershed = 'right';
            break;
          case 'B4CD':
            data.slaughtershed = 'left';
            break;
          case 'B4CC':
            data.slaughtershed = 'northwestKnockback';
            break;
          case 'B4CE':
            data.slaughtershed = 'northeastKnockback';
        }
      },
    },
    {
      id: 'R12S Slaughtershed Stack',
      type: 'HeadMarker',
      netRegex: { id: headMarkerData['slaughterStack'], capture: true },
      condition: (data, matches) => {
        const isDPS = data.party.isDPS(matches.target);
        if (isDPS && data.role === 'dps')
          return true;
        if (!isDPS && data.role !== 'dps')
          return true;
        return false;
      },
      delaySeconds: 0.1, // Delay for followup direction
      durationSeconds: 6,
      alertText: (data, matches, output) => {
        const dir = data.splattershedStackDir;
        const target = matches.target;
        const slaughter = data.slaughtershed;

        if (target === data.me) {
          if (dir) {
            if (slaughter === undefined)
              return output.stackDir!({
                stack: output.stackOnYou!(),
                dir: output[dir]!(),
              });

            return output.stackThenDodge!({
              stack: output.stackDir!({
                stack: output.stackOnYou!(),
                dir: output[dir]!(),
              }),
              dodge: output[slaughter]!(),
            });
          }
          if (slaughter === undefined)
            return output.stackOnYou!();

          return output.stackThenDodge!({
            stack: output.stackOnYou!(),
            dodge: output[slaughter]!(),
          });
        }

        const player = data.party.member(target);
        if (dir) {
          if (slaughter === undefined)
            return output.stackDir!({
              stack: output.stackOnPlayer!({ player: player }),
              dir: output[dir]!(),
            });

          return output.stackThenDodge!({
            stack: output.stackDir!({
              stack: output.stackOnPlayer!({ player: player }),
              dir: output[dir]!(),
            }),
            dodge: output[slaughter]!(),
          });
        }
        if (slaughter === undefined)
          return output.stackOnPlayer!({ player: player });
        return output.stackThenDodge!({
          stack: output.stackOnPlayer!({ player: player }),
          dodge: output[slaughter]!(),
        });
      },
      outputStrings: {
        left: Outputs.left,
        right: Outputs.right,
        northeastKnockback: {
          en: 'Knockback from Northeast',
          de: 'Rückstoß von Nordosten',
          cn: '从右上击退',
          ko: '북동쪽에서 넉백',
        },
        northwestKnockback: {
          en: 'Knockback from Northwest',
          de: 'Rückstoß von Nordwesten',
          cn: '从左上击退',
          ko: '북서쪽에서 넉백',
        },
        northeast: Outputs.dirNE,
        northwest: Outputs.dirNW,
        stackOnYou: Outputs.stackOnYou,
        stackOnPlayer: Outputs.stackOnPlayer,
        stackDir: {
          en: '${stack} ${dir}',
          de: '${stack} ${dir}',
          cn: '${stack} ${dir}',
          ko: '${stack} ${dir}',
        },
        stackThenDodge: {
          en: '${stack} => ${dodge}',
          de: '${stack} => ${dodge}',
          cn: '${stack} => ${dodge}',
          ko: '${stack} => ${dodge}',
        },
      },
    },
    {
      id: 'R12S Slaughtershed Spread',
      type: 'HeadMarker',
      netRegex: { id: headMarkerData['slaughterSpread'], capture: true },
      condition: Conditions.targetIsYou(),
      delaySeconds: 0.1, // Delay for followup direction
      durationSeconds: 6,
      suppressSeconds: 1,
      alertText: (data, _matches, output) => {
        const stackDir = data.splattershedStackDir;
        const dir = stackDir === 'northwest'
          ? 'northeast'
          : stackDir === 'northeast'
          ? 'northwest'
          : undefined;
        const slaughter = data.slaughtershed;

        if (dir) {
          if (slaughter === undefined)
            return output.spreadDir!({ dir: output[dir]!() });
          return output.spreadThenDodge!({
            spread: output.spreadDir!({ dir: output[dir]!() }),
            dodge: output[slaughter]!(),
          });
        }
        if (slaughter === undefined)
          return output.spread!();
        return output.spreadThenDodge!({
          spread: output.spread!(),
          dodge: output[slaughter]!(),
        });
      },
      outputStrings: {
        left: Outputs.left,
        right: Outputs.right,
        northeastKnockback: {
          en: 'Knockback from Northeast',
          de: 'Rückstoß von Nordosten',
          cn: '从右上击退',
          ko: '북동쪽에서 넉백',
        },
        northwestKnockback: {
          en: 'Knockback from Northwest',
          de: 'Rückstoß von Nordwesten',
          cn: '从左上击退',
          ko: '북서쪽에서 넉백',
        },
        northeast: Outputs.dirNE,
        northwest: Outputs.dirNW,
        spread: Outputs.spread,
        spreadDir: {
          en: 'Spread ${dir}',
          de: 'Verteilen ${dir}',
          cn: '${dir}分散',
          ko: '산개 ${dir}',
        },
        spreadThenDodge: {
          en: '${spread} => ${dodge}',
          de: '${spread} => ${dodge}',
          cn: '${spread} => ${dodge}',
          ko: '${spread} => ${dodge}',
        },
      },
    },
    {
      id: 'R12S Splattershed Safe Spot Cleanup',
      type: 'HeadMarker',
      netRegex: { id: headMarkerData['slaughterStack'], capture: false },
      delaySeconds: 0.2,
      run: (data) => delete data.splattershedStackDir,
    },
    {
      id: 'R12S Serpintine Scourge and Raptor Knuckles',
      // Trigger on B4D4 Dramatic Lysis or B4D5 Fourth-wall Fusion
      type: 'Ability',
      netRegex: { id: ['B4D4', 'B4D5'], source: 'Lindwurm', capture: false },
      durationSeconds: 5.5,
      suppressSeconds: 1,
      alertText: (data, _matches, output) => {
        const slaughtershed = data.slaughtershed;
        if (slaughtershed)
          return output[slaughtershed]!();
      },
      outputStrings: {
        right: Outputs.rightThenLeft,
        left: Outputs.leftThenRight,
        northwestKnockback: {
          en: 'Knockback from Northwest => Knockback from Northeast',
          de: 'Rückstoß von Nordwesten => Rückstoß von Nordosten',
          cn: '从左上击退 => 从右上击退',
          ko: '북서에서 넉백 => 북동에서 넉백',
        },
        northeastKnockback: {
          en: 'Knockback from Northeast => Knockback from Northwest',
          de: 'Rückstoß von Nordosten => Rückstoß von Nordwesten',
          cn: '从右上击退 => 从左上击退',
          ko: '북동에서 넉백 => 북서에서 넉백',
        },
      },
    },
    {
      id: 'R12S Raptor Knuckles Uptime Knockback',
      // First knockback is at ~13.374s
      // Second knockback is at ~17.964s
      // Use knockback at ~11.5s to hit both with ~1.8s leniency
      // ~11.457s before is too late as it comes off the same time as hit
      // ~11.554s before works (surecast ends ~0.134 after hit)
      type: 'Ability',
      netRegex: { id: ['B4CC', 'B4CE'], source: 'Lindwurm', capture: false },
      condition: (data) => {
        if (data.phase === 'slaughtershed' && data.triggerSetConfig.uptimeKnockbackStrat)
          return true;
        return false;
      },
      delaySeconds: 11.5,
      durationSeconds: 1.8,
      response: Responses.knockback(),
    },
    {
      id: 'R12S Serpentine Scourge Left Followup',
      type: 'Ability',
      netRegex: { id: 'B4D1', source: 'Lindwurm', capture: false },
      condition: (data) => {
        if (data.slaughtershed)
          return true;
        return false;
      },
      response: Responses.goLeft(),
    },
    {
      id: 'R12S Serpentine Scourge Right Followup',
      type: 'Ability',
      netRegex: { id: 'B4D2', source: 'Lindwurm', capture: false },
      condition: (data) => {
        if (data.slaughtershed)
          return true;
        return false;
      },
      response: Responses.goRight(),
    },
    {
      id: 'R12S Raptor Knuckles Northeast Followup',
      type: 'Ability',
      netRegex: { id: 'B4D0', source: 'Lindwurm', capture: false },
      condition: (data) => {
        if (data.slaughtershed)
          return true;
        return false;
      },
      delaySeconds: 0.8, // Time until B9C7 knockback
      alertText: (_data, _matches, output) => output.northwestKnockback!(),
      outputStrings: {
        northwestKnockback: {
          en: 'Knockback from Northwest',
          de: 'Rückstoß von Nordwesten',
          cn: '从左上击退',
          ko: '북서쪽에서 넉백',
        },
      },
    },
    {
      id: 'R12S Raptor Knuckles Northwest Followup',
      type: 'Ability',
      netRegex: { id: 'B4CF', source: 'Lindwurm', capture: false },
      condition: (data) => {
        if (data.slaughtershed)
          return true;
        return false;
      },
      delaySeconds: 0.8, // Time until B9C7 knockback
      alertText: (_data, _matches, output) => output.northeastKnockback!(),
      outputStrings: {
        northeastKnockback: {
          en: 'Knockback from Northeast',
          de: 'Rückstoß von Nordosten',
          cn: '从右上击退',
          ko: '북동쪽에서 넉백',
        },
      },
    },
    {
      id: 'R12S Slaughtershed Cleanup',
      type: 'Ability',
      netRegex: { id: ['B4D1', 'B4D2', 'B4D0', 'B4CF'], source: 'Lindwurm', capture: false },
      condition: (data) => {
        if (data.slaughtershed)
          return true;
        return false;
      },
      run: (data) => delete data.slaughtershed,
    },
    {
      id: 'R12S Refreshing Overkill',
      // B538 has a 10s castTime that could end with enrage or raidwide
      // Raidwide cast, B539, happens .2s after but it's not until 5.4s~5.8s later that the damage is applied
      // Mits applied after "cast" still count towards the damage application
      type: 'Ability',
      netRegex: { id: 'B539', source: 'Lindwurm', capture: false },
      durationSeconds: 5,
      suppressSeconds: 9999,
      response: Responses.bigAoe('alert'),
    },
    // Phase 2
    {
      id: 'R12S Arcadia Aflame',
      type: 'StartsUsing',
      netRegex: { id: 'B528', source: 'Lindwurm', capture: false },
      response: Responses.bigAoe('alert'),
    },
    {
      id: 'R12S Winged Scourge',
      // B4DA E/W or N/S clones Facing S, Cleaving Front/Back (North/South)
      // B4DB N/S or E/W clones Facing W, Cleaving Front/Back (East/West)
      // Clones are positioned:
      //          (100, 86)
      // (86, 100)         (114, 100)
      //          (100, 114)
      type: 'StartsUsing',
      netRegex: { id: ['B4DA', 'B4DB'], source: 'Lindschrat', capture: true },
      suppressSeconds: 1,
      infoText: (data, matches, output) => {
        if (matches.id === 'B4DA') {
          if (data.replication1FollowUp)
            return output.northSouthCleaves2!();

          const x = parseFloat(matches.x);
          if (x < 87 || x > 113)
            return output.eWCleavingNorthSouth!();
          return output.nSCleavingNorthSouth!();
        }
        if (data.replication1FollowUp)
          return output.eastWestCleaves2!();

        const x = parseFloat(matches.x);
        if (x < 87 || x > 113)
          return output.eWCleavingEastWest!();
        return output.nSCleavingEastWest!();
      },
      outputStrings: {
        nSCleavingNorthSouth: {
          en: 'N/S Cleaving North/South',
          de: 'N/S Cleaven Norden/Süden',
          cn: '上/下扇形 上/下',
          ko: '북/남 부채꼴 북쪽/남쪽',
        },
        eWCleavingNorthSouth: {
          en: 'E/W Cleaving North/South',
          de: 'O/W Cleaven Norden/Süden',
          cn: '左/右扇形 上/下',
          ko: '동/서 부채꼴 북쪽/남쪽',
        },
        nSCleavingEastWest: {
          en: 'N/S Cleaving East/West',
          de: 'N/S Cleaven Osten/Westen',
          cn: '上/下扇形 左/右',
          ko: '북/남 부채꼴 동쪽/서쪽',
        },
        eWCleavingEastWest: {
          en: 'E/W Cleaving East/West',
          de: 'O/W Cleaven Osten/Westen',
          cn: '左/右扇形 左/右',
          ko: '동/서 부채꼴 동쪽/서쪽',
        },
        northSouthCleaves2: {
          en: 'North/South Cleaves',
          de: 'Nord/Süd Cleaves',
          cn: '上/下扇形',
          ko: '북/남 부채꼴',
        },
        eastWestCleaves2: {
          en: 'East/West Cleaves',
          de: 'Ost/West Cleaves',
          cn: '左/右扇形',
          ko: '동/서 부채꼴',
        },
      },
    },
    {
      id: 'R12S Fire and Dark Resistance Down II Collector',
      // CFB Dark Resistance Down II
      // B79 Fire Resistance Down II
      type: 'GainsEffect',
      netRegex: { effectId: ['CFB', 'B79'], capture: true },
      condition: (data) => !data.replication1FollowUp,
      run: (data, matches) => {
        const debuff = matches.effectId === 'CFB' ? 'dark' : 'fire';
        if (data.me === matches.target)
          data.replication1Debuff = debuff;

        if (debuff === 'fire')
          data.replication1FireDebuffCounter = data.replication1FireDebuffCounter + 1;
        else
          data.replication1DarkDebuffCounter = data.replication1DarkDebuffCounter + 1;
      },
    },
    {
      id: 'R12S Fire and Dark Resistance Down II',
      // CFB Dark Resistance Down II
      // B79 Fire Resistance Down II
      type: 'GainsEffect',
      netRegex: { effectId: ['CFB', 'B79'], capture: true },
      condition: (data, matches) => {
        if (data.me === matches.target)
          return !data.replication1FollowUp;
        return false;
      },
      suppressSeconds: 9999,
      infoText: (_data, matches, output) => {
        return matches.effectId === 'CFB' ? output.dark!() : output.fire!();
      },
      outputStrings: {
        fire: {
          en: 'Fire Debuff: Spread near Dark (later)',
          de: 'Feuer Debuff: Nahe Dunkel verteilen (später)',
          cn: '火 Debuff: 暗附近分散 (稍后)',
          ko: '불 디버프: 어둠 근처 산개 (나중에)',
        },
        dark: {
          en: 'Dark Debuff: Stack near Fire (later)',
          de: 'Dunkel Debuff: Nahe Feuer sammeln (später)',
          cn: '暗 Debuff: 火附近分摊 (稍后)',
          ko: '어둠 디버프: 불 근처 쉐어 (나중에)',
        },
      },
    },
    {
      id: 'R12S Fake Fire Resistance Down II',
      // Two players will not receive a debuff, they will need to act as if they had
      // Mechanics happen across 1.1s
      type: 'GainsEffect',
      netRegex: { effectId: ['CFB', 'B79'], capture: false },
      condition: (data) => !data.replication1FollowUp,
      delaySeconds: 1.3, // +0.2s Delay for debuff/damage propagation
      suppressSeconds: 9999,
      infoText: (data, _matches, output) => {
        if (data.replication1Debuff === undefined) {
          // Expecting 2 Fire, 4 Dark (6 Total)
          if (
            data.replication1FireDebuffCounter === 2 &&
            data.replication1DarkDebuffCounter === 4
          )
            return output.noDebuff!();
          return output.noDebuffFail!();
        }
      },
      outputStrings: {
        noDebuff: {
          en: 'No Debuff: Spread near Dark (later)',
          de: 'Kein Debuff: Nahe Dunkel verteilen (später)',
          cn: '无 Debuff: 暗附近分散 (稍后)',
          ko: '디버프 없음: 어둠 근처 산개 (나중에)',
        },
        noDebuffFail: {
          en: 'Debuffs Messed Up, Check Partner',
          de: 'Debuffs durcheinander, Partner überprüfen',
          cn: 'Debuff 获取故障, 检查搭档状态',
          ko: '디버프 꼬임, 파트너 확인',
        },
      },
    },
    {
      id: 'R12S Snaking Kick',
      // Targets random player
      // Second cast of this happens before Grotesquerie, delay until Grotesquerie to reduce chance of none projection players running into it
      type: 'StartsUsing',
      netRegex: { id: 'B527', source: 'Lindwurm', capture: true },
      delaySeconds: 0.1, // Need to delay for actor position update
      suppressSeconds: 9999,
      alertText: (data, matches, output) => {
        const actor = data.actorPositions[matches.sourceId];
        if (actor === undefined)
          return output.getBehind!();

        const dirNum = (Directions.hdgTo16DirNum(actor.heading) + 8) % 16;
        const dir = Directions.output16Dir[dirNum] ?? 'unknown';
        return output.getBehindDir!({
          dir: output[dir]!(),
          mech: output.getBehind!(),
        });
      },
      outputStrings: {
        ...Directions.outputStrings16Dir,
        getBehind: Outputs.getBehind,
        getBehindDir: {
          en: '${dir}: ${mech}',
          de: '${dir}: ${mech}',
          cn: '${dir}: ${mech}',
          ko: '${dir}: ${mech}',
        },
      },
    },
    {
      id: 'R12S Replication 1 Follow-up Tracker',
      // Tracking from B527 Snaking Kick
      type: 'Ability',
      netRegex: { id: 'B527', source: 'Lindwurm', capture: false },
      suppressSeconds: 9999,
      run: (data) => data.replication1FollowUp = true,
    },
    {
      id: 'R12S Top-Tier Slam Actor Collect',
      // Fire NPCs always move in the first Set
      // Locations are static
      // Fire => Dark => Fire => Dark
      // Dark => Fire => Dark => Fire
      // The other 4 cleave in a line
      // (90, 90)           (110, 90)
      //      (95, 95)  (105, 95)
      //             Boss
      //      (95, 100) (105, 105)
      // (90, 110)          (110, 110)
      // ActorMove ~0.3s later will have the data
      // ActorSet from the clones splitting we can infer the fire entities since their positions and headings are not perfect
      // For first set there are two patterns that use these coordinates:
      //           (100, 86)
      // (86, 100)           (114, 100)
      //           (100, 114)
      // Either N/S are clones casting Winged Scourge, or the E/W clones cast Winged Scourge
      // Each pattern has its own pattern for IDs of the clones, in order
      // N/S will have Fire -5 and -6 of its original
      // E/W will have Fire -6 and -7 of its original
      // Could use -6 to cover both cases, but that doesn't determine which add jumps first
      type: 'Ability',
      netRegex: { id: 'B4D9', source: 'Lindschrat', capture: true },
      condition: (data, matches) => {
        if (data.replication1FollowUp) {
          const pos = data.actorPositions[matches.sourceId];
          if (pos === undefined)
            return false;
          // These values should be 0 when x or y coord has non-zero decimal values
          // Heading is also checked as the non fire clones all face a perfect heading
          const xFilter = pos.x % 1;
          const yFilter = pos.y % 1;
          if (xFilter === 0 && yFilter === 0 && pos.heading === 0)
            return false;
          return true;
        }
        return false;
      },
      suppressSeconds: 9999, // Only need one of the two
      run: (data, matches) => data.replication1FireActor = matches.sourceId,
    },
    {
      id: 'R12S Top-Tier Slam/Mighty Magic Locations',
      type: 'Ability',
      netRegex: { id: 'B4D9', source: 'Lindschrat', capture: false },
      condition: (data) => {
        if (data.replication1FollowUp && data.replication1FireActor !== undefined)
          return true;
        return false;
      },
      delaySeconds: 1, // Data is sometimes not available right away
      suppressSeconds: 9999,
      infoText: (data, _matches, output) => {
        const fireId = data.replication1FireActor;
        if (fireId === undefined)
          return;

        const actor = data.actorPositions[fireId];
        if (actor === undefined)
          return;

        const debuff = data.replication1Debuff;
        const x = actor.x;
        const dirNum = Directions.xyTo8DirNum(x, actor.y, center.x, center.y);
        const dir1 = Directions.output8Dir[dirNum] ?? 'unknown';
        const dirNum2 = (dirNum + 4) % 8;
        const dir2 = Directions.output8Dir[dirNum2] ?? 'unknown';

        // Check if combatant moved to inner or outer
        const isIn = (x > 94 && x < 106);
        const fireIn = isIn ? dir1 : dir2;
        const fireOut = isIn ? dir2 : dir1;

        if (debuff === 'dark')
          return output.fire!({
            dir1: output[fireIn]!(),
            dir2: output[fireOut]!(),
          });

        // Dark will be opposite pattern of Fire
        const darkIn = isIn ? dir2 : dir1;
        const darkOut = isIn ? dir1 : dir2;

        // Fire debuff players and unmarked bait Dark
        // Expecting 2 Fire, 4 Dark (6 Total)
        if (
          debuff === 'fire' ||
          (
            data.replication1FireDebuffCounter === 2 &&
            data.replication1DarkDebuffCounter === 4
          )
        )
          return output.dark!({
            dir1: output[darkIn]!(),
            dir2: output[darkOut]!(),
          });
        // Non-debuff players when not 2 fire and 4 dark debuffs
        // Output Dark location as 2 players will need it
        return output.darkDebuffFail!({
          dir1: output[darkIn]!(),
          dir2: output[darkOut]!(),
        });
      },
      outputStrings: {
        ...Directions.outputStringsIntercardDir, // Cardinals should result in '???'
        fire: {
          en: 'Bait Fire In ${dir1}/Out ${dir2} (Partners)',
          de: 'Köder Feuer im ${dir1}/Außen ${dir2} (Partner)',
          cn: '内${dir1}/外${dir2}诱导火 (和搭档一起)',
          ko: '불 안쪽 ${dir1}/바깥쪽 ${dir2} 유도 (파트너)',
        },
        dark: {
          en: 'Bait Dark In ${dir1}/Out ${dir2} (Solo)',
          de: 'Köder Dunkel in ${dir1}/Außen ${dir2} (Solo)',
          cn: '内${dir1}/外${dir2}诱导暗 (单独)',
          ko: '어둠 안쪽 ${dir1}/바깥쪽 ${dir2} 유도 (혼자)',
        },
        darkDebuffFail: {
          en: 'Check Partner, Dark is In ${dir1}/Out ${dir2}',
          de: 'Partner überprüfen, Dunkel ist ${dir1}/Außen ${dir2}',
          cn: '检查搭档状态, 暗在内${dir1}/外${dir2}',
          ko: '파트너 확인, 어둠 안쪽 ${dir1}/바깥쪽 ${dir2}',
        },
      },
    },
    {
      id: 'R12S Double Sobat',
      // Shared half-room cleave on tank => random turn half-room cleave =>
      // Esoteric Finisher big circle aoes that hits two highest emnity targets
      type: 'HeadMarker',
      netRegex: { id: headMarkerData['sharedTankbuster'], capture: true },
      response: Responses.sharedTankBuster(),
    },
    {
      id: 'R12S Double Sobat 2',
      // Followup half-room cleave:
      // B521 Double Sobat: 0 degree left turn then B525
      // B522 Double Sobat: 90 degree left turn then B525
      // B523 Double Sobat: 180 degree left turn then B525
      // B524 Double Sobat: 270 degree left turn (this ends up 90 degrees to the right)
      type: 'Ability',
      netRegex: { id: ['B521', 'B522', 'B523', 'B524'], source: 'Lindwurm', capture: true },
      suppressSeconds: 1,
      alertText: (_data, matches, output) => {
        const x = parseFloat(matches.x);
        const y = parseFloat(matches.y);
        const targetX = parseFloat(matches.targetX);
        const targetY = parseFloat(matches.targetY);
        // Boss snaps to the player position which could be different from heading at time of ability
        const dirNum = Directions.xyTo16DirNum(targetX, targetY, x, y);
        const getNewDirNum = (
          dirNum: number,
          id: string,
        ): number => {
          switch (id) {
            case 'B521':
              return dirNum;
            case 'B522':
              return dirNum - 4;
            case 'B523':
              return dirNum - 8;
            case 'B524':
              return dirNum - 12;
          }
          throw new UnreachableCode();
        };

        // Adding 16 incase of negative values
        const newDirNum = (getNewDirNum(dirNum, matches.id) + 16 + 8) % 16;

        const dir = Directions.output16Dir[newDirNum] ?? 'unknown';
        return output.getBehindDir!({
          dir: output[dir]!(),
          mech: output.getBehind!(),
        });
      },
      outputStrings: {
        ...Directions.outputStrings16Dir,
        getBehind: Outputs.getBehind,
        getBehindDir: {
          en: '${dir}: ${mech}',
          de: '${dir}: ${mech}',
          cn: '${dir}: ${mech}',
          ko: '${dir}: ${mech}',
        },
      },
    },
    {
      id: 'R12S Esoteric Finisher',
      // After Double Sobat 2, boss hits targets highest emnity target, second targets second highest
      type: 'StartsUsing',
      netRegex: { id: 'B525', source: 'Lindwurm', capture: true },
      delaySeconds: (_data, matches) => parseFloat(matches.castTime),
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          tankBusterCleaves: Outputs.tankBusterCleaves,
          avoidTankCleaves: Outputs.avoidTankCleaves,
        };

        if (data.role === 'tank' || data.role === 'healer') {
          if (data.role === 'healer')
            return { infoText: output.tankBusterCleaves!() };
          return { alertText: output.tankBusterCleaves!() };
        }
        return { infoText: output.avoidTankCleaves!() };
      },
    },
    {
      id: 'R12S Staging 1 Tethered Clone Collect',
      // Map the locations to a player name
      type: 'Tether',
      netRegex: { id: headMarkerData['lockedTether'], capture: true },
      condition: (data) => data.replicationCounter === 1,
      run: (data, matches) => {
        const actor = data.actorPositions[matches.sourceId];
        if (actor === undefined)
          return;

        const dirNum = Directions.xyTo8DirNum(actor.x, actor.y, center.x, center.y);
        data.replication2CloneDirNumPlayers[dirNum] = matches.target;
      },
    },
    {
      id: 'R12S Staging 1 Tethered Clone',
      // Combatants are added ~4s before Staging starts casting
      // Same tether ID is used for "locked" ability tethers
      type: 'Tether',
      netRegex: { id: headMarkerData['lockedTether'], capture: true },
      condition: Conditions.targetIsYou(),
      suppressSeconds: 9999,
      infoText: (data, matches, output) => {
        const actor = data.actorPositions[matches.sourceId];
        if (actor === undefined)
          return output.cloneTether!();

        const dirNum = Directions.xyTo8DirNum(actor.x, actor.y, center.x, center.y);
        const dir = Directions.output8Dir[dirNum] ?? 'unknown';
        return output.cloneTetherDir!({ dir: output[dir]!() });
      },
      outputStrings: {
        ...Directions.outputStrings8Dir,
        cloneTether: {
          en: 'Tethered to Clone',
          de: 'Verbunden zum Klon',
          cn: '分身连线',
          ko: '분신과 연결됨',
        },
        cloneTetherDir: {
          en: 'Tethered to ${dir} Clone',
          de: 'Verbunden zum ${dir} Klon',
          cn: '与${dir}分身连线',
          ko: '${dir} 분신과 연결됨',
        },
      },
    },
    {
      id: 'R12S Replication 2 and Replication 4 Ability Tethers Collect',
      // Record and store a map of where the tethers come from and what they do for later
      type: 'Tether',
      netRegex: {
        id: [
          headMarkerData['projectionTether'],
          headMarkerData['manaBurstTether'],
          headMarkerData['heavySlamTether'],
          headMarkerData['fireballSplashTether'],
        ],
        capture: true,
      },
      condition: (data) => {
        if (data.phase === 'replication2' || data.phase === 'idyllic')
          return true;
        return false;
      },
      run: (data, matches) => {
        const actor = data.actorPositions[matches.sourceId];
        if (actor === undefined)
          return;
        const dirNum = Directions.xyTo8DirNum(actor.x, actor.y, center.x, center.y);
        if (data.phase === 'replication2') {
          // Handle boss tether separately as its direction location is unimportant
          if (matches.id !== headMarkerData['fireballSplashTether'])
            data.replication2DirNumAbility[dirNum] = matches.id;
        }
        if (data.phase === 'idyllic')
          data.replication4DirNumAbility[dirNum] = matches.id;
      },
    },
    {
      id: 'R12S Replication 2 Ability Tethers Initial Call',
      // Occur ~8s after end of Replication 2 cast
      type: 'Tether',
      netRegex: {
        id: [
          headMarkerData['projectionTether'],
          headMarkerData['manaBurstTether'],
          headMarkerData['heavySlamTether'],
          headMarkerData['fireballSplashTether'],
        ],
        capture: false,
      },
      suppressSeconds: 9999, // Only trigger on first tether
      infoText: (data, _matches, output) => {
        const clones = data.replication2CloneDirNumPlayers;
        const strat = data.triggerSetConfig.replication2Strategy;
        const myDirNum = Object.keys(clones).find(
          (key) => clones[parseInt(key)] === data.me,
        );

        if (myDirNum !== undefined) {
          // Get dirNum of player for custom output based on staging 1 tether
          // Player can replace the get tether with get defamation, get stack and
          // the location they want based on custom plan
          switch (parseInt(myDirNum)) {
            case 0:
              return output.getTetherNClone!({
                tether: strat === 'dn'
                  ? output.getBossTether!()
                  : strat === 'banana'
                  ? output.getConeTetherCW!()
                  : strat === 'nukemaru'
                  ? output.getConeTetherCCW!()
                  : output.getTether!(),
              });
            case 1:
              return output.getTetherNEClone!({
                tether: strat === 'dn'
                  ? output.getConeTetherCW!()
                  : strat === 'banana'
                  ? output.getDefamationTetherCW!()
                  : strat === 'nukemaru'
                  ? output.getStackTetherCCW!()
                  : output.getTether!(),
              });
            case 2:
              return output.getTetherEClone!({
                tether: strat === 'dn'
                  ? output.getStackTetherCW!()
                  : strat === 'banana'
                  ? output.getNoTether!()
                  : strat === 'nukemaru'
                  ? output.getBossTether!()
                  : output.getTether!(),
              });
            case 3:
              return output.getTetherSEClone!({
                tether: strat === 'dn'
                  ? output.getDefamationTetherCW!()
                  : strat === 'banana'
                  ? output.getDefamationTetherCCW!()
                  : strat === 'nukemaru'
                  ? output.getStackTetherCW!()
                  : output.getTether!(),
              });
            case 4:
              return output.getTetherSClone!({
                tether: strat === 'dn'
                  ? output.getNoTether!()
                  : strat === 'banana'
                  ? output.getConeTetherCCW!()
                  : strat === 'nukemaru'
                  ? output.getConeTetherCW!()
                  : output.getTether!(),
              });
            case 5:
              return output.getTetherSWClone!({
                tether: strat === 'dn'
                  ? output.getDefamationTetherCCW!()
                  : strat === 'banana'
                  ? output.getStackTetherCCW!()
                  : strat === 'nukemaru'
                  ? output.getDefamationTetherCW!()
                  : output.getTether!(),
              });
            case 6:
              return output.getTetherWClone!({
                tether: strat === 'dn'
                  ? output.getStackTetherCCW!()
                  : strat === 'banana'
                  ? output.getBossTether!()
                  : strat === 'nukemaru'
                  ? output.getNoTether!()
                  : output.getTether!(),
              });
            case 7:
              return output.getTetherNWClone!({
                tether: strat === 'dn'
                  ? output.getConeTetherCCW!()
                  : strat === 'banana'
                  ? output.getStackTetherCW!()
                  : strat === 'nukemaru'
                  ? output.getDefamationTetherCCW!()
                  : output.getTether!(),
              });
          }
        }

        // Unknown staging clone tether
        return output.getTether!();
      },
      outputStrings: replication2OutputStrings,
    },
    {
      id: 'R12S Replication 2 Locked Tether Collect',
      type: 'Tether',
      netRegex: { id: headMarkerData['lockedTether'], capture: true },
      condition: (data) => {
        if (
          data.phase === 'replication2' &&
          data.replicationCounter === 2
        )
          return true;
        return false;
      },
      run: (data, matches) => {
        const target = matches.target;
        const sourceId = matches.sourceId;
        const boss = headMarkerData['fireballSplashTether'];

        // Check if boss tether
        if (data.replication2BossId === sourceId)
          data.replication2PlayerAbilities[target] = boss;
        else if (data.replication2BossId !== sourceId) {
          const actor = data.actorPositions[sourceId];
          if (actor === undefined) {
            // Setting to use that we know we have a tether but couldn't determine what ability it is
            data.replication2PlayerAbilities[target] = 'unknown';
            return;
          }

          const dirNum = Directions.xyTo8DirNum(
            actor.x,
            actor.y,
            center.x,
            center.y,
          );

          // Lookup what the tether was at the same location
          const ability = data.replication2DirNumAbility[dirNum];
          if (ability === undefined) {
            // Setting to use that we know we have a tether but couldn't determine what ability it is
            data.replication2PlayerAbilities[target] = 'unknown';
            return;
          }
          data.replication2PlayerAbilities[target] = ability;
        }

        // Create ability order once we have all 8 players
        // If players had more than one tether previously, the extra tethers are randomly assigned
        if (Object.keys(data.replication2PlayerAbilities).length === 7) {
          // Fill in for player that had no tether, they are going to be boss' defamation
          if (data.replication2PlayerAbilities[data.me] === undefined)
            data.replication2PlayerAbilities[data.me] = 'none';

          const abilities = data.replication2PlayerAbilities;
          const order = [0, 4, 1, 5, 2, 6, 3, 7]; // Order in which clones spawned, this is static
          const players = data.replication2CloneDirNumPlayers; // Direction of player's clone

          // Mechanics are resolved clockwise
          for (const dirNum of order) {
            const player = players[dirNum] ?? 'unknown';
            // No Tether player wouldn't have an ability found for other
            // players, so this can be set to 'none' here when undefined
            // Additional players missing abilities, but received a tether
            // would have 'unknown' instead of undefined
            const ability = abilities[player] ?? 'none';
            data.replication2PlayerOrder.push(player);
            data.replication2AbilityOrder.push(ability);
          }

          // Detect recognized strategy by checking first 7 abilities
          const detectStrategy = (
            order: string[],
          ): 'dn' | 'banana' | 'nukemaru' | 'unknown' => {
            const defamation = headMarkerData['manaBurstTether'];
            const stack = headMarkerData['heavySlamTether'];
            const projection = headMarkerData['projectionTether'];
            // DN
            if (
              order[rep2DirIndexMap['north']] === boss &&
              order[rep2DirIndexMap['south']] === 'none' &&
              order[rep2DirIndexMap['northeast']] === projection &&
              order[rep2DirIndexMap['southwest']] === defamation &&
              order[rep2DirIndexMap['east']] === stack &&
              order[rep2DirIndexMap['west']] === stack &&
              order[rep2DirIndexMap['southeast']] === defamation
            )
              return 'dn';
            // Banana Codex
            if (
              order[rep2DirIndexMap['north']] === projection &&
              order[rep2DirIndexMap['south']] === projection &&
              order[rep2DirIndexMap['northeast']] === defamation &&
              order[rep2DirIndexMap['southwest']] === stack &&
              order[rep2DirIndexMap['east']] === 'none' &&
              order[rep2DirIndexMap['west']] === boss &&
              order[rep2DirIndexMap['southeast']] === defamation
            )
              return 'banana';
            // Nukemaru
            if (
              order[rep2DirIndexMap['north']] === projection &&
              order[rep2DirIndexMap['south']] === projection &&
              order[rep2DirIndexMap['northeast']] === stack &&
              order[rep2DirIndexMap['southwest']] === defamation &&
              order[rep2DirIndexMap['east']] === boss &&
              order[rep2DirIndexMap['west']] === 'none' &&
              order[rep2DirIndexMap['southeast']] === stack
            )
              return 'nukemaru';
            // Not Yet Supported, File a Feature Request or PR
            return 'unknown';
          };

          data.replication2StrategyDetected = detectStrategy(data.replication2AbilityOrder);
        }
      },
    },
    {
      id: 'R12S Replication 2 Locked Tether',
      type: 'Tether',
      netRegex: { id: headMarkerData['lockedTether'], capture: true },
      condition: (data, matches) => {
        if (
          data.phase === 'replication2' &&
          data.replicationCounter === 2 &&
          data.me === matches.target
        )
          return true;
        return false;
      },
      delaySeconds: 0.1,
      infoText: (data, matches, output) => {
        const sourceId = matches.sourceId;
        const strat = data.triggerSetConfig.replication2Strategy;
        // Check if it's the boss
        if (data.replication2BossId === sourceId)
          return output.fireballSplashTether!({
            mech1: strat === 'dn'
              ? output.baitJumpDNN!({ strat: output.north!() })
              : strat === 'banana'
              ? output.baitJumpBananaW!({ strat: output.west!() })
              : strat === 'nukemaru'
              ? output.baitJumpNukemaruE!({ strat: output.east!() })
              : output.baitJump!(),
          });

        // Get direction of the tether
        const actor = data.actorPositions[sourceId];
        const ability = data.replication2PlayerAbilities[data.me];
        const clones = data.replication2CloneDirNumPlayers;
        const myDirNum = Object.keys(clones).find(
          (key) => clones[parseInt(key)] === data.me,
        );
        const myDirNumInt = myDirNum === undefined ? -1 : parseInt(myDirNum);
        if (actor === undefined) {
          switch (ability) {
            case headMarkerData['projectionTether']:
              switch (myDirNumInt) {
                case 0: // Banana and Nukemaru
                  return output.projectionTether!({
                    mech1: strat === 'banana'
                      ? output.baitProteanBananaN!({
                        strat: output['dirWSW']!(),
                      }) // Southmost protean
                      : strat === 'nukemaru'
                      ? output.baitProteanNukemaruN!({
                        strat: output['dirENE']!(),
                      }) // Northmost protean
                      : output.baitProtean!(),
                  });
                case 1: // DN only
                  return output.projectionTether!({
                    mech1: strat === 'dn'
                      ? output.baitProteanDNNE!({ strat: output.north!() }) // Inner NNE
                      : output.baitProtean!(),
                  });
                case 4: // Banana and Nukemaru
                  return output.projectionTether!({
                    mech1: strat === 'banana'
                      ? output.baitProteanBananaS!({
                        strat: output['dirWNW']!(),
                      }) // Northmost protean
                      : strat === 'nukemaru'
                      ? output.baitProteanNukemaruN!({
                        strat: output['dirESE']!(),
                      }) // Southmost protean
                      : output.baitProtean!(),
                  });
                case 7: // DN only
                  return output.projectionTether!({
                    mech1: strat === 'dn'
                      ? output.baitProteanDNNW!({ strat: output.north!() }) // Inner NNW
                      : output.baitProtean!(),
                  });
              }
              return output.projectionTether!({
                mech1: strat === 'dn'
                  ? output.baitProteanDN!({ strat: output.north!() })
                  : strat === 'banana'
                  ? output.baitProteanBanana!({ strat: output.west!() })
                  : strat === 'nukemaru'
                  ? output.baitProteanNukemaru!({ strat: output.east!() })
                  : output.baitProtean!(),
              });
            case headMarkerData['manaBurstTether']:
              switch (myDirNumInt) {
                case 1: // Banana Only
                  return output.manaBurstTether!({
                    mech1: strat === 'banana'
                      ? output.defamationOnYouBananaNE!({
                        strat: output['dirNNE']!(),
                      }) // North/NNE
                      : output.defamationOnYou!(),
                  });
                case 3: // DN and Banana
                  return output.manaBurstTether!({
                    mech1: strat === 'dn'
                      ? output.defamationOnYouDNSE!({
                        strat: output['dirESE']!(),
                      }) // East/ESE
                      : strat === 'banana'
                      ? output.defamationOnYouBananaSE!({
                        strat: output['dirSSE']!(),
                      }) // South/SSE
                      : output.defamationOnYou!(),
                  });
                case 5: // DN and Nukemaru
                  return output.manaBurstTether!({
                    mech1: strat === 'dn'
                      ? output.defamationOnYouDNSW!({
                        strat: output['dirWSW']!(),
                      }) // West/WSW
                      : strat === 'nukemaru'
                      ? output.defamationOnYouNukemaruSW!({
                        strat: output['dirSSW']!(),
                      }) // South/SSW
                      : output.defamationOnYou!(),
                  });
                case 7: // Nukemaru Only
                  return output.manaBurstTether!({
                    mech1: strat === 'nukemaru'
                      ? output.defamationOnYouNukemaruNW!({
                        strat: output['dirNNW']!(),
                      }) // North/NNW
                      : output.defamationOnYou!(),
                  });
              }
              return output.manaBurstTether!({
                mech1: output.defamationOnYou!(),
              });
            case headMarkerData['heavySlamTether']:
              switch (myDirNumInt) {
                case 1: // Nukemaru Only
                  return output.heavySlamTether!({
                    mech1: strat === 'nukemaru'
                      ? output.baitProteanNukemaruNE!({ strat: output.east!() }) // Inner ENE
                      : output.baitProtean!(),
                  });
                case 2: // DN Only
                  return output.heavySlamTether!({
                    mech1: strat === 'dn'
                      ? output.baitProteanDNE!({
                        strat: output['dirNNE']!(),
                      }) // Eastmost Protean
                      : output.baitProtean!(),
                  });
                case 3: // Nukemaru Only
                  return output.heavySlamTether!({
                    mech1: strat === 'nukemaru'
                      ? output.baitProteanNukemaruSE!({ strat: output.east!() }) // Inner ESE
                      : output.baitProtean!(),
                  });
                case 5: // Banana Only
                  return output.heavySlamTether!({
                    mech1: strat === 'banana'
                      ? output.baitProteanBananaSW!({ strat: output.west!() }) // Inner WSW
                      : output.baitProtean!(),
                  });
                case 6: // DN Only
                  return output.heavySlamTether!({
                    mech1: strat === 'dn'
                      ? output.baitProteanDNW!({
                        strat: output['dirNNW']!(),
                      }) // Westmost Protean
                      : output.baitProtean!(),
                  });
                case 7: // Banana Only
                  return output.heavySlamTether!({
                    mech1: strat === 'banana'
                      ? output.baitProteanBananaNW!({ strat: output.west!() }) // Inner WNW
                      : output.baitProtean!(),
                  });
              }
              return output.heavySlamTether!({
                mech1: strat === 'dn'
                  ? output.baitProteanDN!({ strat: output.north!() })
                  : strat === 'banana'
                  ? output.baitProteanBanana!({ strat: output.west!() })
                  : output.baitProtean!(),
              });
          }
          return;
        }

        const dirNum = Directions.xyTo8DirNum(
          actor.x,
          actor.y,
          center.x,
          center.y,
        );
        const dir = Directions.output8Dir[dirNum] ?? 'unknown';

        switch (ability) {
          case headMarkerData['projectionTether']:
            switch (myDirNumInt) {
              case 0: // Banana and Nukemaru
                return output.projectionTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'banana'
                    ? output.baitProteanBananaN!({
                      strat: output['dirWSW']!(),
                    }) // Southmost protean
                    : strat === 'nukemaru'
                    ? output.baitProteanNukemaruN!({
                      strat: output['dirENE']!(),
                    }) // Northmost protean
                    : output.baitProtean!(),
                });
              case 1: // DN only
                return output.projectionTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'dn'
                    ? output.baitProteanDNNE!({ strat: output.north!() }) // Inner NNE
                    : output.baitProtean!(),
                });
              case 4: // Banana and Nukemaru
                return output.projectionTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'banana'
                    ? output.baitProteanBananaS!({
                      strat: output['dirWNW']!(),
                    }) // Northmost protean
                    : strat === 'nukemaru'
                    ? output.baitProteanNukemaruS!({
                      strat: output['dirESE']!(),
                    }) // Southmost protean
                    : output.baitProtean!(),
                });
              case 7: // DN only
                return output.projectionTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'dn'
                    ? output.baitProteanDNNW!({ strat: output.north!() }) // Inner NNW
                    : output.baitProtean!(),
                });
            }
            return output.projectionTetherDir!({
              dir: output[dir]!(),
              mech1: strat === 'dn'
                ? output.baitProteanDN!({ strat: output.north!() })
                : strat === 'banana'
                ? output.baitProteanBanana!({ strat: output.west!() })
                : strat === 'nukemaru'
                ? output.baitProteanNukemaru!({ strat: output.east!() })
                : output.baitProtean!(),
            });
          case headMarkerData['manaBurstTether']:
            switch (myDirNumInt) {
              case 1: // Banana Only
                return output.manaBurstTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'banana'
                    ? output.defamationOnYouBananaNE!({
                      strat: output['dirNNE']!(),
                    }) // North/NNE
                    : output.defamationOnYou!(),
                });
              case 3: // DN and Banana
                return output.manaBurstTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'dn'
                    ? output.defamationOnYouDNSE!({
                      strat: output['dirESE']!(),
                    }) // East/ESE
                    : strat === 'banana'
                    ? output.defamationOnYouBananaSE!({
                      strat: output['dirSSE']!(),
                    }) // South/SSE
                    : output.defamationOnYou!(),
                });
              case 5: // DN and Nukemaru
                return output.manaBurstTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'dn'
                    ? output.defamationOnYouDNSW!({
                      strat: output['dirWSW']!(),
                    }) // West/WSW
                    : strat === 'nukemaru'
                    ? output.defamationOnYouNukemaruSW!({
                      strat: output['dirSSW']!(),
                    }) // South/SSW
                    : output.defamationOnYou!(),
                });
              case 7: // Nukemaru Only
                return output.manaBurstTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'nukemaru'
                    ? output.defamationOnYouNukemaruNW!({
                      strat: output['dirNNW']!(),
                    }) // North/NNW
                    : output.defamationOnYou!(),
                });
            }
            return output.manaBurstTetherDir!({
              dir: output[dir]!(),
              mech1: output.defamationOnYou!(),
            });
          case headMarkerData['heavySlamTether']:
            switch (myDirNumInt) {
              case 1: // Nukemaru Only
                return output.heavySlamTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'nukemaru'
                    ? output.baitProteanNukemaruNE!({ strat: output.east!() }) // Inner ENE
                    : output.baitProtean!(),
                });
              case 2: // DN Only
                return output.heavySlamTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'dn'
                    ? output.baitProteanDNE!({
                      strat: output['dirNNE']!(),
                    }) // Eastmost Protean
                    : output.baitProtean!(),
                });
              case 3: // Nukemaru Only
                return output.heavySlamTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'nukemaru'
                    ? output.baitProteanNukemaruSE!({ strat: output.east!() }) // Inner ESE
                    : output.baitProtean!(),
                });
              case 5: // Banana Only
                return output.heavySlamTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'banana'
                    ? output.baitProteanBananaSW!({ strat: output.west!() }) // Inner WSW
                    : output.baitProtean!(),
                });
              case 6: // DN Only
                return output.heavySlamTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'dn'
                    ? output.baitProteanDNW!({
                      strat: output['dirNNW']!(),
                    }) // Westmost Protean
                    : output.baitProtean!(),
                });
              case 7: // Banana Only
                return output.heavySlamTetherDir!({
                  dir: output[dir]!(),
                  mech1: strat === 'banana'
                    ? output.baitProteanBananaNW!({ strat: output.west!() }) // Inner WNW
                    : output.baitProtean!(),
                });
            }
            return output.heavySlamTetherDir!({
              dir: output[dir]!(),
              mech1: strat === 'dn'
                ? output.baitProteanDN!({ strat: output.north!() })
                : strat === 'banana'
                ? output.baitProteanBanana!({ strat: output.west!() })
                : strat === 'nukemaru'
                ? output.baitProteanBanana!({ strat: output.east!() })
                : output.baitProtean!(),
            });
        }
      },
      outputStrings: {
        ...Directions.outputStrings16Dir,
        north: Outputs.north,
        east: Outputs.east,
        south: Outputs.south,
        west: Outputs.west,
        defamationOnYou: Outputs.defamationOnYou,
        defamationOnYouDNSE: {
          en: 'Defamation on YOU, Go ${strat}',
          de: 'Ehrenstrafe auf DIR, Geh ${strat}',
          cn: '大圈点名, 去${strat}',
          ko: '광역징 대상자, ${strat}',
        },
        defamationOnYouDNSW: {
          en: 'Defamation on YOU, Go ${strat}',
          de: 'Ehrenstrafe auf DIR, Geh ${strat}',
          cn: '大圈点名, 去${strat}',
          ko: '광역징 대상자, ${strat}',
        },
        defamationOnYouBananaNE: {
          en: 'Defamation on YOU, Go ${strat}',
          de: 'Ehrenstrafe auf DIR, Geh ${strat}',
          cn: '大圈点名, 去${strat}',
          ko: '광역징 대상자, ${strat}',
        },
        defamationOnYouBananaSE: {
          en: 'Defamation on YOU, Go ${strat}',
          de: 'Ehrenstrafe auf DIR, Geh ${strat}',
          cn: '大圈点名, 去${strat}',
          ko: '광역징 대상자, ${strat}',
        },
        defamationOnYouNukemaruSW: {
          en: 'Defamation on YOU, Go ${strat}',
          de: 'Ehrenstrafe auf DIR, Geh ${strat}',
          cn: '大圈点名, 去${strat}',
          ko: '광역징 대상자, ${strat}',
        },
        defamationOnYouNukemaruNW: {
          en: 'Defamation on YOU, Go ${strat}',
          de: 'Ehrenstrafe auf DIR, Geh ${strat}',
          cn: '大圈点名, 去${strat}',
          ko: '광역징 대상자, ${strat}',
        },
        baitProtean: {
          en: 'Bait Protean from Boss',
          de: 'Köder Kegel-AoE vom Boss',
          cn: '从 Boss 诱导扇形',
          ko: '보스의 부채꼴 유도',
        },
        baitProteanDN: { // If clone tether num missing
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanDNNE: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanDNE: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanDNW: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanDNNW: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanBanana: { // If clone tether num missing
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanBananaN: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanBananaS: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanBananaSW: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanBananaNW: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanNukemaru: { // If clone tether num missing
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanNukemaruN: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanNukemaruS: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanNukemaruNE: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitProteanNukemaruSE: {
          en: 'Bait Protean from Boss (${strat})',
          de: 'Köder Kegel-AoE vom Boss (${strat})',
          cn: '从 Boss 诱导扇形 (${strat})',
          ko: '보스의 부채꼴 유도 (${strat})',
        },
        baitJump: {
          en: 'Bait Jump',
          de: 'Köder Sprung',
          cn: '诱导跳跃',
          ko: '점프 유도',
        },
        baitJumpDNN: {
          en: 'Bait Jump ${strat}',
          de: 'Köder Sprung ${strat}',
          cn: '诱导跳跃 ${strat}',
          ko: '점프 유도 ${strat}',
        },
        baitJumpBananaW: {
          en: 'Bait Jump ${strat}',
          de: 'Köder Sprung ${strat}',
          cn: '诱导跳跃 ${strat}',
          ko: '점프 유도 ${strat}',
        },
        baitJumpNukemaruE: {
          en: 'Bait Jump ${strat}',
          de: 'Köder Sprung ${strat}',
          cn: '诱导跳跃 ${strat}',
          ko: '점프 유도 ${strat}',
        },
        projectionTetherDir: {
          en: '${dir} Cone Tether: ${mech1}',
          de: '${dir} Kegel-Verbindung: ${mech1}',
          cn: '${dir} 扇形连线: ${mech1}',
          ko: '${dir} 부채꼴 선: ${mech1}',
        },
        projectionTether: {
          en: 'Cone Tether: ${mech1}',
          de: 'Kegel-Verbindung: ${mech1}',
          cn: '扇形连线: ${mech1}',
          ko: '부채꼴 선: ${mech1}',
        },
        manaBurstTetherDir: {
          en: '${dir} Defamation Tether: ${mech1}',
          de: '${dir} Ehrenstrafe-Verbindung: ${mech1}',
          cn: '${dir} 大圈连线: ${mech1}',
          ko: '${dir} 광역징 선: ${mech1}',
        },
        manaBurstTether: {
          en: 'Defamation Tether: ${mech1}',
          de: 'Ehrenstrafe-Verbindung: ${mech1}',
          cn: '大圈连线: ${mech1}',
          ko: '광역징 선: ${mech1}',
        },
        heavySlamTetherDir: {
          en: '${dir} Stack Tether: ${mech1}',
          de: '${dir} Sammel-Verbindung: ${mech1}',
          cn: '${dir} 分摊连线: ${mech1}',
          ko: '${dir} 쉐어징 선: ${mech1}',
        },
        heavySlamTether: {
          en: 'Stack Tether: ${mech1}',
          de: 'Sammel-Verbindung: ${mech1}',
          cn: '分摊连线: ${mech1}',
          ko: '쉐어징 선: ${mech1}',
        },
        fireballSplashTether: {
          en: 'Boss Tether: ${mech1}',
          de: 'Boss-Verbindung: ${mech1}',
          cn: 'Boss 连线: ${mech1}',
          ko: '보스 선: ${mech1}',
        },
      },
    },
    {
      id: 'R12S Replication 2 Mana Burst Far Target',
      // A player without a tether will be target for defamation
      type: 'Tether',
      netRegex: { id: headMarkerData['lockedTether'], capture: false },
      condition: (data) => {
        if (data.phase === 'replication2' && data.replicationCounter === 2)
          return true;
        return false;
      },
      delaySeconds: 0.2,
      suppressSeconds: 1,
      infoText: (data, _matches, output) => {
        const ability = data.replication2PlayerAbilities[data.me];
        const strat = data.triggerSetConfig.replication2Strategy;
        if (ability !== 'none' || ability === undefined)
          return;
        return output.noTether!({
          mech1: strat === 'dn'
            ? output.baitFarDefamationDN!({ strat: output.south!() })
            : strat === 'banana'
            ? output.baitFarDefamationBanana!({ strat: output.east!() })
            : strat === 'nukemaru'
            ? output.baitFarDefamationNukemaru!({ strat: output.west!() })
            : output.baitFarDefamation!(),
          mech2: output.stackGroups!(),
        });
      },
      outputStrings: {
        east: Outputs.east,
        south: Outputs.south,
        west: Outputs.west,
        baitFarDefamation: {
          en: 'Bait Far Defamation',
          de: 'Köder Entfernte Ehrenstrafe',
          cn: '诱导远大圈',
          ko: '멀리 광역징 유도',
        },
        baitFarDefamationDN: {
          en: 'Bait Far Defamation (Go ${strat})',
          de: 'Köder Entfernte Ehrenstrafe (Geh ${strat})',
          cn: '诱导远大圈 (去 ${strat})',
          ko: '멀리 광역징 유도 (${strat})',
        },
        baitFarDefamationBanana: {
          en: 'Bait Far Defamation (Go ${strat})',
          de: 'Köder Entfernte Ehrenstrafe (Geh ${strat})',
          cn: '诱导远大圈 (去 ${strat})',
          ko: '멀리 광역징 유도 (${strat})',
        },
        baitFarDefamationNukemaru: {
          en: 'Bait Far Defamation (Go ${strat})',
          de: 'Köder Entfernte Ehrenstrafe (Geh ${strat})',
          cn: '诱导远大圈 (去 ${strat})',
          ko: '멀리 광역징 유도 (${strat})',
        },
        stackGroups: {
          en: 'Stack Groups',
          de: 'Gruppen-Sammeln',
          fr: 'Package en groupes',
          ja: '組み分け頭割り',
          cn: '分组分摊',
          ko: '그룹별 쉐어',
          tc: '分組分攤',
        },
        noTether: {
          en: 'No Tether: ${mech1} => ${mech2}',
          de: 'Keine Verbindung: ${mech1} => ${mech2}',
          cn: '无连线: ${mech1} => ${mech2}',
          ko: '선 없음: ${mech1} => ${mech2}',
        },
      },
    },
    {
      id: 'R12S Heavy Slam',
      // After B4E7 Mana Burst, Groups must stack up on the heavy slam targetted players
      type: 'Ability',
      netRegex: { id: 'B4E7', source: 'Lindwurm', capture: false },
      suppressSeconds: 1,
      alertText: (data, _matches, output) => {
        const ability = data.replication2PlayerAbilities[data.me];
        switch (ability) {
          case headMarkerData['projectionTether']:
            return output.projectionTether!({
              mech1: output.stackGroups!(),
              mech2: output.lookAway!(),
              mech3: output.getBehind!(),
            });
          case headMarkerData['manaBurstTether']:
            return output.manaBurstTether!({
              mech1: output.stackGroups!(),
              mech2: output.projection!(),
              mech3: output.getBehind!(),
            });
          case headMarkerData['heavySlamTether']:
            return output.heavySlamTether!({
              mech1: output.stackGroups!(),
              mech2: output.projection!(),
              mech3: output.getBehind!(),
            });
          case headMarkerData['fireballSplashTether']:
            return output.fireballSplashTether!({
              mech1: output.stackGroups!(),
              mech2: output.projection!(),
              mech3: output.getBehind!(),
            });
        }
        return output.noTether!({
          mech1: output.stackGroups!(),
          mech2: output.projection!(),
          mech3: output.getBehind!(),
        });
      },
      outputStrings: {
        getBehind: Outputs.getBehind,
        lookAway: Outputs.lookAway,
        projection: {
          en: 'Cones',
          de: 'Klone',
          cn: '扇形',
          ko: '부채꼴',
        },
        stackGroups: {
          en: 'Stack Groups',
          de: 'Gruppen-Sammeln',
          fr: 'Package en groupes',
          ja: '組み分け頭割り',
          cn: '分组分摊',
          ko: '그룹별 쉐어',
          tc: '分組分攤',
        },
        stackOnYou: Outputs.stackOnYou,
        projectionTether: {
          en: '${mech1} + ${mech2} => ${mech3}',
          de: '${mech1} + ${mech2} => ${mech3}',
          cn: '${mech1} + ${mech2} => ${mech3}',
          ko: '${mech1} + ${mech2} => ${mech3}',
        },
        manaBurstTether: {
          en: '${mech1} => ${mech2} => ${mech3}',
          de: '${mech1} => ${mech2} => ${mech3}',
          cn: '${mech1} => ${mech2} => ${mech3}',
          ko: '${mech1} => ${mech2} => ${mech3}',
        },
        heavySlamTether: {
          en: '${mech1} => ${mech2} => ${mech3}',
          de: '${mech1} => ${mech2} => ${mech3}',
          cn: '${mech1} => ${mech2} => ${mech3}',
          ko: '${mech1} => ${mech2} => ${mech3}',
        },
        fireballSplashTether: {
          en: '${mech1} => ${mech2} => ${mech3}',
          de: '${mech1} => ${mech2} => ${mech3}',
          cn: '${mech1} => ${mech2} => ${mech3}',
          ko: '${mech1} => ${mech2} => ${mech3}',
        },
        noTether: {
          en: '${mech1} => ${mech2} => ${mech3}',
          de: '${mech1} => ${mech2} => ${mech3}',
          cn: '${mech1} => ${mech2} => ${mech3}',
          ko: '${mech1} => ${mech2} => ${mech3}',
        },
      },
    },
    {
      id: 'R12S Grotesquerie',
      // This seems to be the point at which the look for the Snaking Kick is snapshot
      // The VFX B4E9 happens ~0.6s before Snaking Kick
      // B4EA has the targetted player in it
      // B4EB Hemorrhagic Projection conal aoe goes off ~0.5s after in the direction the player was facing
      type: 'Ability',
      netRegex: { id: 'B4EA', source: 'Lindwurm', capture: false },
      suppressSeconds: 9999,
      alertText: (data, _matches, output) => {
        // Get Boss facing
        const bossId = data.replication2BossId;
        if (bossId === undefined)
          return output.getBehind!();

        const actor = data.actorPositions[bossId];
        if (actor === undefined)
          return output.getBehind!();

        const dirNum = (Directions.hdgTo16DirNum(actor.heading) + 8) % 16;
        const dir = Directions.output16Dir[dirNum] ?? 'unknown';
        return output.getBehindDir!({
          dir: output[dir]!(),
          mech: output.getBehind!(),
        });
      },
      outputStrings: {
        ...Directions.outputStrings16Dir,
        getBehind: Outputs.getBehind,
        getBehindDir: {
          en: '${dir}: ${mech}',
          de: '${dir}: ${mech}',
          cn: '${dir}: ${mech}',
          ko: '${dir}: ${mech}',
        },
      },
    },
    {
      id: 'R12S Netherwrath Near/Far and First Clones',
      // In DN, Boss jumps onto clone of player that took Firefall Splash, there is an aoe around the clone + proteans
      // In Banana Codex and Nukemaru, N/S Projections happen at this time
      type: 'StartsUsing',
      netRegex: { id: ['B52E', 'B52F'], source: 'Lindwurm', capture: true },
      infoText: (data, matches, output) => {
        const strat = data.replication2StrategyDetected;
        const ability = data.replication2PlayerAbilities[data.me];
        const isNear = matches.id === 'B52E';

        // DN Strategy
        if (strat === 'dn') {
          if (isNear) {
            switch (ability) {
              case headMarkerData['projectionTether']:
                return output.projectionTetherNear!({
                  proteanBaits: output.beFar!(),
                  mech1: output.scaldingWave!(),
                  mech2: output.stacks!(),
                  spiteBaits: output.near!(),
                });
              case headMarkerData['manaBurstTether']:
                return output.manaBurstTetherNear!({
                  spiteBaits: output.beNear!(),
                  mech1: output.timelessSpite!(),
                  mech2: output.proteans!(),
                  proteanBaits: output.far!(),
                });
              case headMarkerData['heavySlamTether']:
                return output.heavySlamTetherNear!({
                  proteanBaits: output.beFar!(),
                  mech1: output.scaldingWave!(),
                  mech2: output.stacks!(),
                  spiteBaits: output.near!(),
                });
              case headMarkerData['fireballSplashTether']:
                return output.fireballSplashTetherNear!({
                  spiteBaits: output.beNear!(),
                  mech1: output.timelessSpite!(),
                  mech2: output.proteans!(),
                  proteanBaits: output.far!(),
                });
            }
            return output.noTetherNear!({
              spiteBaits: output.beNear!(),
              mech1: output.timelessSpite!(),
              mech2: output.proteans!(),
              proteanBaits: output.far!(),
            });
          }

          // Netherwrath Far
          switch (ability) {
            case headMarkerData['projectionTether']:
              return output.projectionTetherFar!({
                proteanBaits: output.beNear!(),
                mech1: output.scaldingWave!(),
                mech2: output.stacks!(),
                spiteBaits: output.far!(),
              });
            case headMarkerData['manaBurstTether']:
              return output.manaBurstTetherFar!({
                spiteBaits: output.beFar!(),
                mech1: output.timelessSpite!(),
                mech2: output.proteans!(),
                proteanBaits: output.near!(),
              });
            case headMarkerData['heavySlamTether']:
              return output.heavySlamTetherFar!({
                proteanBaits: output.beNear!(),
                mech1: output.scaldingWave!(),
                mech2: output.stacks!(),
                spiteBaits: output.far!(),
              });
            case headMarkerData['fireballSplashTether']:
              return output.fireballSplashTetherFar!({
                spiteBaits: output.beFar!(),
                mech1: output.timelessSpite!(),
                mech2: output.proteans!(),
                proteanBaits: output.near!(),
              });
          }
          return output.noTetherFar!({
            spiteBaits: output.beFar!(),
            mech1: output.timelessSpite!(),
            mech2: output.proteans!(),
            proteanBaits: output.near!(),
          });
        }

        // Banana Codex and Nukemaru Strategies
        if (strat === 'banana' || strat === 'nukemaru') {
          // Technically, these strategies do not care about Near/Far, but
          // included as informational
          switch (ability) {
            case headMarkerData['projectionTether']:
              return output.projectionTetherBait!({
                mech1: output.timelessSpite!(),
                spiteBaits: isNear ? output.near!() : output.far!(),
                mech2: output.proteans!(),
              });
            case headMarkerData['manaBurstTether']:
              return output.manaBurstTetherHitbox!({
                mech1: strat === 'banana'
                  ? output.hitboxBanana!()
                  : output.hitboxNukemaru!(),
                spiteBaits: isNear ? output.near!() : output.far!(),
                mech2: output.stackDir!({
                  dir: strat === 'banana'
                    ? output.dirSW!()
                    : output.dirNE!(),
                }),
              });
            case headMarkerData['heavySlamTether']:
              return output.heavySlamTetherBait!({
                mech1: output.timelessSpite!(),
                spiteBaits: isNear ? output.near!() : output.far!(),
                mech2: output.proteans!(),
              });
            case headMarkerData['fireballSplashTether']:
              return output.fireballSplashTetherHitbox!({
                mech1: strat === 'banana'
                  ? output.hitboxBanana!()
                  : output.hitboxNukemaru!(),
                spiteBaits: isNear ? output.near!() : output.far!(),
                mech2: output.stackDir!({
                  dir: strat === 'banana'
                    ? output.dirSW!()
                    : output.dirNE!(),
                }),
              });
          }
          return output.noTetherHitbox!({
            mech1: strat === 'banana'
              ? output.hitboxBanana!()
              : output.hitboxNukemaru!(),
            spiteBaits: isNear ? output.near!() : output.far!(),
            mech2: output.stackDir!({
              dir: strat === 'banana'
                ? output.dirSW!()
                : output.dirNE!(),
            }),
          });
        }

        // No built-in strategy / unsupported order, call generic far/near and
        // what's happening next
        const getMechanic = (
          order: string,
        ): 'proteans' | 'defamation' | 'projection' | 'stack' | 'unknown' => {
          const boss = headMarkerData['fireballSplashTether'];
          const defamation = headMarkerData['manaBurstTether'];
          const stack = headMarkerData['heavySlamTether'];
          const projection = headMarkerData['projectionTether'];
          if (order === boss)
            return 'proteans';
          if (order === defamation || order === 'none')
            return 'defamation';
          if (order === projection)
            return 'projection';
          if (order === stack)
            return 'stack';
          return 'unknown';
        };
        const order = data.replication2AbilityOrder;
        const mechanic1 = getMechanic(order[0] ?? 'unknown');
        const mechanic2 = getMechanic(order[1] ?? 'unknown');
        const mechanic3 = getMechanic(order[2] ?? 'unknown');
        const mechanic4 = getMechanic(order[3] ?? 'unknown');
        return output.netherwrathMechThenMech!({
          spiteBaits: isNear ? output.near!() : output.far!(),
          mech1: output[mechanic1]!(),
          mech2: output[mechanic2]!(),
          mech3: output[mechanic3]!(),
          mech4: output[mechanic4]!(),
        });
      },
      outputStrings: {
        dirNE: Outputs.dirNE,
        dirSW: Outputs.dirSW,
        scaldingWave: Outputs.protean,
        timelessSpite: Outputs.stackPartner,
        stacks: Outputs.stacks,
        stackDir: {
          en: 'Stack ${dir}',
          de: 'Sammeln ${dir}',
          cn: '${dir} 分摊',
          ko: '쉐어 ${dir}',
        },
        proteans: {
          en: 'Proteans',
          de: 'Kegel-AoEs',
          cn: '扇形',
          ko: '부채꼴',
        },
        beNear: {
          en: 'Be Near',
          de: 'Sei Nahe',
          cn: '站近',
          ko: '가까이 있기',
        },
        beFar: {
          en: 'Be Far',
          de: 'Sei Fern',
          cn: '站远',
          ko: '멀리 있기',
        },
        hitboxBanana: {
          en: 'Be West on Boss Hitbox',
          de: 'Sei westlich der Hitbox vom Boss',
          cn: '去左边, Boss判定圈上',
          ko: '보스 히트박스 서쪽에 있기',
        },
        hitboxNukemaru: {
          en: 'Be West on Boss Hitbox',
          de: 'Sei westlich auf der Hitbox vom Boss',
          cn: '去左边, Boss判定圈下',
          ko: '보스 히트박스 서쪽에 있기',
        },
        near: {
          en: 'Near',
          de: 'Nah',
          fr: 'Proche',
          cn: '近',
          ko: '가까이',
        },
        far: {
          en: 'Far',
          de: 'Fern',
          fr: 'Loin',
          cn: '远',
          ko: '멀리',
        },
        projectionTetherFar: {
          en: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          de: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          cn: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          ko: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
        },
        manaBurstTetherFar: {
          en: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          de: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          cn: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          ko: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
        },
        heavySlamTetherFar: {
          en: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          de: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          cn: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          ko: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
        },
        fireballSplashTetherFar: {
          en: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          de: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          cn: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          ko: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
        },
        noTetherFar: {
          en: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          de: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          cn: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          ko: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
        },
        projectionTetherNear: {
          en: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          de: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          cn: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          ko: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
        },
        manaBurstTetherNear: {
          en: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          de: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          cn: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          ko: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
        },
        heavySlamTetherNear: {
          en: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          de: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          cn: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
          ko: '${proteanBaits} + ${mech1} (${mech2} ${spiteBaits})',
        },
        fireballSplashTetherNear: {
          en: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          de: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          cn: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          ko: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
        },
        noTetherNear: {
          en: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          de: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          cn: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
          ko: '${spiteBaits} + ${mech1} (${mech2} ${proteanBaits})',
        },
        projectionTetherBait: {
          en: '${mech1} (${spiteBaits} Baits) => ${mech2}',
          de: '${mech1} (${spiteBaits} Ködern) => ${mech2}',
          cn: '${mech1} (${spiteBaits} Baits) => ${mech2}',
          ko: '${mech1} (${spiteBaits} 유도) => ${mech2}',
        },
        manaBurstTetherHitbox: {
          en: '${mech1} + Avoid ${spiteBaits} Baits => ${mech2}',
          de: '${mech1} + Vermeide ${spiteBaits} Ködern => ${mech2}',
          cn: '${mech1} + 躲避 ${spiteBaits} 诱导 => ${mech2}',
          ko: '${mech1} + ${spiteBaits} 유도 피하기 => ${mech2}',
        },
        heavySlamTetherBait: {
          en: '${mech1} (${spiteBaits} Baits) => ${mech2}',
          de: '${mech1} (${spiteBaits} Ködern) => ${mech2}',
          cn: '${mech1} (${spiteBaits} 诱导) => ${mech2}',
          ko: '${mech1} (${spiteBaits} 유도) => ${mech2}',
        },
        fireballSplashTetherHitbox: {
          en: '${mech1} + Avoid ${spiteBaits} Baits => ${mech2}',
          de: '${mech1} + Vermeide ${spiteBaits} Ködern => ${mech2}',
          cn: '${mech1} + 躲避 ${spiteBaits} 诱导 => ${mech2}',
          ko: '${mech1} + ${spiteBaits} 유도 피하기 => ${mech2}',
        },
        noTetherHitbox: {
          en: '${mech1} + Avoid ${spiteBaits} Baits => ${mech2}',
          de: '${mech1} + Vermeide ${spiteBaits} Ködern => ${mech2}',
          cn: '${mech1} + 躲避 ${spiteBaits} 诱导 => ${mech2}',
          ko: '${mech1} + ${spiteBaits} 유도 피하기 => ${mech2}',
        },
        stack: Outputs.stackMarker,
        projection: {
          en: 'Cones',
          de: 'Klone',
          cn: '扇形',
          ko: '부채꼴',
        },
        defamation: {
          en: 'Defamation',
          de: 'Ehrenstrafe',
          cn: '大圈',
          ko: '광역징',
        },
        unknown: Outputs.unknown,
        netherwrathMechThenMech: {
          en: '${spiteBaits} Baits + ${mech1} N + ${mech2} S => ${mech3} NE + ${mech4} SW',
          de: '${spiteBaits} Ködern + ${mech1} N + ${mech2} S => ${mech3} NO + ${mech4} SW',
          cn: '${spiteBaits} 诱导 + ${mech1} 上 + ${mech2} 下 => ${mech3} 右上 + ${mech4} 左下',
          ko: '${spiteBaits} 유도 + ${mech1} 북 + ${mech2} 남 => ${mech3} 북동 + ${mech4} 남서',
        },
      },
    },
    {
      id: 'R12S Reenactment 1 Scalding Waves Collect (DN)',
      // NOTE: This is used in DN Strategy
      // Players need to wait for BBE3 Mana Burst Defamations on the clones to complete before next mechanic
      // There are multiple BBE3s, setting flag to trigger after
      // B8E1 Scalding Waves
      type: 'Ability',
      netRegex: { id: 'B8E1', source: 'Lindwurm', capture: false },
      condition: (data) => data.phase === 'reenactment1',
      suppressSeconds: 9999,
      run: (data) => data.netherwrathFollowup = true,
    },
    {
      id: 'R12S Reenactment 1 Clone Stack SW (Second Clones Banana/Nukemaru)',
      // NOTE: This is used in Banana Codex Strategy and Nukemaru
      // SW (Banana)/NE (Nukemaru) Clone Stack happens after N/S Clone Projections
      // Defamation Tether Players, Boss Tether Player, and No Tether Player take stack
      // Using B922 Hemorrhagic Projection from clones
      type: 'Ability',
      netRegex: { id: 'B922', source: 'Lindwurm', capture: false },
      condition: (data) => {
        // Banana Codex and Nukemaru Strategy Order
        if (
          data.replication2StrategyDetected === 'banana' ||
          data.replication2StrategyDetected === 'nukemaru'
        )
          return true;
        return false;
      },
      suppressSeconds: 9999, // Projection happens twice here
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          stackThenStackBanana: {
            en: 'Stack on SW Clone => Stack on NW Clone',
            de: 'Auf SW Klon sammeln => Auf NW Klon sammeln',
            cn: '左下分身分摊 => 左上分身分摊',
            ko: '남서 분신에서 쉐어 => 북서 분신에서 쉐어',
          },
          avoidStackThenProteanBanana: {
            en: 'Avoid SW Stack => Bait Protean West',
            de: 'Vermeide SW sammeln => Köder Kegel-AoE West',
            cn: '避开左下分摊 => 左侧诱导扇形',
            ko: '남서 분신 쉐어 피하기 => 서쪽에서 부채꼴 유도',
          },
          stackThenProteansBanana: {
            en: 'SW Clone Stack => West Proteans',
            de: 'SW Klon sammeln => West Kegel-Aoe',
            cn: '左下分身分摊 => 左侧扇形',
            ko: '남서 분신 쉐어 => 서쪽 부채꼴',
          },
          stackThenStackNukemaru: {
            en: 'Stack on NE Clone => Stack on SE Clone',
            de: 'Auf NO Klon sammeln => Auf SO Klon sammeln',
            cn: '右上分身分摊 => 右下分身分摊',
            ko: '북동 분신에서 쉐어 => 남동 분신에서 쉐어',
          },
          avoidStackThenProteanNukemaru: {
            en: 'Avoid NE Stack => Bait Protean East',
            de: 'Vermeide NO sammeln => Köder Kegel-AoE Ost',
            cn: '避开右上分摊 => 右侧诱导扇形',
            ko: '북동 분신 쉐어 피하기 => 동쪽에서 부채꼴 유도',
          },
          stackThenProteansNukemaru: {
            en: 'NE Clone Stack => East Proteans',
            de: 'NO Klon sammeln => Ost Kegel-Aoe',
            cn: '右上分身分摊 => 右侧扇形',
            ko: '북동 분신 쉐어 => 동쪽 부채꼴',
          },
        };

        const strat = data.replication2StrategyDetected;
        const ability = data.replication2PlayerAbilities[data.me];
        switch (ability) {
          case headMarkerData['projectionTether']:
          case headMarkerData['heavySlamTether']:
            return {
              infoText: strat === 'banana'
                ? output.avoidStackThenProteanBanana!()
                : output.avoidStackThenProteanNukemaru!(),
            };
          case headMarkerData['manaBurstTether']:
          case headMarkerData['fireballSplashTether']:
          case 'none':
            return {
              alertText: strat === 'banana'
                ? output.stackThenStackBanana!()
                : output.stackThenStackNukemaru!(),
            };
        }

        // Missing ability data, output mechanic order
        return {
          infoText: strat === 'banana'
            ? output.stackThenProteansBanana!()
            : output.stackThenProteansNukemaru!(),
        };
      },
    },
    {
      id: 'R12S Reenactment 1 Clone Stacks E/W (Third Clones DN)',
      // NOTE: This is used with DN Strategy
      // Players need to wait for BBE3 Mana Burst defamations on clones to complete
      // This happens three times during reenactment and the third one (which is after the proteans) is the trigger
      type: 'Ability',
      netRegex: { id: 'BBE3', source: 'Lindwurm', capture: false },
      condition: (data) => {
        if (data.netherwrathFollowup) {
          const order = data.replication2AbilityOrder;
          const stack = headMarkerData['heavySlamTether'];
          const defamation = headMarkerData['manaBurstTether'];
          const projection = headMarkerData['projectionTether'];
          if (
            order[rep2DirIndexMap['east']] === stack &&
            order[rep2DirIndexMap['west']] === stack &&
            order[rep2DirIndexMap['northeast']] === projection &&
            order[rep2DirIndexMap['southwest']] === defamation
          )
            return true;
        }
        return false;
      },
      suppressSeconds: 9999,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'East/West Clone Stacks',
          de: 'Ost/West Klon sammeln',
          cn: '左/右分身分摊',
          ko: '동/서 분신 쉐어',
        },
      },
    },
    {
      id: 'R12S Reenactment 1 Proteans West (Third Clones Banana/Nukemaru)',
      // NOTE: This is used in Banana Codex Strategy and Nukemaru
      // Stack Players need to go to the other stack
      // Non-stack players need to bait proteans
      // Using BE5D Heavy Slam from clones
      type: 'Ability',
      netRegex: { id: 'BE5D', source: 'Lindwurm', capture: false },
      condition: (data) => {
        // Banana Codex and Nukemaru Strategy Order
        if (
          data.replication2StrategyDetected === 'banana' ||
          data.replication2StrategyDetected === 'nukemaru'
        )
          return true;
        return false;
      },
      suppressSeconds: 9999,
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          proteanBanana: {
            en: 'Bait Protean West + Avoid Clone AoE',
            de: 'Kegel AoE ködern West + Vermeide Klon AoE',
            cn: '左侧诱导扇形 + 避开分身AoE',
            ko: '서쪽 부채꼴 유도 + 분신 장판 피하기',
          },
          avoidThenStackBanana: {
            en: 'Avoid West Clone/East Defamation + Stack on NW Clone',
            de: 'Vermeide West Klon/Ost Ehrenstrafe + Sammeln auf NW Klon',
            cn: '避开左分身/右大圈 + 左上分身分摊',
            ko: '서쪽 분신/동쪽 광역징 피하기 + 북서 분신에서 쉐어',
          },
          proteansThenStackBanana: {
            en: 'West Proteans => NW Clone Stack',
            de: 'West Kegel AoEs => NW Klon sammeln',
            cn: '左侧扇形 => 左上分身分摊',
            ko: '서쪽 부채꼴 => 북서 분신 쉐어',
          },
          proteanNukemaru: {
            en: 'Bait Protean East + Avoid Clone AoE',
            de: 'Kegel AoE ködern Ost + Vermeide Klon AoE',
            cn: '右侧诱导扇形 + 避开分身AoE',
            ko: '동쪽 부채꼴 유도 + 분신 장판 피하기',
          },
          avoidThenStackNukemaru: {
            en: 'Avoid East Clone/West Defamation + Stack on SE Clone',
            de: 'Vermeide Ost Klon/West Ehrenstrafe + Sammeln auf SO Klon',
            cn: '避开右分身/左大圈 + 右上分身分摊',
            ko: '동쪽 분신/서쪽 광역징 피하기 + 남동 분신에서 쉐어',
          },
          proteansThenStackNukemaru: {
            en: 'East Proteans => SE Clone Stack',
            de: 'Ost Kegel AoEs => SO Klon sammeln',
            cn: '右侧扇形 => 右上分身分摊',
            ko: '동쪽 부채꼴 => 남동 분신 쉐어',
          },
        };

        const strat = data.replication2StrategyDetected;
        const ability = data.replication2PlayerAbilities[data.me];
        switch (ability) {
          case headMarkerData['projectionTether']:
          case headMarkerData['heavySlamTether']:
            return {
              alertText: strat === 'banana'
                ? output.proteanBanana!()
                : output.proteanNukemaru!(),
            };
          case headMarkerData['manaBurstTether']:
          case headMarkerData['fireballSplashTether']:
          case 'none':
            return {
              infoText: strat === 'banana'
                ? output.avoidThenStackBanana!()
                : output.avoidThenStackNukemaru!(),
            };
        }

        // Missing ability data, output mechanic order
        return {
          infoText: strat === 'banana'
            ? output.proteansThenStackBanana!()
            : output.proteansThenStackNukemaru!(),
        };
      },
    },
    {
      id: 'R12S Reenactment 1 Defamation SE Dodge Reminder (Fourth Clones DN)',
      // NOTE: This is used with DN Strategy
      // Players need to run back to north after clone stacks (BE5D Heavy Slam)
      // The clone stacks become a defamation and the other a cleave going East or West through the room
      type: 'Ability',
      netRegex: { id: 'BE5D', source: 'Lindwurm', capture: false },
      condition: (data) => {
        if (data.netherwrathFollowup) {
          const order = data.replication2AbilityOrder;
          const stack = headMarkerData['heavySlamTether'];
          const defamation = headMarkerData['manaBurstTether'];
          const projection = headMarkerData['projectionTether'];
          if (
            order[rep2DirIndexMap['east']] === stack &&
            order[rep2DirIndexMap['west']] === stack &&
            order[rep2DirIndexMap['southeast']] === defamation &&
            order[rep2DirIndexMap['northwest']] === projection
          )
            return true;
        }
        return false;
      },
      suppressSeconds: 9999,
      alertText: (_data, _matches, output) => output.north!(),
      outputStrings: {
        north: Outputs.north,
      },
    },
    {
      id: 'R12S Reenactment 1 Clone Stack NW Reminder (Fourth Clones Banana/Nukemaru)',
      // NOTE: This is used in Banana Codex Strategy and Nukemaru
      // Reminder for players to Stack
      // Reminder for Non-stack players to avoid
      // Using B8E1 Scalding Waves from clones
      type: 'Ability',
      netRegex: { id: 'B8E1', source: 'Lindwurm', capture: false },
      condition: (data) => {
        // Banana Codex and Nukemaru Strategy Order
        if (
          data.replication2StrategyDetected === 'banana' ||
          data.replication2StrategyDetected === 'nukemaru'
        )
          return true;
        return false;
      },
      suppressSeconds: 9999,
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          stackBanana: {
            en: 'Stack on NW Clone',
            de: 'Auf NW Klon sammeln',
            cn: '左上分身分摊',
            ko: '북서 분신에서 쉐어',
          },
          avoidStackBanana: {
            en: 'Avoid NE Stack',
            de: 'Vermeide NO sammeln',
            cn: '避开右上分摊',
            ko: '북동 쉐어 피하기',
          },
          stackAndDefamationBanana: {
            en: 'NW Clone Stack + SE Defamation',
            de: 'NW Klon sammeln + SO Ehrenstrafe',
            cn: '左上分身分摊 + 右下大圈',
            ko: '북서 분신 쉐어 + 남동 광역징',
          },
          stackNukemaru: {
            en: 'Stack on SE Clone',
            de: 'Auf SO Klon sammeln',
            cn: '右下分身分摊',
            ko: '남동 분신에서 쉐어',
          },
          avoidStackNukemaru: {
            en: 'Avoid SE Stack',
            de: 'Vermeide SO sammeln',
            cn: '避开右下分摊',
            ko: '남동 쉐어 피하기',
          },
          stackAndDefamationNukemaru: {
            en: 'SE Clone Stack + NW Defamation',
            de: 'SO Klon sammeln + NW Ehrenstrafe',
            cn: '右下分身分摊 + 左上大圈',
            ko: '남동 분신 쉐어 + 북서 광역징',
          },
        };

        const strat = data.replication2StrategyDetected;
        const ability = data.replication2PlayerAbilities[data.me];
        switch (ability) {
          case headMarkerData['projectionTether']:
          case headMarkerData['heavySlamTether']:
            return {
              infoText: strat === 'banana'
                ? output.avoidStackBanana!()
                : output.avoidStackNukemaru!(),
            };
          case headMarkerData['manaBurstTether']:
          case headMarkerData['fireballSplashTether']:
          case 'none':
            return {
              alertText: strat === 'banana'
                ? output.stackBanana!()
                : output.stackNukemaru!(),
            };
        }

        // Missing ability data, output mechanic order
        return {
          infoText: strat === 'banana'
            ? output.stackAndDefamationBanana!()
            : output.stackAndDefamationNukemaru!(),
        };
      },
    },
    {
      id: 'R12S Mana Sphere Collect and Label',
      // Combatants Spawn ~3s before B505 Mutating Cells startsUsing
      // Their positions are available at B4FD in the 264 AbilityExtra lines and updated periodically after with 270 lines
      // 19208 => Lightning Bowtie (N/S Cleave)
      // 19209 => Fire Bowtie (E/W Cleave)
      // 19205 => Black Hole
      // 19206 => Water Sphere/Chariot
      // 19207 => Wind Donut
      // Position at add is center, so not useful here yet
      type: 'AddedCombatant',
      netRegex: { name: 'Mana Sphere', capture: true },
      run: (data, matches) => {
        const id = matches.id;
        const npcBaseId = parseInt(matches.npcBaseId);
        switch (npcBaseId) {
          case 19205:
            data.manaSpheres[id] = 'blackHole';
            return;
          case 19206:
            data.manaSpheres[id] = 'water';
            return;
          case 19207:
            data.manaSpheres[id] = 'wind';
            return;
          case 19208:
            data.manaSpheres[id] = 'lightning';
            return;
          case 19209:
            data.manaSpheres[id] = 'fire';
            return;
        }
      },
    },
    {
      id: 'R12S Mutation α/β Collect',
      // Used in Blood Mana / Blood Awakening Mechanics
      // 12A1 Mutation α: Don't get hit
      // 12A3 Mutation β: Get Hit
      // Players will get opposite debuff after Blood Mana
      type: 'GainsEffect',
      netRegex: { effectId: ['12A1', '12A3'], capture: true },
      condition: Conditions.targetIsYou(),
      run: (data, matches) => {
        data.myMutation = matches.effectId === '12A1' ? 'alpha' : 'beta';
      },
    },
    {
      id: 'R12S Mutation α/β',
      type: 'GainsEffect',
      netRegex: { effectId: ['12A1', '12A3'], capture: true },
      condition: Conditions.targetIsYou(),
      infoText: (_data, matches, output) => {
        if (matches.effectId === '12A1')
          return output.alpha!();
        return output.beta!();
      },
      tts: (_data, matches, output) => {
        if (matches.effectId === '12A1')
          return output.alphaTts!();
        return output.betaTts!();
      },
      outputStrings: {
        alpha: {
          en: 'Mutation α on YOU',
          de: 'Mutation α auf DIR',
          cn: '变异细胞α点名',
          ko: '변이세포 α 대상자',
        },
        beta: {
          en: 'Mutation β on YOU',
          de: 'Mutation β auf DIR',
          cn: '变异细胞β点名',
          ko: '변이세포 β 대상자',
        },
        alphaTts: {
          en: 'Mutation α on YOU',
          de: 'Mutation α auf DIR',
          cn: '变异细胞α点名',
          ko: '변이세포 알파 대상자',
        },
        betaTts: {
          en: 'Mutation β on YOU',
          de: 'Mutation β auf DIR',
          cn: '变异细胞β点名',
          ko: '변이세포 베타 대상자',
        },
      },
    },
    {
      id: 'R12S Mana Sphere Position Collect',
      // BCB0 Black Holes:
      // These are (90, 100) and (110, 100)
      // B4FD Shapes
      // Side that needs to be exploded will have pairs with 2 of the same x or y coords
      // Side to get the shapes to explode will be closest distance to black hole
      type: 'AbilityExtra',
      netRegex: { id: 'B4FD', capture: true },
      run: (data, matches) => {
        // Calculate Distance to Black Hole
        const getDistance = (
          x: number,
          y: number,
        ): number => {
          const blackHoleX = x < 100 ? 90 : 110;
          const dx = x - blackHoleX;
          const dy = y - 100;
          return Math.round(Math.sqrt(dx * dx + dy * dy));
        };
        const x = parseFloat(matches.x);
        const y = parseFloat(matches.y);
        const d = getDistance(x, y);
        const id = matches.sourceId;

        // Put into different objects for easier lookup
        if (x < 100) {
          data.westManaSpheres[id] = { x: x, y: y };
        }
        data.eastManaSpheres[id] = { x: x, y: y };

        // Shapes with 6 distance are close, Shapes with 12 are far
        if (d < 7) {
          data.closeManaSphereIds.push(id);

          // Have enough data to solve at this point
          if (data.closeManaSphereIds.length === 2) {
            const popSide = x < 100 ? 'east' : 'west';
            data.manaSpherePopSide = popSide;

            const sphereId1 = data.closeManaSphereIds[0];
            const sphereId2 = id;
            if (sphereId1 === undefined)
              return;

            const sphereType1 = data.manaSpheres[sphereId1];
            const sphereType2 = data.manaSpheres[sphereId2];
            if (sphereType1 === undefined || sphereType2 === undefined)
              return;

            // If you see Water, pop side first
            // If you see Wind, non-pop side
            // Can't be Lightning + Wind because Fire hits the donut
            // Fire + Lightning would hit whole room
            // Water + Wind would hit whole room
            const nonPopSide = popSide === 'east' ? 'west' : 'east';
            const first = [sphereType1, sphereType2];
            const dir2 = first.includes('water') ? popSide : nonPopSide;
            data.firstBlackHole = dir2;
          }
        }
      },
    },
    {
      id: 'R12S Black Hole and Shapes',
      // Black Holes and shapes
      type: 'Ability',
      netRegex: { id: 'B4FD', source: 'Mana Sphere', capture: false },
      delaySeconds: 0.2,
      durationSeconds: 8.3,
      suppressSeconds: 9999,
      infoText: (data, _matches, output) => {
        const popSide = data.manaSpherePopSide;
        const blackHole = data.firstBlackHole;
        const sphereId1 = data.closeManaSphereIds[0];
        const sphereId2 = data.closeManaSphereIds[1];
        if (
          popSide === undefined ||
          blackHole === undefined ||
          sphereId1 === undefined ||
          sphereId2 === undefined
        )
          return data.myMutation === 'alpha' ? output.alpha!() : output.beta!();

        const sphereType1 = data.manaSpheres[sphereId1];
        const sphereType2 = data.manaSpheres[sphereId2];
        if (sphereType1 === undefined || sphereType2 === undefined)
          return data.myMutation === 'alpha' ? output.alpha!() : output.beta!();

        if (data.myMutation === 'alpha')
          return output.alphaDir!({
            dir1: output[popSide]!(),
            shape1: output[sphereType1]!(),
            shape2: output[sphereType2]!(),
            northSouth: output.northSouth!(),
            dir2: output[blackHole]!(),
          });
        return output.betaDir!({
          dir1: output[popSide]!(),
          shape1: output[sphereType1]!(),
          shape2: output[sphereType2]!(),
          northSouth: output.northSouth!(),
          dir2: output[blackHole]!(),
        });
      },
      outputStrings: {
        east: Outputs.east,
        west: Outputs.west,
        northSouth: {
          en: 'N/S',
          de: 'N/S',
          fr: 'N/S',
          ja: '南/北',
          cn: '上/下',
          ko: '남/북',
          tc: '上/下',
        },
        water: {
          en: 'Orb',
          de: 'Orb',
          cn: '钢铁碎片',
          ko: '구슬',
        },
        lightning: {
          en: 'Lightning',
          de: 'Blitz',
          cn: '上下扇形碎片',
          ko: '번개',
        },
        fire: {
          en: 'Fire',
          de: 'Feuer',
          cn: '左右扇形碎片',
          ko: '불',
        },
        wind: {
          en: 'Donut',
          de: 'Donut',
          cn: '月环碎片',
          ko: '도넛',
        },
        alpha: {
          en: 'Avoid Shape AoEs, Wait by Black Hole',
          de: 'Vermeide Form-Aoe, Warte beim Schwarzen Loch',
          cn: '避开碎片 AOE, 等待黑洞',
          ko: '도형 장판 피하기, 블랙홀 옆에서 대기',
        },
        beta: {
          en: 'Shared Shape Soak => Get by Black Hole',
          de: 'Form-AoE zusammen nehmen => Geh zum Schwarzen Loch',
          cn: '分摊撞碎片 => 靠近黑洞',
          ko: '도형 쉐어 처리 => 블랙홀 옆으로 이동',
        },
        alphaDir: {
          en: 'Avoid ${dir1} ${shape1}/${shape2} => ${dir2} Black Hole + ${northSouth}',
          de:
            'Vermeide ${dir1} ${shape1}/${shape2} Form-AoEs => ${dir2} Schwarzes Loch + ${northSouth}',
          cn: '避开 ${dir1} ${shape1}/${shape2} => ${dir2} 黑洞 + ${northSouth}',
          ko: '${dir1} ${shape1}/${shape2} 도형 장판 피하기 => ${dir2} 블랙홀 + ${northSouth}',
        },
        betaDir: {
          en: 'Share ${dir1} ${shape1}/${shape2} => ${dir2} Black Hole + ${northSouth}',
          de: 'Teile ${dir1} ${shape1}/${shape2} => ${dir2} Schwarzes Loch + ${northSouth}',
          cn: '分摊 ${dir1} ${shape1}/${shape2} => ${dir2} 黑洞 + ${northSouth}',
          ko: '${dir1} ${shape1}/${shape2} 쉐어 => ${dir2} 블랙홀 + ${northSouth}',
        },
      },
    },
    {
      id: 'R12S Dramatic Lysis Black Hole 1',
      // This may not happen if all shapes are failed
      type: 'Ability',
      netRegex: { id: 'B507', source: 'Lindwurm', capture: false },
      durationSeconds: 15, // ~16s until ability
      suppressSeconds: 9999,
      alertText: (data, _matches, output) => {
        const blackHole = data.firstBlackHole;
        if (blackHole === undefined)
          return data.myMutation === 'alpha' ? output.alpha!() : output.beta!();
        return data.myMutation === 'alpha'
          ? output.alphaDir!({
            northSouth: output.northSouth!(),
            dir2: output[blackHole]!(),
          })
          : output.betaDir!({
            northSouth: output.northSouth!(),
            dir2: output[blackHole]!(),
          });
      },
      outputStrings: {
        east: Outputs.east,
        west: Outputs.west,
        northSouth: {
          en: 'N/S',
          de: 'N/S',
          fr: 'N/S',
          ja: '南/北',
          cn: '上/下',
          ko: '남/북',
          tc: '上/下',
        },
        alpha: {
          en: 'Get by Black Hole',
          de: 'Geh zum Schwarzen Loch',
          cn: '靠近黑洞',
          ko: '블랙홀 옆으로 이동',
        },
        beta: {
          en: 'Get by Black Hole',
          de: 'Geh zum Schwarzen Loch',
          cn: '靠近黑洞',
          ko: '블랙홀 옆으로 이동',
        },
        alphaDir: {
          en: '${dir2} Black Hole + ${northSouth}',
          de: '${dir2} Schwarzes Loch + ${northSouth}',
          cn: '${dir2} 黑洞 + ${northSouth}',
          ko: '${dir2} 블랙홀 + ${northSouth}',
        },
        betaDir: {
          en: '${dir2} Black Hole + ${northSouth}',
          de: '${dir2} Schwarzes Loch + ${northSouth}',
          cn: '${dir2} 黑洞 + ${northSouth}',
          ko: '${dir2} 블랙홀 + ${northSouth}',
        },
      },
    },
    {
      id: 'R12S Blood Wakening Followup',
      // Run to the other Black Hole after abilities go off
      // B501 Lindwurm's Water III
      // B502 Lindwurm's Aero III
      // B503 Straightforward Thunder II
      // B504 Sideways Fire II
      type: 'Ability',
      netRegex: { id: ['B501', 'B502', 'B503', 'B504'], source: 'Lindwurm', capture: false },
      suppressSeconds: 9999,
      alertText: (data, _matches, output) => {
        const blackHole = data.firstBlackHole;
        if (blackHole === undefined)
          return output.move!();
        const next = blackHole === 'east' ? 'west' : 'east';
        return output.moveDir!({
          northSouth: output.northSouth!(),
          dir: output[next]!(),
        });
      },
      outputStrings: {
        east: Outputs.east,
        west: Outputs.west,
        northSouth: {
          en: 'N/S',
          de: 'N/S',
          fr: 'N/S',
          ja: '南/北',
          cn: '上/下',
          ko: '남/북',
          tc: '上/下',
        },
        move: {
          en: 'Move to other Black Hole',
          de: 'Geh zum anderen Schwarzen Loch',
          cn: '去另一个黑洞',
          ko: '다른 블랙홀로 이동',
        },
        moveDir: {
          en: '${dir} Black Hole + ${northSouth}',
          de: '${dir} Schwarzen Loch + ${northSouth}',
          cn: '${dir} 黑洞 + ${northSouth}',
          ko: '${dir} 블랙홀 + ${northSouth}',
        },
      },
    },
    {
      id: 'R12S Netherworld Near/Far',
      type: 'StartsUsing',
      netRegex: { id: ['B52B', 'B52C'], source: 'Lindwurm', capture: true },
      alertText: (data, matches, output) => {
        if (matches.id === 'B52B')
          return data.myMutation === 'beta'
            ? output.betaNear!({ mech: output.getUnder!() })
            : output.alphaNear!({ mech: output.maxMelee!() });
        return data.myMutation === 'beta'
          ? output.betaFar!({ mech: output.maxMelee!() })
          : output.alphaFar!({ mech: output.getUnder!() });
      },
      tts: (data, matches, output) => {
        if (matches.id === 'B52B')
          return data.myMutation === 'beta'
            ? output.betaNearTts!({ mech: output.getUnder!() })
            : output.alphaNear!({ mech: output.maxMelee!() });
        return data.myMutation === 'beta'
          ? output.betaFarTts!({ mech: output.maxMelee!() })
          : output.alphaFar!({ mech: output.getUnder!() });
      },
      outputStrings: {
        getUnder: Outputs.getUnder,
        maxMelee: {
          en: 'Max Melee',
          de: 'Max Nahkampf',
          cn: '最大近战距离',
          ko: '칼끝딜',
        },
        alphaNear: {
          en: '${mech} (Avoid Near Stack)',
          de: '${mech} (Vermeide Nah-Sammeln)',
          cn: '${mech} (避开近分摊)',
          ko: '${mech} (근거리 쉐어 피하기)',
        },
        alphaFar: {
          en: '${mech} (Avoid Far Stack)',
          de: '${mech} (Vermeide Fern-Sammeln)',
          cn: '${mech} (避开远分摊)',
          ko: '${mech} (원거리 쉐어 피하기)',
        },
        betaNear: {
          en: 'Near β Stack: ${mech}',
          de: 'Nah β Sammeln: ${mech}',
          cn: '近 β 分摊: ${mech}',
          ko: '근거리 β 쉐어: ${mech}',
        },
        betaFar: {
          en: 'Far β Stack: ${mech}',
          de: 'Fern β Sammeln: ${mech}',
          cn: '远 β 分摊: ${mech}',
          ko: '원거리 β 쉐어: ${mech}',
        },
        betaNearTts: {
          en: 'Near β Stack: ${mech}',
          de: 'Nah β Sammeln: ${mech}',
          cn: '近 β 分摊: ${mech}',
          ko: '근거리 베타 쉐어: ${mech}',
        },
        betaFarTts: {
          en: 'Far β Stack: ${mech}',
          de: 'Fern β Sammeln: ${mech}',
          cn: '远 β 分摊: ${mech}',
          ko: '원거리 베타 쉐어: ${mech}',
        },
      },
    },
    {
      id: 'R12S Idyllic Dream',
      type: 'StartsUsing',
      netRegex: { id: 'B509', source: 'Lindwurm', capture: false },
      durationSeconds: 4.7,
      response: Responses.bigAoe('alert'),
    },
    {
      id: 'R12S Idyllic Dream Staging 2 Clone Order Collect',
      type: 'ActorControlExtra',
      netRegex: { category: '0197', param1: '11D2', capture: true },
      condition: (data) => {
        if (data.phase === 'idyllic' && data.replicationCounter === 2)
          return true;
        return false;
      },
      run: (data, matches) => {
        const actor = data.actorPositions[matches.id];
        if (actor === undefined)
          return;
        const dirNum = Directions.xyTo8DirNum(actor.x, actor.y, center.x, center.y);
        data.replication3CloneOrder.push(dirNum);
      },
    },
    {
      id: 'R12S Idyllic Dream Staging 2 First Clone Cardinal/Intercardinal',
      type: 'ActorControlExtra',
      netRegex: { category: '0197', param1: '11D2', capture: true },
      condition: (data) => {
        if (data.phase === 'idyllic' && data.replicationCounter === 2)
          return true;
        return false;
      },
      suppressSeconds: 9999,
      infoText: (data, matches, output) => {
        const actor = data.actorPositions[matches.id];
        if (actor === undefined)
          return;

        const dirNum = Directions.xyTo8DirNum(actor.x, actor.y, center.x, center.y);

        if (dirNum % 2 === 0)
          return output.firstClone!({ cards: output.cardinals!() });
        return output.firstClone!({ cards: output.intercards!() });
      },
      outputStrings: {
        cardinals: Outputs.cardinals,
        intercards: Outputs.intercards,
        firstClone: {
          en: 'First Clone: ${cards}',
          de: 'Erste Klone: ${cards}',
          cn: '第一个分身: ${cards}',
          ko: '첫 번째 분신: ${cards}',
        },
      },
    },
    {
      id: 'R12S Idyllic Dream Staging 2 Tethered Clone Collect',
      // Map the locations to a player name
      type: 'Tether',
      netRegex: { id: headMarkerData['lockedTether'], capture: true },
      condition: (data) => {
        if (
          data.phase === 'idyllic' &&
          data.replicationCounter === 2
        )
          return true;
        return false;
      },
      run: (data, matches) => {
        const actor = data.actorPositions[matches.sourceId];
        if (actor === undefined)
          return;

        const dirNum = Directions.xyTo8DirNum(actor.x, actor.y, center.x, center.y);
        data.replication3CloneDirNumPlayers[dirNum] = matches.target;
      },
    },
    {
      id: 'R12S Idyllic Dream Staging 2 Tethered Clone',
      type: 'Tether',
      netRegex: { id: headMarkerData['lockedTether'], capture: true },
      condition: (data, matches) => {
        if (
          data.phase === 'idyllic' &&
          data.replicationCounter === 2 &&
          data.me === matches.target
        )
          return true;
        return false;
      },
      suppressSeconds: 9999,
      infoText: (data, matches, output) => {
        const actor = data.actorPositions[matches.sourceId];
        if (actor === undefined)
          return output.cloneTether!();

        const dirNum = Directions.xyTo8DirNum(actor.x, actor.y, center.x, center.y);
        const dir = Directions.output8Dir[dirNum] ?? 'unknown';
        return output.cloneTetherDir!({ dir: output[dir]!() });
      },
      outputStrings: {
        ...Directions.outputStrings8Dir,
        cloneTether: {
          en: 'Tethered to Clone',
          de: 'Verbunden zum Klon',
          cn: '分身连线',
          ko: '분신과 선 연결',
        },
        cloneTetherDir: {
          en: 'Tethered to ${dir} Clone',
          de: 'Verbunden zum ${dir} Klon',
          cn: '被 ${dir} 分身连线',
          ko: '${dir} 분신과 선 연결',
        },
      },
    },
    {
      id: 'R12S Idyllic Dream Power Gusher and Snaking Kick Collect',
      // Need to know these for later
      // B511 Snaking Kick
      // B512 from boss is the VFX and has headings that show directions for B50F and B510
      // B50F Power Gusher is the East/West caster
      // B510 Power Gusher is the North/South caster
      // Right now just the B510 caster is needed to resolve
      type: 'StartsUsing',
      netRegex: { id: ['B50F', 'B510', 'B511'], source: 'Lindschrat', capture: true },
      run: (data, matches) => {
        // Temporal Curtain can have early calls based on matching the id for which add went where
        switch (matches.id) {
          case 'B510': {
            const y = parseFloat(matches.y);
            data.idyllicVision2NorthSouthCleaveSpot = y < center.y ? 'north' : 'south';
            data.idyllicDreamActorNS = matches.sourceId;
            return;
          }
          case 'B511':
            data.idyllicDreamActorSnaking = matches.sourceId;
            return;
          case 'B50F':
            data.idyllicDreamActorEW = matches.sourceId;
            return;
        }
      },
    },
    {
      id: 'R12S Idyllic Dream Power Gusher Vision',
      // Call where the E/W safe spots will be later
      type: 'StartsUsing',
      netRegex: { id: 'B510', source: 'Lindschrat', capture: true },
      infoText: (_data, matches, output) => {
        const y = parseFloat(matches.y);
        const dir = y < center.y ? 'north' : 'south';
        return output.text!({ dir: output[dir]!(), sides: output.sides!() });
      },
      outputStrings: {
        north: Outputs.north,
        south: Outputs.south,
        sides: Outputs.sides,
        text: {
          en: '${dir} + ${sides} (later)',
          de: '${dir} + ${sides} (später)',
          cn: '${dir} + ${sides} (稍后)',
          ko: '${dir} + ${sides} (나중에)',
        },
      },
    },
    {
      id: 'R12S Replication 4 Ability Tethers Initial Call',
      type: 'Tether',
      netRegex: {
        id: [
          headMarkerData['manaBurstTether'],
          headMarkerData['heavySlamTether'],
        ],
        capture: true,
      },
      condition: (data, matches) => {
        if (data.me === matches.target && data.phase === 'idyllic')
          return true;
        return false;
      },
      delaySeconds: 0.1,
      durationSeconds: 7,
      suppressSeconds: 9999,
      infoText: (data, _matches, output) => {
        const first = data.replication4DirNumAbility[0];
        if (first === undefined) {
          return output.getTether!();
        }

        const mech = first === headMarkerData['heavySlamTether']
          ? 'stacks'
          : first === headMarkerData['manaBurstTether']
          ? 'defamations'
          : 'unknown';

        const clones = data.replication3CloneDirNumPlayers;
        const strat = data.triggerSetConfig.replication4Strategy;
        const myDirNum = Object.keys(clones).find(
          (key) => clones[parseInt(key)] === data.me,
        );
        if (myDirNum !== undefined) {
          // Get dirNum of player for custom output based on staging 2 tether
          // Player can replace the get tether with get defamation, get stack and
          // the location they want based on custom plan
          switch (parseInt(myDirNum)) {
            case 0:
              return output.mechLaterNClone!({
                later: output.mechLater!({ mech: output[mech]!() }),
                tether: strat === 'dn'
                  ? output.getStackEastGroupQuad1DN!({
                    dir: mech === 'stacks'
                      ? output['dirN']!()
                      : mech === 'defamations'
                      ? output['dirNE']!()
                      : output['unknown']!(),
                  })
                  : strat === 'em'
                  ? mech === 'stacks'
                    ? output.getStackWestGroup1EM!({
                      dir: output['dirN']!(),
                    })
                    : mech === 'defamations'
                    ? output.getStackWestGroup2EM!({
                      dir: output['dirSW']!(),
                    })
                    : output.getStackWestGroup12EM!({
                      dir1: output['dirN']!(),
                      dir2: output['dirSW']!(),
                    })
                  : strat === 'caro'
                  ? output.getDefamationEastGroupQuad1Caro!({
                    dir: mech === 'defamations'
                      ? output['dirN']!()
                      : mech === 'stacks'
                      ? output['dirNE']!()
                      : output['unknown']!(),
                  })
                  : strat === 'nukemaru'
                  ? output.getStackEastGroupQuad1Nukemaru!({
                    dir: mech === 'stacks'
                      ? output['dirN']!()
                      : mech === 'defamations'
                      ? output['dirNE']!()
                      : output['unknown']!(),
                  })
                  : output.getTether!(),
              });
            case 1:
              return output.mechLaterNEClone!({
                later: output.mechLater!({ mech: output[mech]!() }),
                tether: strat === 'dn'
                  ? output.getStackEastGroupQuad2DN!({
                    dir: mech === 'stacks'
                      ? output['dirE']!()
                      : mech === 'defamations'
                      ? output['dirSE']!()
                      : output['unknown']!(),
                  })
                  : strat === 'em'
                  ? mech === 'stacks'
                    ? output.getStackEastGroup1EM!({
                      dir: output['dirS']!(),
                    })
                    : mech === 'defamations'
                    ? output.getStackEastGroup2EM!({
                      dir: output['dirNE']!(),
                    })
                    : output.getStackEastGroup12EM!({
                      dir1: output['dirS']!(),
                      dir2: output['dirNE']!(),
                    })
                  : strat === 'caro'
                  ? output.getStackEastGroupQuad2Caro!({
                    dir: mech === 'stacks'
                      ? output['dirE']!()
                      : mech === 'defamations'
                      ? output['dirSE']!()
                      : output['unknown']!(),
                  })
                  : strat === 'nukemaru'
                  ? output.getDefamationEastGroupQuad1Nukemaru!({
                    dir: mech === 'defamations'
                      ? output['dirN']!()
                      : mech === 'stacks'
                      ? output['dirNE']!()
                      : output['unknown']!(),
                  })
                  : output.getTether!(),
              });
            case 2:
              return output.mechLaterEClone!({
                later: output.mechLater!({ mech: output[mech]!() }),
                tether: strat === 'dn'
                  ? output.getStackWestGroupQuad3DN!({
                    dir: mech === 'stacks'
                      ? output['dirS']!()
                      : mech === 'defamations'
                      ? output['dirSW']!()
                      : output['unknown']!(),
                  })
                  : strat === 'em'
                  ? mech === 'stacks'
                    ? output.getStackEastGroup3EM!({
                      dir: output['dirE']!(),
                    })
                    : mech === 'defamations'
                    ? output.getStackEastGroup4EM!({
                      dir: output['dirSE']!(),
                    })
                    : output.getStackEastGroup34EM!({
                      dir1: output['dirE']!(),
                      dir2: output['dirSE']!(),
                    })
                  : strat === 'caro'
                  ? output.getStackEastGroupQuad3Caro!({
                    dir: mech === 'stacks'
                      ? output['dirS']!()
                      : mech === 'defamations'
                      ? output['dirSW']!()
                      : output['unknown']!(),
                  })
                  : strat === 'nukemaru'
                  ? output.getDefamationWestGroupQuad4Nukemaru!({
                    dir: mech === 'defamations'
                      ? output['dirW']!()
                      : mech === 'stacks'
                      ? output['dirNW']!()
                      : output['unknown']!(),
                  })
                  : output.getTether!(),
              });
            case 3:
              return output.mechLaterSEClone!({
                later: output.mechLater!({ mech: output[mech]!() }),
                tether: strat === 'dn'
                  ? output.getStackWestGroupQuad4DN!({
                    dir: mech === 'stacks'
                      ? output['dirW']!()
                      : mech === 'defamations'
                      ? output['dirNW']!()
                      : output['unknown']!(),
                  })
                  : strat === 'em'
                  ? mech === 'defamations'
                    ? output.getDefamationEastGroup3EM!({
                      dir: output['dirE']!(),
                    })
                    : mech === 'stacks'
                    ? output.getDefamationEastGroup4EM!({
                      dir: output['dirSE']!(),
                    })
                    : output.getDefamationEastGroup34EM!({
                      dir1: output['dirE']!(),
                      dir2: output['dirSE']!(),
                    })
                  : strat === 'caro'
                  ? output.getDefamationEastGroupQuad4Caro!({
                    dir: mech === 'defamations'
                      ? output['dirW']!()
                      : mech === 'stacks'
                      ? output['dirNW']!()
                      : output['unknown']!(),
                  })
                  : strat === 'nukemaru'
                  ? output.getDefamationEastGroupQuad2Nukemaru!({
                    dir: mech === 'defamations'
                      ? output['dirE']!()
                      : mech === 'stacks'
                      ? output['dirSE']!()
                      : output['unknown']!(),
                  })
                  : output.getTether!(),
              });
            case 4:
              return output.mechLaterSClone!({
                later: output.mechLater!({ mech: output[mech]!() }),
                tether: strat === 'dn'
                  ? output.getDefamationEastGroupQuad1DN!({
                    dir: mech === 'defamations'
                      ? output['dirN']!()
                      : mech === 'stacks'
                      ? output['dirNE']!()
                      : output['unknown']!(),
                  })
                  : strat === 'em'
                  ? mech === 'defamations'
                    ? output.getDefamationEastGroup1EM!({
                      dir: output['dirS']!(),
                    })
                    : mech === 'stacks'
                    ? output.getDefamationEastGroup2EM!({
                      dir: output['dirNE']!(),
                    })
                    : output.getDefamationEastGroup12EM!({
                      dir1: output['dirS']!(),
                      dir2: output['dirNE']!(),
                    })
                  : strat === 'caro'
                  ? output.getDefamationWestGroupQuad1Caro!({
                    dir: mech === 'defamations'
                      ? output['dirN']!()
                      : mech === 'stacks'
                      ? output['dirNE']!()
                      : output['unknown']!(),
                  })
                  : strat === 'nukemaru'
                  ? output.getDefamationWestGroupQuad3Nukemaru!({
                    dir: mech === 'defamations'
                      ? output['dirS']!()
                      : mech === 'stacks'
                      ? output['dirSW']!()
                      : output['unknown']!(),
                  })
                  : output.getTether!(),
              });
            case 5:
              return output.mechLaterSWClone!({
                later: output.mechLater!({ mech: output[mech]!() }),
                tether: strat === 'dn'
                  ? output.getDefamationEastGroupQuad2DN!({
                    dir: mech === 'defamations'
                      ? output['dirE']!()
                      : mech === 'stacks'
                      ? output['dirSE']!()
                      : output['unknown']!(),
                  })
                  : strat === 'em'
                  ? mech === 'defamations'
                    ? output.getDefamationWestGroup1EM!({
                      dir: output['dirN']!(),
                    })
                    : mech === 'stacks'
                    ? output.getDefamationWestGroup2EM!({
                      dir: output['dirSE']!(),
                    })
                    : output.getDefamationWestGroup12EM!({
                      dir1: output['dirN']!(),
                      dir2: output['dirSE']!(),
                    })
                  : strat === 'caro'
                  ? output.getStackWestGroupQuad2Caro!({
                    dir: mech === 'stacks'
                      ? output['dirE']!()
                      : mech === 'defamations'
                      ? output['dirSE']!()
                      : output['unknown']!(),
                  })
                  : strat === 'nukemaru'
                  ? output.getStackWestGroupQuad3Nukemaru!({
                    dir: mech === 'stacks'
                      ? output['dirS']!()
                      : mech === 'defamations'
                      ? output['dirSW']!()
                      : output['unknown']!(),
                  })
                  : output.getTether!(),
              });
            case 6:
              return output.mechLaterWClone!({
                later: output.mechLater!({ mech: output[mech]!() }),
                tether: strat === 'dn'
                  ? output.getDefamationWestGroupQuad3DN!({
                    dir: mech === 'defamations'
                      ? output['dirS']!()
                      : mech === 'stacks'
                      ? output['dirSW']!()
                      : output['unknown']!(),
                  })
                  : strat === 'em'
                  ? mech === 'defamations'
                    ? output.getDefamationWestGroup3EM!({
                      dir: output['dirW']!(),
                    })
                    : mech === 'stacks'
                    ? output.getDefamationWestGroup4EM!({
                      dir: output['dirNW']!(),
                    })
                    : output.getDefamationWestGroup34EM!({
                      dir1: output['dirW']!(),
                      dir2: output['dirNW']!(),
                    })
                  : strat === 'caro'
                  ? output.getStackWestGroupQuad3Caro!({
                    dir: mech === 'stacks'
                      ? output['dirS']!()
                      : mech === 'defamations'
                      ? output['dirSW']!()
                      : output['unknown']!(),
                  })
                  : strat === 'nukemaru'
                  ? output.getStackWestGroupQuad4Nukemaru!({
                    dir: mech === 'stacks'
                      ? output['dirW']!()
                      : mech === 'defamations'
                      ? output['dirNW']!()
                      : output['unknown']!(),
                  })
                  : output.getTether!(),
              });
            case 7:
              return output.mechLaterNWClone!({
                later: output.mechLater!({ mech: output[mech]!() }),
                tether: strat === 'dn'
                  ? output.getDefamationWestGroupQuad4DN!({
                    dir: mech === 'defamations'
                      ? output['dirW']!()
                      : mech === 'stacks'
                      ? output['dirNW']!()
                      : output['unknown']!(),
                  })
                  : strat === 'em'
                  ? mech === 'stacks'
                    ? output.getStackWestGroup3EM!({
                      dir: output['dirW']!(),
                    })
                    : mech === 'defamations'
                    ? output.getStackWestGroup4EM!({
                      dir: output['dirNW']!(),
                    })
                    : output.getStackWestGroup34EM!({
                      dir1: output['dirW']!(),
                      dir2: output['dirNW']!(),
                    })
                  : strat === 'caro'
                  ? output.getDefamationWestGroupQuad4Caro!({
                    dir: mech === 'defamations'
                      ? output['dirW']!()
                      : mech === 'stacks'
                      ? output['dirNW']!()
                      : output['unknown']!(),
                  })
                  : strat === 'nukemaru'
                  ? output.getStackEastGroupQuad2Nukemaru!({
                    dir: mech === 'stacks'
                      ? output['dirE']!()
                      : mech === 'defamations'
                      ? output['dirSE']!()
                      : output['unknown']!(),
                  })
                  : output.getTether!(),
              });
          }
        }

        return output.mechLaterTether!({
          later: output.mechLater!({ mech: output[mech]!() }),
          tether: output.getTether!(),
        });
      },
      outputStrings: {
        ...Directions.outputStrings8Dir,
        getTether: {
          en: 'Get Tether',
          de: 'Nimm Verbindung',
          cn: '接线',
          ko: '선 가져가기',
        },
        mechLater: {
          en: '${mech} First (later)',
          de: '${mech} Zuerst (später)',
          cn: '${mech} 先 (稍后)',
          ko: '${mech} 먼저 (나중에)',
        },
        defamations: {
          en: 'Defamations',
          de: 'Große AoE auf dir',
          fr: 'Grosse AoE sur vous',
          ja: '自分に巨大な爆発',
          cn: '大圈',
          ko: '광역징',
          tc: '大圈點名',
        },
        stacks: Outputs.stacks,
        mechLaterTether: {
          en: '${later}; ${tether}',
          de: '${later}; ${tether}',
          cn: '${later}; ${tether}',
          ko: '${later}; ${tether}',
        },
        mechLaterNClone: {
          en: '${later}; ${tether}',
          de: '${later}; ${tether}',
          cn: '${later}; ${tether}',
          ko: '${later}; ${tether}',
        },
        mechLaterNEClone: {
          en: '${later}; ${tether}',
          de: '${later}; ${tether}',
          cn: '${later}; ${tether}',
          ko: '${later}; ${tether}',
        },
        mechLaterEClone: {
          en: '${later}; ${tether}',
          de: '${later}; ${tether}',
          cn: '${later}; ${tether}',
          ko: '${later}; ${tether}',
        },
        mechLaterSEClone: {
          en: '${later}; ${tether}',
          de: '${later}; ${tether}',
          cn: '${later}; ${tether}',
          ko: '${later}; ${tether}',
        },
        mechLaterSClone: {
          en: '${later}; ${tether}',
          de: '${later}; ${tether}',
          cn: '${later}; ${tether}',
          ko: '${later}; ${tether}',
        },
        mechLaterSWClone: {
          en: '${later}; ${tether}',
          de: '${later}; ${tether}',
          cn: '${later}; ${tether}',
          ko: '${later}; ${tether}',
        },
        mechLaterWClone: {
          en: '${later}; ${tether}',
          de: '${later}; ${tether}',
          cn: '${later}; ${tether}',
          ko: '${later}; ${tether}',
        },
        mechLaterNWClone: {
          en: '${later}; ${tether}',
          de: '${later}; ${tether}',
          cn: '${later}; ${tether}',
          ko: '${later}; ${tether}',
        },
        getStackEastGroupQuad1DN: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackEastGroupQuad2DN: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackWestGroupQuad3DN: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackWestGroupQuad4DN: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getDefamationEastGroupQuad1DN: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationEastGroupQuad2DN: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationWestGroupQuad3DN: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationWestGroupQuad4DN: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getStackWestGroup1EM: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackWestGroup2EM: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackWestGroup12EM: {
          en: 'Get ${dir1}/${dir2} Stack Tether',
          de: 'Nimm ${dir1}/${dir2} Sammel-Verbindung',
          cn: '接${dir1}/${dir2}分摊线',
          ko: '${dir1}/${dir2} 쉐어 선 가져가기',
        },
        getStackEastGroup1EM: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackEastGroup2EM: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackEastGroup12EM: {
          en: 'Get ${dir1}/${dir2} Stack Tether',
          de: 'Nimm ${dir1}/${dir2} Sammel-Verbindung',
          cn: '接${dir1}/${dir2}分摊线',
          ko: '${dir1}/${dir2} 쉐어 선 가져가기',
        },
        getStackEastGroup3EM: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackEastGroup4EM: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackEastGroup34EM: {
          en: 'Get ${dir1}/${dir2} Stack Tether',
          de: 'Nimm ${dir1}/${dir2} Sammel-Verbindung',
          cn: '接${dir1}/${dir2}分摊线',
          ko: '${dir1}/${dir2} 쉐어 선 가져가기',
        },
        getDefamationEastGroup3EM: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationEastGroup4EM: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationEastGroup34EM: {
          en: 'Get ${dir1}/${dir2} Defamation Tether',
          de: 'Nimm ${dir1}/${dir2} Ehrenstrafe-Verbindung',
          cn: '接${dir1}/${dir2}大圈线',
          ko: '${dir1}/${dir2} 광역징 선 가져가기',
        },
        getDefamationEastGroup1EM: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationEastGroup2EM: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationEastGroup12EM: {
          en: 'Get ${dir1}/${dir2} Defamation Tether',
          de: 'Nimm ${dir1}/${dir2} Ehrenstrafe-Verbindung',
          cn: '接${dir1}/${dir2}大圈线',
          ko: '${dir1}/${dir2} 광역징 선 가져가기',
        },
        getDefamationWestGroup1EM: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationWestGroup2EM: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationWestGroup12EM: {
          en: 'Get ${dir1}/${dir2} Defamation Tether',
          de: 'Nimm ${dir1}/${dir2} Ehrenstrafe-Verbindung',
          cn: '接${dir1}/${dir2}大圈线',
          ko: '${dir1}/${dir2} 광역징 선 가져가기',
        },
        getDefamationWestGroup3EM: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationWestGroup4EM: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationWestGroup34EM: {
          en: 'Get ${dir1}/${dir2} Defamation Tether',
          de: 'Nimm ${dir1}/${dir2} Ehrenstrafe-Verbindung',
          cn: '接${dir1}/${dir2}大圈线',
          ko: '${dir1}/${dir2} 광역징 선 가져가기',
        },
        getStackWestGroup3EM: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackWestGroup4EM: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackWestGroup34EM: {
          en: 'Get ${dir1}/${dir2} Stack Tether',
          de: 'Nimm ${dir1}/${dir2} Sammel-Verbindung',
          cn: '接${dir1}/${dir2}分摊线',
          ko: '${dir1}/${dir2} 쉐어 선 가져가기',
        },
        getDefamationEastGroupQuad1Caro: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getStackEastGroupQuad2Caro: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackEastGroupQuad3Caro: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getDefamationEastGroupQuad4Caro: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationWestGroupQuad1Caro: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getStackWestGroupQuad2Caro: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackWestGroupQuad3Caro: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getDefamationWestGroupQuad4Caro: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getStackEastGroupQuad1Nukemaru: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getDefamationEastGroupQuad1Nukemaru: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationWestGroupQuad4Nukemaru: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationEastGroupQuad2Nukemaru: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getDefamationWestGroupQuad3Nukemaru: {
          en: 'Get ${dir} Defamation Tether',
          de: 'Nimm ${dir} Ehrenstrafe-Verbindung',
          cn: '接${dir}大圈线',
          ko: '${dir} 광역징 선 가져가기',
        },
        getStackWestGroupQuad3Nukemaru: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackWestGroupQuad4Nukemaru: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
        getStackEastGroupQuad2Nukemaru: {
          en: 'Get ${dir} Stack Tether',
          de: 'Nimm ${dir} Sammel-Verbindung',
          cn: '接${dir}分摊线',
          ko: '${dir} 쉐어 선 가져가기',
        },
      },
    },
    {
      id: 'R12S Replication 4 Locked Tether Collect',
      type: 'Tether',
      netRegex: { id: headMarkerData['lockedTether'], capture: true },
      condition: (data) => {
        if (
          data.phase === 'idyllic' &&
          data.replicationCounter === 4
        )
          return true;
        return false;
      },
      run: (data, matches) => {
        const actor = data.actorPositions[matches.sourceId];
        const target = matches.target;
        if (actor === undefined) {
          // Setting to use that we know we have a tether but couldn't determine what ability it is
          if (data.me === target)
            data.replication4PlayerAbilities[target] = 'unknown';
          return;
        }

        const dirNum = Directions.xyTo8DirNum(
          actor.x,
          actor.y,
          center.x,
          center.y,
        );

        // Store the player at each dirNum
        data.replication4BossCloneDirNumPlayers[dirNum] = target;

        // Lookup what the tether was at the same location
        const ability = data.replication4DirNumAbility[dirNum];
        if (ability === undefined) {
          // Setting to use that we know we have a tether but couldn't determine what ability it is
          data.replication4PlayerAbilities[target] = 'unknown';
          return;
        }
        data.replication4PlayerAbilities[target] = ability;

        // Create ability order once we have all 8 players
        // If players had more than one tether previously, the extra tethers are randomly assigned
        if (Object.keys(data.replication4PlayerAbilities).length === 8) {
          // Used for Twisted Vision 7 and 8 mechanics
          const abilities = data.replication4PlayerAbilities;
          const order = data.replication3CloneOrder; // Order in which clones spawned
          const players = data.replication3CloneDirNumPlayers; // Direction of player's clone

          // Mechanics are resolved clockwise, create order based on cards/inters
          const first = order[0];
          if (first === undefined)
            return;
          const dirNumOrder = first % 2 === 0 ? [0, 2, 4, 6, 1, 3, 5, 7] : [1, 3, 5, 7, 0, 2, 4, 6];
          for (const dirNum of dirNumOrder) {
            const player = players[dirNum] ?? 'unknown';
            const ability = abilities[player] ?? 'unknown';
            data.replication4PlayerOrder.push(player);
            data.replication4AbilityOrder.push(ability);
          }
        }
      },
    },
    {
      id: 'R12S Replication 4 Locked Tether',
      // At this point the player needs to dodge the north/south cleaves + chariot
      // Simultaneously there will be a B4F2 Lindwurm's Meteor bigAoe that ends with room split
      type: 'Tether',
      netRegex: { id: headMarkerData['lockedTether'], capture: true },
      condition: (data, matches) => {
        if (
          data.phase === 'idyllic' &&
          data.twistedVisionCounter === 3 &&
          data.me === matches.target
        )
          return true;
        return false;
      },
      delaySeconds: 0.1,
      durationSeconds: 8,
      alertText: (data, matches, output) => {
        const meteorAoe = output.meteorAoe!({
          bigAoe: output.bigAoe!(),
          groups: output.healerGroups!(),
        });
        const cleaveOrigin = data.idyllicVision2NorthSouthCleaveSpot;
        const myAbility = data.replication4PlayerAbilities[data.me];
        // Get direction of the tether
        const actor = data.actorPositions[matches.sourceId];
        if (actor === undefined || cleaveOrigin === undefined) {
          switch (myAbility) {
            case headMarkerData['manaBurstTether']:
              return output.manaBurstTether!({ meteorAoe: meteorAoe });
            case headMarkerData['heavySlamTether']:
              return output.heavySlamTether!({ meteorAoe: meteorAoe });
          }
          return;
        }

        const dirNum = Directions.xyTo8DirNum(actor.x, actor.y, center.x, center.y);
        const dir = Directions.output8Dir[dirNum] ?? 'unknown';

        const dodge = output.dodgeCleaves!({
          dir: output[cleaveOrigin]!(),
          sides: output.sides!(),
        });

        switch (myAbility) {
          case headMarkerData['manaBurstTether']:
            return output.manaBurstTetherDir!({
              dir: output[dir]!(),
              dodgeCleaves: dodge,
              meteorAoe: meteorAoe,
            });
          case headMarkerData['heavySlamTether']:
            return output.heavySlamTetherDir!({
              dir: output[dir]!(),
              dodgeCleaves: dodge,
              meteorAoe: meteorAoe,
            });
        }
      },
      outputStrings: {
        ...Directions.outputStrings8Dir,
        north: Outputs.north,
        south: Outputs.south,
        sides: Outputs.sides,
        bigAoe: Outputs.bigAoe,
        healerGroups: Outputs.healerGroups,
        meteorAoe: {
          en: '${bigAoe} + ${groups}',
          de: '${bigAoe} + ${groups}',
          cn: '${bigAoe} + ${groups}',
          ko: '${bigAoe} + ${groups}',
        },
        dodgeCleaves: {
          en: '${dir} + ${sides}',
          de: '${dir} + ${sides}',
          cn: '${dir} + ${sides}',
          ko: '${dir} + ${sides}',
        },
        manaBurstTetherDir: {
          en: '${dodgeCleaves} (${dir} Defamation Tether) => ${meteorAoe}',
          de: '${dodgeCleaves} (${dir} Ehrenstrafe-Verbindung) => ${meteorAoe}',
          cn: '${dodgeCleaves} (${dir}大圈线) => ${meteorAoe}',
          ko: '${dodgeCleaves} (${dir} 광역징 선) => ${meteorAoe}',
        },
        manaBurstTether: {
          en: ' N/S Clone (Defamation Tether) => ${meteorAoe}',
          de: ' N/S Klon (Ehrenstrafe-Verbindung) => ${meteorAoe}',
          cn: ' 南/北分身 (大圈线) => ${meteorAoe}',
          ko: ' 북/남 분신 (광역징 선) => ${meteorAoe}',
        },
        heavySlamTetherDir: {
          en: '${dodgeCleaves} (${dir} Stack Tether) => ${meteorAoe}',
          de: '${dodgeCleaves} (${dir} Sammel-Verbindung) => ${meteorAoe}',
          cn: '${dodgeCleaves} (${dir}分摊线) => ${meteorAoe}',
          ko: '${dodgeCleaves} (${dir} 쉐어징 선) => ${meteorAoe}',
        },
        heavySlamTether: {
          en: ' N/S Clone (Stack Tether) => ${meteorAoe}',
          de: ' N/S Klon (Sammel-Verbindung) => ${meteorAoe}',
          cn: ' 南/北分身 (分摊线) => ${meteorAoe}',
          ko: ' 북/남 분신 (쉐어징 선) => ${meteorAoe}',
        },
      },
    },
    {
      id: 'R12S CombatantMemory Tower Collect',
      // Towers spawn in these Locations:
      // (81.7574, 95.7574)  (90.2426, 95.7574)   |   (109.7574, 95.7574)  (118.2426, 95.7574)
      // (81.7574, 104.2426) (90.2426, 104.2426)  |   (109.7574, 104.2426) (118.2426, 104.2426)
      // The patterns will be the same on both platforms (not mirrored)
      // There are only two patterns.
      // Earth/Wind are North, Fire/Dark are South
      // Wind/Dark and Fire/Earth are diagonal from each other on each platform
      // So if we know where one tower is, we know where all are
      // Wind:  1EBF25
      // Dark:  1EBF26
      // Earth: 1EBF27
      // Fire:  1EBF28
      type: 'CombatantMemory',
      netRegex: {
        change: 'Add',
        pair: [{ key: 'BNpcID', value: ['1EBF25', '1EBF26', '1EBF27', '1EBF28'] }],
        capture: true,
      },
      suppressSeconds: 9999,
      run: (data, matches) => {
        const x = parseFloat(matches.pairPosX ?? '0');
        const towerMap = {
          '1EBF25': 'wind',
          '1EBF26': 'dark',
          '1EBF27': 'earth',
          '1EBF28': 'fire',
          'unknown': 'unknown',
        };
        const pattern1 = ['earth', 'wind', 'dark', 'fire'];
        const pattern2 = ['wind', 'earth', 'fire', 'dark'];
        const bnpcid = matches.pairBNpcID ?? 'unknown';
        const kind = towerMap[bnpcid as keyof typeof towerMap];
        if (kind === 'earth' || kind === 'dark') {
          if ((x > 81 && x < 83) || (x > 109 && x < 111))
            data.cosmicKissPattern = pattern1;
          else
            data.cosmicKissPattern = pattern2;
        } else if (kind === 'wind' || kind === 'fire') {
          if ((x > 81 && x < 83) || (x > 109 && x < 111))
            data.cosmicKissPattern = pattern2;
          else
            data.cosmicKissPattern = pattern1;
        }
      },
    },
    {
      id: 'R12S Arcadian Arcanum',
      // Players hit will receive 1044 Light Resistance Down II debuff
      type: 'StartsUsing',
      netRegex: { id: 'B529', source: 'Lindwurm', capture: false },
      response: Responses.spread(),
    },
    {
      id: 'R12S Light Resistance Down II Collect',
      // Players cannot soak a tower that has holy (triple element towers)
      type: 'GainsEffect',
      netRegex: { effectId: '1044', capture: true },
      condition: Conditions.targetIsYou(),
      run: (data) => data.hasLightResistanceDown = true,
    },
    {
      id: 'R12S Light Resistance Down II',
      type: 'GainsEffect',
      netRegex: { effectId: '1044', capture: true },
      condition: (data, matches) => {
        if (data.twistedVisionCounter === 3 && data.me === matches.target)
          return true;
        return false;
      },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Soak Fire/Earth Meteor (later)',
          de: 'Nimm Feuer/Erde Meteor (später)',
          cn: '踩火/土陨石塔 (稍后)',
          ko: '불/땅 메테오 밟기 (나중에)',
        },
      },
    },
    {
      id: 'R12S No Light Resistance Down II',
      type: 'GainsEffect',
      netRegex: { effectId: '1044', capture: false },
      condition: (data) => data.twistedVisionCounter === 3,
      delaySeconds: 0.1,
      suppressSeconds: 9999,
      infoText: (data, _matches, output) => {
        if (!data.hasLightResistanceDown)
          return output.text!();
      },
      outputStrings: {
        text: {
          en: 'Soak a White/Star Meteor (later)',
          de: 'Nimm Weißen/Stern Meteor (später)',
          cn: '踩光/彩色陨石塔 (稍后)',
          ko: '바람/어둠 메테오 밟기 (나중에)',
        },
      },
    },
    {
      id: 'R12S Twisted Vision 4 Stack/Defamation 1',
      type: 'StartsUsing',
      netRegex: { id: 'BBE2', source: 'Lindwurm', capture: false },
      condition: (data) => data.twistedVisionCounter === 4,
      durationSeconds: 10,
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          stacks: Outputs.stacks,
          stackOnYou: Outputs.stackOnYou,
          defamations: {
            en: 'Avoid Defamations',
            de: 'Vermeide Ehrenstrafe',
            cn: '远离大圈',
            ko: '광역징 피하기',
          },
          defamationOnYou: Outputs.defamationOnYou,
          stacksThenDefamations: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          defamationsThenStacks: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          stacksThenDefamationOnYou: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          defamationsThenStackOnYou: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          stackOnYouThenDefamations: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          defamationOnYouThenStack: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
        };
        const player1 = data.replication4BossCloneDirNumPlayers[0];
        const player2 = data.replication4BossCloneDirNumPlayers[4];
        const player3 = data.replication4BossCloneDirNumPlayers[1];
        const player4 = data.replication4BossCloneDirNumPlayers[5];
        const abilityId = data.replication4DirNumAbility[0]; // Only need to know one

        if (
          abilityId === undefined || player1 === undefined ||
          player2 === undefined || player3 === undefined ||
          player4 === undefined
        )
          return;

        const ability1 = abilityId === headMarkerData['manaBurstTether']
          ? 'defamations'
          : abilityId === headMarkerData['heavySlamTether']
          ? 'stacks'
          : 'unknown';

        if (ability1 === 'stacks') {
          if (data.me === player1 || data.me === player2)
            return {
              alertText: output.stackOnYouThenDefamations!({
                mech1: output.stackOnYou!(),
                mech2: output.defamations!(),
              }),
            };

          if (data.me === player3 || data.me === player4)
            return {
              infoText: output.stacksThenDefamationOnYou!({
                mech1: output.stacks!(),
                mech2: output.defamationOnYou!(),
              }),
            };

          return {
            infoText: output.stacksThenDefamations!({
              mech1: output.stacks!(),
              mech2: output.defamations!(),
            }),
          };
        }

        if (ability1 === 'defamations') {
          if (data.me === player1 || data.me === player2)
            return {
              alertText: output.defamationOnYouThenStack!({
                mech1: output.defamationOnYou!(),
                mech2: output.stacks!(),
              }),
            };

          if (data.me === player3 || data.me === player4)
            return {
              infoText: output.defamationsThenStackOnYou!({
                mech1: output.defamations!(),
                mech2: output.stackOnYou!(),
              }),
            };

          return {
            infoText: output.defamationsThenStacks!({
              mech1: output.defamations!(),
              mech2: output.stacks!(),
            }),
          };
        }
      },
    },
    {
      id: 'R12S Twisted Vision 4 Stack/Defamation Counter',
      // Used for keeping of which Twisted Vision 4 mechanic we are on
      // Note: B519 Heavy Slam and B517 Mana Burst cast regardless of players alive
      //       A B4F0 Unmitigated Impact will occur should the stack be missed
      // Note2: B518 Mana Burst seems to not cast if the target is dead, and there doesn't seem to be repercussions
      type: 'Ability',
      netRegex: { id: ['B519', 'B517'], source: 'Lindschrat', capture: false },
      condition: (data) => data.twistedVisionCounter === 4,
      suppressSeconds: 1,
      run: (data) => {
        data.twistedVision4MechCounter = data.twistedVision4MechCounter + 2; // Mechanic is done in pairs
      },
    },
    {
      id: 'R12S Twisted Vision 4 Stack/Defamation 2-4',
      type: 'Ability',
      netRegex: { id: ['B519', 'B517'], source: 'Lindschrat', capture: false },
      condition: (data) => data.twistedVisionCounter === 4 && data.twistedVision4MechCounter <= 6,
      suppressSeconds: 1,
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          stacks: Outputs.stacks,
          stackOnYou: Outputs.stackOnYou,
          defamations: {
            en: 'Avoid Defamations',
            de: 'Vermeide Ehrenstrafe',
            cn: '远离大圈',
            ko: '광역징 피하기',
          },
          defamationOnYou: Outputs.defamationOnYou,
          stacksThenDefamations: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          defamationsThenStacks: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          stacksThenDefamationOnYou: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          defamationsThenStackOnYou: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          stackOnYouThenDefamations: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          defamationOnYouThenStack: {
            en: '${mech1} => ${mech2}',
            de: '${mech1} => ${mech2}',
            cn: '${mech1} => ${mech2}',
            ko: '${mech1} => ${mech2}',
          },
          towers: {
            en: 'Tower Positions',
            de: 'Turm Positionen',
            fr: 'Position tour',
            ja: '塔の位置へ',
            cn: '塔站位',
            ko: '탑 자리잡기',
            tc: '八人塔站位',
          },
        };
        const count = data.twistedVision4MechCounter;
        const players = data.replication4BossCloneDirNumPlayers;
        const abilityIds = data.replication4DirNumAbility;
        const player1 = count === 2
          ? players[1]
          : count === 4
          ? players[2]
          : players[3];
        const player2 = count === 2
          ? players[5]
          : count === 4
          ? players[6]
          : players[7];
        const abilityId = count === 2
          ? abilityIds[1]
          : count === 4
          ? abilityIds[2]
          : abilityIds[3];

        if (
          abilityId === undefined || player1 === undefined ||
          player2 === undefined
        )
          return;

        const ability1 = abilityId === headMarkerData['manaBurstTether']
          ? 'defamations'
          : abilityId === headMarkerData['heavySlamTether']
          ? 'stacks'
          : 'unknown';

        if (count < 6) {
          const player3 = count === 2 ? players[2] : players[3];
          const player4 = count === 2 ? players[6] : players[7];
          if (player3 === undefined || player4 === undefined)
            return;

          if (ability1 === 'stacks') {
            if (data.me === player1 || data.me === player2)
              return {
                alertText: output.stackOnYouThenDefamations!({
                  mech1: output.stackOnYou!(),
                  mech2: output.defamations!(),
                }),
              };

            if (data.me === player3 || data.me === player4)
              return {
                infoText: output.stacksThenDefamationOnYou!({
                  mech1: output.stacks!(),
                  mech2: output.defamationOnYou!(),
                }),
              };

            return {
              infoText: output.stacksThenDefamations!({
                mech1: output.stacks!(),
                mech2: output.defamations!(),
              }),
            };
          }

          if (ability1 === 'defamations') {
            if (data.me === player1 || data.me === player2)
              return {
                alertText: output.defamationOnYouThenStack!({
                  mech1: output.defamationOnYou!(),
                  mech2: output.stacks!(),
                }),
              };
            if (data.me === player3 || data.me === player4)
              return {
                infoText: output.defamationsThenStackOnYou!({
                  mech1: output.defamations!(),
                  mech2: output.stackOnYou!(),
                }),
              };

            return {
              infoText: output.defamationsThenStacks!({
                mech1: output.defamations!(),
                mech2: output.stacks!(),
              }),
            };
          }
        }

        // Last set followed up with tower positions
        if (ability1 === 'stacks') {
          if (data.me === player1 || data.me === player2)
            return {
              alertText: output.stackOnYouThenDefamations!({
                mech1: output.stackOnYou!(),
                mech2: output.towers!(),
              }),
            };

          return {
            infoText: output.stacksThenDefamations!({
              mech1: output.stacks!(),
              mech2: output.towers!(),
            }),
          };
        }

        if (ability1 === 'defamations') {
          if (data.me === player1 || data.me === player2)
            return {
              alertText: output.defamationOnYouThenStack!({
                mech1: output.defamationOnYou!(),
                mech2: output.towers!(),
              }),
            };

          return {
            infoText: output.defamationsThenStacks!({
              mech1: output.defamations!(),
              mech2: output.towers!(),
            }),
          };
        }
      },
    },
    {
      id: 'R12S Twisted Vision 5 Towers (Early)',
      // Calls on last stack or defamation
      type: 'Ability',
      netRegex: { id: ['B519', 'B517'], source: 'Lindschrat', capture: false },
      condition: (data) => data.twistedVisionCounter === 4 && data.twistedVision4MechCounter > 6,
      durationSeconds: 5,
      suppressSeconds: 9999,
      infoText: (_data, _matches, output) => output.towers!(),
      outputStrings: {
        towers: {
          en: 'Tower Positions',
          de: 'Turm Positionen',
          fr: 'Position tour',
          ja: '塔の位置へ',
          cn: '塔站位',
          ko: '탑 자리잡기',
          tc: '八人塔站位',
        },
      },
    },
    {
      id: 'R12S Twisted Vision 5 Towers',
      // TODO: Get Position of the towers and player side and state the front/left back/right
      // Towers aren't visible until after cast, but you would have 4.4s to adjust if the trigger was delayed
      // 4s castTime
      type: 'StartsUsing',
      netRegex: { id: 'BBE2', source: 'Lindwurm', capture: true },
      condition: (data) => data.twistedVisionCounter === 5,
      durationSeconds: (_data, matches) => parseFloat(matches.castTime) + 4.1,
      alertText: (data, _matches, output) => {
        if (data.hasLightResistanceDown)
          return output.fireEarthTower!();
        return output.holyTower!();
      },
      outputStrings: {
        fireEarthTower: {
          en: 'Soak Fire/Earth Meteor',
          de: 'Nimm Feuer/Erd Meteor',
          cn: '踩火/土陨石塔',
          ko: '불/땅 메테오 밟기',
        },
        holyTower: {
          en: 'Soak a White/Star Meteor',
          de: 'Nimm Weißen/Stern Meteor',
          cn: '踩光/彩色陨石塔',
          ko: '바람/어둠 메테오 밟기',
        },
      },
    },
    {
      id: 'R12S Cosmic Kiss Tower Collect',
      // Map the Cosmic Kiss player is hit by back to the tower type
      // Actors will be at the same locations as the towers
      type: 'Ability',
      netRegex: { id: 'B4F4', source: 'Lindwurm', capture: true },
      condition: Conditions.targetIsYou(),
      delaySeconds: 0.1, // Need to delay for position data to update
      run: (data, matches) => {
        const actor = data.actorPositions[matches.sourceId];
        const towers = data.cosmicKissPattern;
        if (actor === undefined || towers.length !== 4)
          return;

        const x = actor.x;
        const y = actor.y;

        if ((x > 81 && x < 83) || (x > 109 && x < 111)) {
          data.myCosmicKiss = data.cosmicKissPattern[y < center.y ? 0 : 2];
        } else if ((x > 89 && x < 91) || (x > 117 && x < 119)) {
          data.myCosmicKiss = data.cosmicKissPattern[y < center.y ? 1 : 3];
        }
      },
    },
    {
      id: 'R12S Hot-blooded Collect',
      // Player can still cast, but shouldn't move for 5s duration
      type: 'GainsEffect',
      netRegex: { effectId: '12A0', capture: true },
      condition: Conditions.targetIsYou(),
      run: (data, _matches) => data.hasPyretic = true,
    },
    {
      id: 'R12S Hot-blooded',
      // Player can still cast, but shouldn't move for 5s duration
      type: 'GainsEffect',
      netRegex: { effectId: '12A0', capture: true },
      condition: Conditions.targetIsYou(),
      durationSeconds: (_data, matches) => parseFloat(matches.duration),
      response: Responses.stopMoving(),
    },
    {
      id: 'R12S Idyllic Dream Lindwurm\'s Stone III',
      // 5s castTime
      type: 'StartsUsing',
      netRegex: { id: 'B4F7', source: 'Lindwurm', capture: true },
      condition: (data) => {
        // Avoid simultaneous trigger for Pyretic player as they wouldn't be at the earth location
        if (data.hasPyretic)
          return false;
        // Handle this in Doom cleanse instead
        if (data.CanCleanse())
          return false;
        return true;
      },
      durationSeconds: (_data, matches) => parseFloat(matches.castTime),
      suppressSeconds: 1,
      promise: async (data) => {
        const combatants = (await callOverlayHandler({
          call: 'getCombatants',
          names: [data.me],
        })).combatants;
        const me = combatants[0];
        if (combatants.length !== 1 || me === undefined) {
          console.error(
            `R12S Idyllic Dream Lindwurm's Stone III: Wrong combatants count ${combatants.length}`,
          );
          return;
        }

        const x = me.PosX;
        if (x < center.x)
          data.myPlatform = 'west';
        else
          data.myPlatform = 'east';
      },
      infoText: (data, _matches, output) => {
        const pattern = data.cosmicKissPattern;
        const platform = data.myPlatform;
        if (pattern.length !== 4 || platform === undefined)
          return output.avoidEarthTower!({ dir: output.south!() });

        if (
          (pattern[0] === 'earth' && platform === 'west') ||
          (pattern[1] === 'earth' && platform === 'east')
        )
          return output.avoidEarthTower!({ dir: output.in!() });

        if (
          (pattern[0] === 'earth' && platform === 'east') ||
          (pattern[1] === 'earth' && platform === 'west')
        )
          return output.avoidEarthTower!({ dir: output.southIn!() });
      },
      outputStrings: {
        south: Outputs.south,
        in: Outputs.in,
        southIn: {
          en: 'South + In',
          de: 'Süden + Rein',
          cn: '下+内',
          ko: '남쪽 + 안으로',
        },
        avoidEarthTower: {
          en: '${dir} (Avoid Earth Tower)',
          de: '${dir} (Vermeide Erd-Turm)',
          cn: '${dir} (避开土塔)',
          ko: '${dir} (땅 탑 피하기)',
        },
      },
    },
    {
      id: 'R12S Doom Collect',
      // Happens about 1.3s after Dark Tower when it casts B4F6 Lindwurm's Dark II
      type: 'GainsEffect',
      netRegex: { effectId: 'D24', capture: true },
      run: (data, matches) => data.doomPlayers.push(matches.target),
    },
    {
      id: 'R12S Doom Cleanse',
      type: 'GainsEffect',
      netRegex: { effectId: 'D24', capture: false },
      condition: (data) => data.CanCleanse(),
      delaySeconds: 0.1,
      suppressSeconds: 1,
      promise: async (data) => {
        const combatants = (await callOverlayHandler({
          call: 'getCombatants',
          names: [data.me],
        })).combatants;
        const me = combatants[0];
        if (combatants.length !== 1 || me === undefined) {
          console.error(
            `R12S Doom Cleanse: Wrong combatants count ${combatants.length}`,
          );
          return;
        }

        const x = me.PosX;
        if (x < center.x)
          data.myPlatform = 'west';
        else
          data.myPlatform = 'east';
      },
      infoText: (data, _matches, output) => {
        const pattern = data.cosmicKissPattern;
        const platform = data.myPlatform;
        let dir;
        if (pattern.length !== 4 || platform === undefined)
          dir = 'south';
        else if (
          (pattern[0] === 'earth' && platform === 'west') ||
          (pattern[1] === 'earth' && platform === 'east')
        )
          dir = 'in';
        else if (
          (pattern[0] === 'earth' && platform === 'east') ||
          (pattern[1] === 'earth' && platform === 'west')
        )
          dir = 'southIn';
        if (dir === undefined)
          dir = 'south';

        const players = data.doomPlayers;
        if (players.length > 2) {
          // Mechanic was messed up, but recoverable
          // Pyretic player shouldn't move and shouldn't need to avoid earth
          if (data.hasPyretic)
            return output.cleanseDooms!();
          return output.mech!({
            cleanse: output.cleanseDooms!(),
            avoid: output.avoidEarthTower!({ dir: output[dir]!() }),
          });
        }
        if (players.length === 2) {
          const target1 = data.party.member(data.doomPlayers[0]);
          const target2 = data.party.member(data.doomPlayers[1]);
          if (data.hasPyretic)
            return output.cleanseDoom2!({ target1: target1, target2: target2 });
          return output.mech!({
            cleanse: output.cleanseDoom2!({ target1: target1, target2: target2 }),
            avoid: output.avoidEarthTower!({ dir: output[dir]!() }),
          });
        }
        if (players.length === 1) {
          const target1 = data.party.member(data.doomPlayers[0]);
          if (data.hasPyretic)
            return output.cleanseDoom!({ target: target1 });
          return output.mech!({
            cleanse: output.cleanseDoom!({ target: target1 }),
            avoid: output.avoidEarthTower!({ dir: output[dir]!() }),
          });
        }
      },
      outputStrings: {
        cleanseDooms: {
          en: 'Cleanse Doom(s)',
          de: 'Verhängnis Reinigen',
          cn: '康复死宣',
          ko: '죽음의 선고 에스나',
        },
        cleanseDoom: {
          en: 'Cleanse ${target}',
          de: 'Reinige ${target}',
          fr: 'Guérison sur ${target}',
          cn: '康复 ${target}',
          ko: '${target} 에스나',
          tc: '康復 ${target}',
        },
        cleanseDoom2: {
          en: 'Cleanse ${target1}/${target2}',
          de: 'Reinige ${target1}/${target2}',
          cn: '康复 ${target1}/${target2}',
          ko: '${target1}/${target2} 에스나',
        },
        south: Outputs.south,
        in: Outputs.in,
        southIn: {
          en: 'South + In',
          de: 'Süden + Rein',
          cn: '下+内',
          ko: '남쪽 + 안으로',
        },
        avoidEarthTower: {
          en: '${dir}',
          de: '${dir}',
          cn: '${dir}',
          ko: '${dir}',
        },
        mech: {
          en: '${cleanse} + ${avoid}',
          de: '${cleanse} + ${avoid}',
          cn: '${cleanse} + ${avoid}',
          ko: '${cleanse} + ${avoid}',
        },
      },
    },
    {
      id: 'R12S Avoid Earth Tower (Missing Dooms)',
      // Handle scenario where both Dooms end up not being applied
      // Triggering on the Lindwurm's Dark II ability that would apply Doom
      type: 'Ability',
      netRegex: { id: 'B4F6', capture: false },
      condition: (data) => data.CanCleanse(),
      delaySeconds: 0.5, // Time until after Doom was expected
      suppressSeconds: 9999,
      promise: async (data) => {
        const combatants = (await callOverlayHandler({
          call: 'getCombatants',
          names: [data.me],
        })).combatants;
        const me = combatants[0];
        if (combatants.length !== 1 || me === undefined) {
          console.error(
            `R12S Doom Cleanse: Wrong combatants count ${combatants.length}`,
          );
          return;
        }

        const x = me.PosX;
        if (x < center.x)
          data.myPlatform = 'west';
        else
          data.myPlatform = 'east';
      },
      infoText: (data, _matches, output) => {
        if (data.doomPlayers[0] === undefined) {
          const pattern = data.cosmicKissPattern;
          const platform = data.myPlatform;
          if (pattern.length !== 4 || platform === undefined)
            return output.avoidEarthTower!({ dir: output.south!() });

          if (
            (pattern[0] === 'earth' && platform === 'west') ||
            (pattern[1] === 'earth' && platform === 'east')
          )
            return output.avoidEarthTower!({ dir: output.in!() });

          if (
            (pattern[0] === 'earth' && platform === 'east') ||
            (pattern[1] === 'earth' && platform === 'west')
          )
            return output.avoidEarthTower!({ dir: output.southIn!() });
        }
      },
      outputStrings: {
        south: Outputs.south,
        in: Outputs.in,
        southIn: {
          en: 'South + In',
          de: 'Süden + Rein',
          cn: '下+内',
          ko: '남쪽 + 안으로',
        },
        avoidEarthTower: {
          en: '${dir} (Avoid Earth Tower)',
          de: '${dir} (Vermeide Erd-Turm)',
          cn: '${dir} (避开土塔)',
          ko: '${dir} (땅 탑 피하기)',
        },
      },
    },
    {
      id: 'R12S Nearby and Faraway Portent',
      // 129D Lindwurm's Portent prevents stacking the portents
      // 129E Farwaway Portent, Always on wind player
      // 129F Nearby Portent, Always on Dark player
      // 10s duration, need to delay to avoid earth + doom trigger overlap
      // This would go out to players that soaked white/holy meteors
      type: 'GainsEffect',
      netRegex: { effectId: ['129E', '129F'], capture: true },
      condition: Conditions.targetIsYou(),
      delaySeconds: (_data, matches) => parseFloat(matches.duration) - 5.3,
      infoText: (data, matches, output) => {
        if (matches.effectId === '129E') {
          switch (data.triggerSetConfig.portentStrategy) {
            case 'dn':
              return output.farOnYouWindDN!();
            case 'zenith':
              return output.farOnYouWindZenith!();
            case 'nukemaru':
              return output.farOnYouWindNukemaru!();
          }
          return output.farOnYouWind!();
        }
        switch (data.triggerSetConfig.portentStrategy) {
          case 'dn':
            return output.nearOnYouDarkDN!();
          case 'zenith':
            return output.nearOnYouDarkZenith!();
          case 'nukemaru':
            return output.nearOnYouDarkNukemaru!();
        }
        return output.nearOnYouDark!();
      },
      outputStrings: {
        nearOnYouDarkDN: {
          en: 'Near on YOU: Be on Hitbox N',
          de: 'Nah auf DIR: Sei auf der Hitbox im Norden',
          cn: '近点名: 站上边判定圈',
          ko: '근거리 대상자: 히트박스 북쪽에 서기',
        },
        nearOnYouDarkZenith: {
          en: 'Near on YOU: Be on Middle Hitbox (Lean North)',
          de: 'Nah auf DIR: Sei auf der Hitbox in der Mitte (etwas nördlich)',
          cn: '近点名: 站中间判定圈 (偏上)',
          ko: '근거리 대상자: 히트박스 중앙에 서기 (약간 북쪽)',
        },
        nearOnYouDarkNukemaru: {
          en: 'Near on YOU: Max Melee S (Near Outer Player)',
          de: 'Nah auf DIR: Max Nahkampf im Süden (Nahe äußerem Spieler)',
          cn: '近点名: 下边最远近战距离 (靠近外侧玩家)',
          ko: '근거리 대상자: 남쪽 칼끝딜 (바깥 플레이어 가까이)',
        },
        nearOnYouDark: {
          en: 'Dark: Near on YOU',
          de: 'Dunkel: Nah auf DIR',
          cn: '暗: 近点名',
          ko: '어둠: 근거리 대상자',
        },
        farOnYouWindDN: {
          en: 'Far on YOU: Be on Middle Hitbox',
          de: 'Fern auf DIR: Sei auf der Mittleren Hitbox',
          cn: '远点名: 站中间判定圈',
          ko: '원거리 대상자: 히트박스 중앙에 서기',
        },
        farOnYouWindZenith: {
          en: 'Far on YOU: Max Melee N',
          de: 'Fern auf DIR: Maximaler Nahkampf im Norden',
          cn: '远点名: 上边最远近战距离',
          ko: '원거리 대상자: 북쪽 칼끝딜',
        },
        farOnYouWindNukemaru: {
          en: 'Far on YOU: Be on Hitbox S',
          de: 'Fern auf DIR: Auf der Hitbox im Süden',
          cn: '远点名: 站下边判定圈',
          ko: '원거리 대상자: 히트박스 남쪽에 서기',
        },
        farOnYouWind: {
          en: 'Wind: Far on YOU',
          de: 'Wind: Fern auf DIR',
          cn: '风: 远点名',
          ko: '바람: 원거리 대상자',
        },
      },
    },
    {
      id: 'R12S Nearby and Faraway Portent Baits',
      // This would go out on players that soaked fire/earth meteors
      type: 'GainsEffect',
      netRegex: { effectId: ['129E', '129F'], capture: true },
      condition: (data) => data.hasLightResistanceDown,
      delaySeconds: (_data, matches) => parseFloat(matches.duration) - 5.3,
      suppressSeconds: 1,
      infoText: (data, _matches, output) => {
        if (data.hasPyretic) {
          switch (data.triggerSetConfig.portentStrategy) {
            case 'dn':
              return output.baitFireDN!();
            case 'zenith':
              return output.baitFireZenith!();
            case 'nukemaru':
              return output.baitFireNukemaru!();
          }
          return output.baitFire!();
        }
        switch (data.triggerSetConfig.portentStrategy) {
          case 'dn':
            return output.baitEarthDN!();
          case 'zenith':
            return output.baitEarthZenith!();
          case 'nukemaru':
            return output.baitEarthNukemaru!();
        }
        return output.baitEarth!();
      },
      outputStrings: {
        baitFireDN: {
          en: 'Bait Cone N Center Below Dark/S Center',
          de: 'Köder Kegel-AoE Norden Mitte Darunter Dunkel/Süden Mitte',
          cn: '诱导扇形: 北侧中心, 暗下方/南侧中心',
          ko: '부채꼴 유도 북쪽 어둠 밑/남쪽 중앙',
        },
        baitFireZenith: {
          en: 'Bait Cone S, Max Melee',
          de: 'Köder Kegel-AoE S, Max Nahkampf',
          cn: '诱导扇形: 南侧, 最远近战距离',
          ko: '부채꼴 유도 남쪽, 칼끝딜',
        },
        baitFireNukemaru: {
          en: 'Bait Cone, N of Platform/S Max Melee',
          de: 'Köder Kegel-AoE, Norden der Plattform/ Süden Max Nahkampf',
          cn: '诱导扇形: 平台北侧/南侧最远近战距离',
          ko: '부채꼴 유도, 플랫폼 북쪽/남쪽 칼끝딜',
        },
        baitFire: {
          en: 'Fire: Bait Cone',
          de: 'Feuer: Köder Kegel-AoE',
          cn: '火: 诱导扇形',
          ko: '불: 부채꼴 유도',
        },
        baitEarthDN: {
          en: 'Bait Cone N Center Below Dark/S Center',
          de: 'Köder Kegel-AoE Norden Mitte Darunter Dunkel/Süden Mitte',
          cn: '诱导扇形: 北侧中心, 暗下方/南侧中心',
          ko: '부채꼴 유도 북쪽 어둠 밑/남쪽 중앙',
        },
        baitEarthZenith: {
          en: 'Bait Cone Middle, Max Melee (Lean North)',
          de: 'Köder Kegel-AoE Mitte, Max Nahkampf (etwas nördlich)',
          cn: '诱导扇形: 中间, 最远近战距离 (偏北)',
          ko: '부채꼴 유도 중앙, 칼끝딜 (약간 북쪽)',
        },
        baitEarthNukemaru: {
          en: 'Bait Cone, S Max Melee/N of Platform',
          de: 'Köder Kegel-AoE, Süden Max Nahkampf/ Norden der Plattform',
          cn: '诱导扇形: 南侧最远近战距离/平台北侧',
          ko: '부채꼴 유도, 남쪽 칼끝딜/플랫폼 북쪽',
        },
        baitEarth: {
          en: 'Earth: Bait Cone',
          de: 'Erde: Köder Kegel-AoE',
          cn: '土: 诱导扇形',
          ko: '땅: 부채꼴 유도',
        },
      },
    },
    {
      id: 'R12S Temporal Curtain Part 1 Collect',
      // Describe actor going into portal
      type: 'Ability',
      netRegex: { id: 'B51D', source: 'Lindschrat', capture: true },
      run: (data, matches) => {
        switch (matches.sourceId) {
          case data.idyllicDreamActorEW:
            data.idyllicVision8SafeSides = 'frontBack';
            return;
          case data.idyllicDreamActorNS:
            data.idyllicVision8SafeSides = 'sides';
        }
      },
    },
    {
      id: 'R12S Temporal Curtain Part 1',
      // Describe actor going into portal
      type: 'Ability',
      netRegex: { id: 'B51D', source: 'Lindschrat', capture: true },
      infoText: (data, matches, output) => {
        switch (matches.sourceId) {
          case data.idyllicDreamActorEW:
            return output.frontBackLater!();
          case data.idyllicDreamActorNS:
            return output.sidesLater!();
        }
      },
      outputStrings: {
        frontBackLater: {
          en: 'Portal + Under Boss (later)',
          de: 'Portal + Unter den Boss (später)',
          cn: '传送 + Boss脚下 (稍后)',
          ko: '포탈 + 앞/뒤 분신 (나중에)',
        },
        sidesLater: {
          en: 'Portal + E/W of Clone (later)',
          de: 'Portal + O/W vom Klon (später)',
          cn: '传送 + 分身左/右 (稍后)',
          ko: '포탈 + 양 옆 분신 (나중에)',
        },
      },
    },
    {
      id: 'R12S Temporal Curtain Part 2 Collect',
      // Describe actor going into portal
      type: 'AbilityExtra',
      netRegex: { id: 'B4D9', capture: true },
      run: (data, matches) => {
        switch (matches.sourceId) {
          case data.idyllicDreamActorEW:
            data.idyllicVision7SafeSides = 'frontBack';
            return;
          case data.idyllicDreamActorNS:
            data.idyllicVision7SafeSides = 'sides';
            return;
          case data.idyllicDreamActorSnaking: {
            const x = parseFloat(matches.x);
            data.idyllicVision7SafePlatform = x < 100 ? 'east' : 'west';
          }
        }
      },
    },
    {
      id: 'R12S Temporal Curtain Part 2',
      // Describe actor going into portal
      type: 'AbilityExtra',
      netRegex: { id: 'B4D9', capture: false },
      infoText: (data, _matches, output) => {
        if (data.idyllicVision7SafeSides === 'frontBack') {
          if (data.idyllicVision7SafePlatform === 'east')
            return output.frontBackEastLater!();
          if (data.idyllicVision7SafePlatform === 'west')
            return output.frontBackWestLater!();
        }
        if (data.idyllicVision7SafeSides === 'sides') {
          if (data.idyllicVision7SafePlatform === 'east')
            return output.sidesEastLater!();
          if (data.idyllicVision7SafePlatform === 'west')
            return output.sidesWestLater!();
        }
      },
      outputStrings: {
        frontBackWestLater: {
          en: 'West Platform => N/S of Clone (later)',
          de: 'Westen Platform => N/S des Klones (später)',
          cn: '左平台 + 分身上/下 (稍后)',
          ko: '서쪽 플랫폼 => 앞/뒤 분신 (나중에)',
        },
        sidesWestLater: {
          en: 'West Platform => Under Boss (later)',
          de: 'Westen Platform => Unter den Boss (später)',
          cn: '左平台 + Boss脚下 (稍后)',
          ko: '서쪽 플랫폼 => 양 옆 분신 (나중에)',
        },
        frontBackEastLater: {
          en: 'East Platform => N/S of Clone (later)',
          de: 'Ost Platform => N/S des Klones (später)',
          cn: '右平台 + 分身上/下 (稍后)',
          ko: '동쪽 플랫폼 => 앞/뒤 분신 (나중에)',
        },
        sidesEastLater: {
          en: 'East Platform => Under Boss (later)',
          de: 'Ost Platform => Unter den Boss (später)',
          cn: '右平台 + Boss脚下 (稍后)',
          ko: '동쪽 플랫폼 => 양 옆 분신 (나중에)',
        },
      },
    },
    {
      id: 'R12S Twisted Vision 6 Light Party Stacks',
      // At end of cast it's cardinal or intercard
      type: 'Ability',
      netRegex: { id: 'BBE2', source: 'Lindwurm', capture: false },
      condition: (data) => data.twistedVisionCounter === 6,
      alertText: (data, _matches, output) => {
        const first = data.replication3CloneOrder[0];
        if (first === undefined)
          return;
        const dirNumOrder = first % 2 === 0 ? [0, 2, 4, 6] : [1, 3, 5, 7];

        // Need to lookup what ability is at each dir, only need cards or intercard dirs
        const abilities = data.replication4AbilityOrder.splice(0, 4);
        const stackDirs = [];
        let i = 0;

        // Find first all stacks in cards or intercards
        // Incorrect amount means players made an unsolvable? run
        for (const dirNum of dirNumOrder) {
          if (abilities[i++] === headMarkerData['heavySlamTether'])
            stackDirs.push(dirNum);
        }
        // Only grabbing first two
        const dirNum1 = stackDirs[0];
        const dirNum2 = stackDirs[1];

        // If we failed to get two stacks, just output generic cards/intercards reminder
        if (dirNum1 === undefined || dirNum2 === undefined) {
          return first % 2 === 0 ? output.cardinals!() : output.intercards!();
        }
        const dir1 = Directions.output8Dir[dirNum1] ?? 'unknown';
        const dir2 = Directions.output8Dir[dirNum2] ?? 'unknown';
        return output.stack!({ dir1: output[dir1]!(), dir2: output[dir2]!() });
      },
      outputStrings: {
        ...Directions.outputStrings8Dir,
        cardinals: Outputs.cardinals,
        intercards: Outputs.intercards,
        stack: {
          en: 'Stack ${dir1}/${dir2} + Lean Middle Out',
          de: 'Sammeln ${dir1}/${dir2} + Etwas Mittig Außen',
          cn: '${dir1}/${dir2}分摊 + 偏向中间外侧',
          ko: '${dir1}/${dir2} 쉐어 + 중앙 밖으로 약간 빼기',
        },
      },
    },
    {
      id: 'R12S Twisted Vision 7 Safe Platform',
      type: 'StartsUsing',
      netRegex: { id: 'BBE2', source: 'Lindwurm', capture: true },
      condition: (data) => data.twistedVisionCounter === 7,
      durationSeconds: (_data, matches) => parseFloat(matches.castTime) + 4.5,
      infoText: (data, _matches, output) => {
        if (data.idyllicVision7SafeSides === 'frontBack') {
          if (data.idyllicVision7SafePlatform === 'east')
            return output.frontBackEastPlatform!();
          if (data.idyllicVision7SafePlatform === 'west')
            return output.frontBackWestPlatform!();
        }
        if (data.idyllicVision7SafeSides === 'sides') {
          if (data.idyllicVision7SafePlatform === 'east')
            return output.sidesEastPlatform!();
          if (data.idyllicVision7SafePlatform === 'west')
            return output.sidesWestPlatform!();
        }
        return output.safePlatform!();
      },
      outputStrings: {
        safePlatform: {
          en: 'Move to Safe Platform Side => Dodge Cleaves',
          de: 'Geh zur sicheren Platform-Seite => Cleave ausweichen',
          cn: '移动到安全平台侧 => 避开扇形',
          ko: '안전한 플랫폼 쪽으로 이동 => 부채꼴 피하기',
        },
        sidesWestPlatform: {
          en: 'West Platform => Under Boss',
          de: 'Westen Platform => Unter den Boss',
          cn: '左平台 + Boss脚下',
          ko: '서쪽 플랫폼 => 보스 밑',
        },
        sidesEastPlatform: {
          en: 'East Platform => Under Boss',
          de: 'Ost Platform => Unter den Boss',
          cn: '右平台 + Boss脚下',
          ko: '동쪽 플랫폼 => 보스 밑',
        },
        frontBackEastPlatform: {
          en: 'East Platform => N/S of Clone',
          de: 'Ost Platform => N/S des Klones',
          cn: '右平台 + 分身上/下',
          ko: '동쪽 플랫폼 => 분신 남/북',
        },
        frontBackWestPlatform: {
          en: 'West Platform => N/S of Clone',
          de: 'Westen Platform => N/S des Klones',
          cn: '左平台 + 分身上/下',
          ko: '서쪽 플랫폼 => 분신 남/북',
        },
      },
    },
    {
      id: 'R12S Twisted Vision 8 Light Party Stacks',
      // At end of cast it's cardinal or intercard
      type: 'StartsUsing',
      netRegex: { id: 'BBE2', source: 'Lindwurm', capture: false },
      condition: (data) => data.twistedVisionCounter === 8,
      alertText: (data, _matches, output) => {
        const first = data.replication3CloneOrder[0];
        if (first === undefined)
          return;
        const dirNumOrder = first % 2 !== 0 ? [0, 2, 4, 6] : [1, 3, 5, 7];

        // Need to lookup what ability is at each dir, only need cards or intercard dirs
        const abilities = data.replication4AbilityOrder.slice(4, 8);
        const stackDirs = [];
        let i = 0;

        // Find first all stacks in cards or intercards
        // Incorrect amount means players made an unsolvable? run
        for (const dirNum of dirNumOrder) {
          if (abilities[i++] === headMarkerData['heavySlamTether'])
            stackDirs.push(dirNum);
        }
        // Only grabbing first two
        const dirNum1 = stackDirs[0];
        const dirNum2 = stackDirs[1];

        // If we failed to get two stacks, just output generic cards/intercards reminder
        if (dirNum1 === undefined || dirNum2 === undefined) {
          return first % 2 !== 0 ? output.cardinals!() : output.intercards!();
        }
        const dir1 = Directions.output8Dir[dirNum1] ?? 'unknown';
        const dir2 = Directions.output8Dir[dirNum2] ?? 'unknown';
        return output.stack!({ dir1: output[dir1]!(), dir2: output[dir2]!() });
      },
      outputStrings: {
        ...Directions.outputStrings8Dir,
        cardinals: Outputs.cardinals,
        intercards: Outputs.intercards,
        stack: {
          en: 'Stack ${dir1}/${dir2} + Lean Middle Out',
          de: 'Sammeln ${dir1}/${dir2} + Etwas Mittig Außen',
          cn: '${dir1}/${dir2}分摊 + 偏向中间外侧',
          ko: '${dir1}/${dir2} 쉐어 + 중앙 바깥쪽으로 빼기',
        },
      },
    },
    {
      id: 'R12S Twisted Vision 8 Dodge Cleaves',
      // Trigger on Clone's BE5D Heavy Slam
      type: 'Ability',
      netRegex: { id: 'BE5D', source: 'Lindwurm', capture: false },
      condition: (data) => data.twistedVisionCounter === 8,
      alertText: (data, _matches, output) => {
        if (data.idyllicVision8SafeSides === 'sides')
          return output.sides!();
        if (data.idyllicVision8SafeSides === 'frontBack')
          return output.frontBack!();
      },
      run: (data) => {
        // Prevent re-execution of output
        delete data.idyllicVision8SafeSides;
      },
      outputStrings: {
        sides: {
          en: 'E/W of Clone',
          de: 'O/W des Klones',
          cn: '分身东/西',
          ko: '분신 동/서',
        },
        frontBack: {
          en: 'Under Boss',
          de: 'Unter den Boss',
          cn: 'Boss脚下',
          ko: '보스 밑',
        },
      },
    },
    {
      id: 'R12S Arcadian Hell 1',
      // B533 + B534 x4, Total ~280k Damage
      type: 'StartsUsing',
      netRegex: { id: 'B533', source: 'Lindwurm', capture: false },
      durationSeconds: 4.7,
      suppressSeconds: 9999,
      response: Responses.aoe(),
    },
    {
      id: 'R12S Arcadian Hell 2',
      // B533 + B535 x8, Total ~360k Damage
      type: 'StartsUsing',
      netRegex: { id: 'B535', source: 'Lindschrat', capture: false },
      durationSeconds: 4.7,
      suppressSeconds: 9999,
      response: Responses.bigAoe(),
    },
  ],
  timelineReplace: [
    {
      'locale': 'en',
      'replaceText': {
        'Netherwrath Near/Netherwrath Far': 'Netherwrath Near/Far',
        'Netherworld Near/Netherwworld Far': 'Netherworld Near/Far',
      },
    },
    {
      'locale': 'de',
      'replaceSync': {
        'Blood Vessel': 'Gliederzelle',
        'Lindschrat': 'abgespalten(?:e|er|es|en) Körper',
        'Lindwurm': 'Lindwurm',
        'Mana Sphere': 'Manasphäre',
        'Understudy': 'Mimikry-Zelle',
      },
      'replaceText': {
        '--bind--': '--fesseln--',
        '--boss clones': '--Boss Klone',
        '--clone takes portal--': '--Klon nimmt Portal--',
        '--clones(?! move)': '--Klone',
        '--clones move ': '--Klone bewegen sich',
        '--close shapes eaten--': '--nahe Formen absorbiert--',
        '--Doom x2--': '--Verhängnis x2--',
        '--e/w clones--': '--O/W Klone--',
        '--far shapes eaten--': '--ferne Formen absorbiert--',
        '--Hot-blooded x2--': '--Hitze des Kampfes x2--',
        '--locked tethers--': '--Verbindungen fest--',
        '--n/s clones--': '--N/S Klone--',
        '--ne/sw clones--': '--NO/SW Klone--',
        '--se/nw clones--': '--SO/NW Klone--',
        '--shapes--': '--Formen--',
        '--soaked shapes eaten--': '--genommene Formen absorbiert--',
        '--tether': '--Verbindung',
        '--untargetable\\?--': '--nicht anvisierbar?--',
        '\\(boss\\)': '(Boss)',
        '\\(cast\\)': '(wirken)',
        '\\(castbar\\)': '(vorbereiten)',
        '\\(clones\\)': '(Klone)',
        'Arcadia Aflame': 'Arkadische Flammen',
        'Arcadian Arcanum': 'Arkadion Arcanum',
        'Arcadian Hell': 'Arkadische Hölle',
        'Black Hole': 'schwarz(?:e|er|es|en) Loch',
        'Blood Mana': 'Manasphären',
        'Blood Wakening': 'Manasphären-Erweckung',
        'Bloody Burst': 'Ätherentfesselung',
        'Bring Down the House': 'Hals- und Bühnenbruch',
        '(?<! )Burst': 'Detonation',
        'Cell Shedding': 'Zelleliminierung',
        'Constrictor': 'Riesenschlangen-Würgegriff',
        'Cosmic Kiss': 'Einschlag',
        'Cruel Coil': 'Wurmwickel',
        'Double Sobat': 'Doppel-Sobat',
        'Down for the Count': 'Am Boden',
        'Downfall': 'Vollkommener Ruin',
        'Dramatic Lysis': 'Zellexplosion',
        'Esoteric Finisher': 'Mana-Kombi',
        'Feral Fission': 'Wildes Wurmgewühl',
        'Firefall Splash': 'Feuerfall-Schmetterer',
        'Fourth-wall Fusion': 'Zelldetonation',
        'Grand Entrance': 'Großer Auftritt',
        'Grotesquerie(?!: (Act|Curtain))': 'Zellbefall',
        'Grotesquerie: Act 1': 'Zellbefall: Prophase',
        'Grotesquerie: Act 2': 'Zellbefall: Metaphase',
        'Grotesquerie: Act 3': 'Zellbefall: Anaphase',
        'Grotesquerie: Curtain Call': 'Zellbefall: Telophase',
        'Heavy Slam': 'Heftiger Knall',
        'Hemorrhagic Projection': 'Gelenkte Schockwelle',
        'Idyllic Dream': 'Arkadischer Traum',
        'Lindwurm\'s Aero III': 'Lindwurm-Windga',
        'Lindwurm\'s Dark II': 'Lindwurm-Negra',
        'Lindwurm\'s Glare': 'Lindwurm-Blendung',
        'Lindwurm\'s Meteor': 'Lindwurm-Meteo',
        'Lindwurm\'s Stone III': 'Lindwurm-Steinga',
        'Lindwurm\'s Thunder II': 'Lindwurm-Blitzra',
        'Lindwurm\'s Water III': 'Lindwurm-Aquaga',
        'Mana Burst': 'Mana-Knall',
        'Metamitosis': 'Zellstreuung',
        'Mighty Magic': 'Macht der Magie',
        'Mortal Slayer': 'Tödliche Plage',
        'Mutating Cells': 'Mutationszellen',
        'Netherworld Far': 'Ferne Unterwelt',
        'Netherworld Near': 'Nahe Unterwelt',
        'Netherwrath Far': 'Ferner Zorn der Unterwelt',
        'Netherwrath Near': 'Naher Zorn der Unterwelt',
        'Phagocyte Spotlight': 'Zellabfall',
        'Power Gusher': 'Düsterer Dunkelquell',
        'Raptor Knuckles': 'Berstender Knöchel',
        'Ravenous Reach': 'Zellulärstreckung',
        'Reenactment': 'Memento Mimikry',
        'Refreshing Overkill': 'Übertödliche Überheilung',
        'Replication': 'Zellteilung',
        'Roiling Mass': 'Zellmutation',
        'Scalding Waves': 'Siedende Wogen',
        'Serpentine Scourge': 'Geißelatem',
        'Sideways Fire II': 'Horizontales Feura',
        'Skinsplitter': 'Hautteiler',
        'Slaughtershed': 'Zellenriss',
        'Snaking Kick': 'Natterntritt',
        'Splattershed': 'Venenriss',
        'Split Scourge': 'Streuplage',
        'Staging': 'Mimikry-Zellen',
        'Straightforward Thunder II': 'Vertikales Blitzra',
        'Temporal Curtain': 'Raumspalt',
        'The Fixer': 'Retter des Arkadions',
        'Timeless Spite': 'Zorneswelle der Unterwelt',
        'Top-tier Slam': 'Elite-Faustschlag',
        'Twisted Vision': 'Raumverschiebung',
        'Venomous Scourge': 'Tropfplage',
        'Visceral Burst': 'Eingeweideriss',
        'Wailing Wave': 'Welle der Unterwelt',
        'Winged Scourge': 'Geflügelte Plage',
      },
    },
    {
      'locale': 'fr',
      'missingTranslations': true,
      'replaceSync': {
        'Blood Vessel': 'cellule enchaînée',
        'Lindschrat': 'clone humanoïde',
        'Lindwurm': 'Lindwurm',
        'Mana Sphere': 'sphère de mana',
        'Understudy': 'cellule mimétique',
      },
      'replaceText': {
        'Arcadia Aflame': 'Enfer arcadien',
        'Arcadian Arcanum': 'Merveilles de l\'inconnu',
        'Arcadian Hell': 'Enfer arcadien',
        'Black Hole': 'trou noir',
        'Blood Mana': 'Sphère de mana',
        'Blood Wakening': 'Sphère de mana éveillée',
        'Bloody Burst': 'Dissipation magique',
        'Bring Down the House': 'Effondrement brutal',
        '(?<! )Burst': 'Grosse explosion',
        'Cell Shedding': 'Oblitération cellulaire',
        'Constrictor': 'Étreinte mortelle',
        'Cosmic Kiss': 'Impact',
        'Cruel Coil': 'Spirale cruelle',
        'Double Sobat': 'Double coup de pied',
        'Down for the Count': 'Au tapis',
        'Downfall': 'Effondrement absolu',
        'Dramatic Lysis': 'Explosion cellulaire',
        'Esoteric Finisher': 'Combinaison de mana',
        'Feral Fission': 'Fission sauvage',
        'Firefall Splash': 'Écrasement embrasé',
        'Fourth-wall Fusion': 'Déflagration cellulaire',
        'Grand Entrance': 'Entrée triomphale',
        'Grotesquerie(?!: (Act|Curtain))': 'Cellules parasites',
        'Grotesquerie:(?! )': 'Cellules parasites',
        'Grotesquerie: Act 1': 'Cellules parasites : premier acte',
        'Grotesquerie: Act 2': 'Cellules parasites : deuxième acte',
        'Grotesquerie: Act 3': 'Cellules parasites : troisième acte',
        'Grotesquerie: Curtain Call': 'Cellules parasites : rappel',
        'Heavy Slam': 'Claque puissante',
        'Hemorrhagic Projection': 'Onde de choc directionnelle',
        'Idyllic Dream': 'Rêve arcadien',
        'Lindwurm\'s Aero III': 'Méga Vent du Lindwurm',
        'Lindwurm\'s Dark II': 'Fusion du Lindwurm',
        'Lindwurm\'s Glare': 'Obscurité du Lindwurm',
        'Lindwurm\'s Meteor': 'Météores du Lindwurm',
        'Lindwurm\'s Stone III': 'Méga Terre du Lindwurm',
        'Lindwurm\'s Thunder II': 'Extra Foudre du Lindwurm',
        'Lindwurm\'s Water III': 'Méga Eau du Lindwurm',
        'Mana Burst': 'Explosion de mana',
        'Metamitosis': 'Dissémination cellulaire',
        'Mighty Magic': 'Magie suprême',
        'Mortal Slayer': 'Pestilence mortelle',
        'Mutating Cells': 'Cellules mutantes',
        'Netherworld Far': 'Outre-monde : éloigné',
        'Netherworld Near': 'Outre-monde : proche',
        'Netherwrath Far': 'Rage d\'outre-monde : éloignée',
        'Netherwrath Near': 'Rage d\'outre-monde : proche',
        'Phagocyte Spotlight': 'Chute cellulaire',
        'Power Gusher': 'Déferlement de puissance',
        'Raptor Knuckles': 'Poing du prédateur',
        'Ravenous Reach': 'Voracité putride',
        'Reenactment': 'Reconstitution',
        'Refreshing Overkill': 'Soin extrême et frappe suprême',
        'Replication': 'Réplication',
        'Roiling Mass': 'Mutation cellulaire',
        'Scalding Waves': 'Ondes enflammées',
        'Serpentine Scourge': 'Souffle pestilentiel',
        'Sideways Fire II': 'Extra Feu horizontal',
        'Skinsplitter': 'Fendeur de chair',
        'Slaughtershed': 'Abattoir',
        'Snaking Kick': 'Coup de pied du serpent',
        'Splattershed': 'Pulvérisation de chair',
        'Split Scourge': 'Pestilence fractionnée',
        'Staging': 'Cellules mimétiques',
        'Straightforward Thunder II': 'Extra Foudre verticale',
        'Temporal Curtain': 'Rupture spatiale',
        'The Fixer': 'Sauveur de l\'Arcadion',
        'Timeless Spite': 'Rage d\'outre-monde : vague',
        'Top-tier Slam': 'Charge de haut rang',
        'Twisted Vision': 'Projection mentale',
        'Venomous Scourge': 'Pestilence larguée',
        'Visceral Burst': 'Explosion viscérale',
        'Wailing Wave': 'Outre-monde : vague',
        'Winged Scourge': 'Pestilence volante',
      },
    },
    {
      'locale': 'ja',
      'missingTranslations': true,
      'replaceSync': {
        'Blood Vessel': '連環細胞',
        'Lindschrat': '人型分体',
        'Lindwurm': 'リンドブルム',
        'Mana Sphere': 'マナスフィア',
        'Understudy': '模倣細胞',
      },
      'replaceText': {
        'Arcadia Aflame': 'アルカディアン・フレイム',
        'Arcadian Arcanum': 'アルカディアン・アルケイナム',
        'Arcadian Hell': 'アルカディアン・ヘル',
        'Black Hole': 'ブラックホール',
        'Blood Mana': 'マナスフィア',
        'Blood Wakening': 'マナスフィア・アウェイク',
        'Bloody Burst': '魔力放散',
        'Bring Down the House': 'ブリングダウン・ハウス',
        '(?<! )Burst': '大爆発',
        'Cell Shedding': '細胞消失',
        'Constrictor': 'コンストリクター',
        'Cosmic Kiss': '着弾',
        'Cruel Coil': 'クルエルコイル',
        'Double Sobat': 'ダブルソバット',
        'Down for the Count': 'ノックダウン',
        'Downfall': 'ダウンフォール',
        'Dramatic Lysis': '細胞爆発',
        'Esoteric Finisher': 'マナコンビネーション',
        'Feral Fission': 'フェラルフィッション',
        'Firefall Splash': 'ファイアフォール・スプラッシュ',
        'Fourth-wall Fusion': '細胞重爆',
        'Grand Entrance': 'グランドエントランス',
        'Grotesquerie(?!: (Act|Curtain))': '細胞付着',
        'Grotesquerie:(?! )': '細胞付着',
        'Grotesquerie: Act 1': '細胞付着・前期',
        'Grotesquerie: Act 2': '細胞付着・中期',
        'Grotesquerie: Act 3': '細胞付着・後期',
        'Grotesquerie: Curtain Call': '細胞付着・終期',
        'Heavy Slam': 'ヘビースラム',
        'Hemorrhagic Projection': '指向性衝撃波',
        'Idyllic Dream': 'アルカディアン・ドリーム',
        'Lindwurm\'s Aero III': 'リンドブルム・エアロガ',
        'Lindwurm\'s Dark II': 'リンドブルム・ダーラ',
        'Lindwurm\'s Glare': 'リンドブルム・グレア',
        'Lindwurm\'s Meteor': 'リンドブルム・メテオ',
        'Lindwurm\'s Stone III': 'リンドブルム・ストンガ',
        'Lindwurm\'s Thunder II': 'リンドブルム・サンダラ',
        'Lindwurm\'s Water III': 'リンドブルム・ウォタガ',
        'Mana Burst': 'マナバースト',
        'Metamitosis': '細胞飛散',
        'Mighty Magic': 'マイティマジック',
        'Mortal Slayer': 'リーサルスカージ',
        'Mutating Cells': '変異細胞',
        'Netherworld Far': 'ネザーワールド・ファー',
        'Netherworld Near': 'ネザーワールド・ニア',
        'Netherwrath Far': 'ネザーレイジ・ファー',
        'Netherwrath Near': 'ネザーレイジ・ニア',
        'Phagocyte Spotlight': '細胞落着',
        'Power Gusher': 'パワーガッシャー',
        'Raptor Knuckles': 'ラプターナックル',
        'Ravenous Reach': 'ラヴェナスリーチ',
        'Reenactment': 'リエナクトメント',
        'Refreshing Overkill': 'オーバーキュア・オーバーキル',
        'Replication': 'レプリケーション',
        'Roiling Mass': '細胞変異',
        'Scalding Waves': '炎波',
        'Serpentine Scourge': 'スカージブレス',
        'Sideways Fire II': 'ホリゾンタルファイラ',
        'Skinsplitter': 'スキンスプリッター',
        'Slaughtershed': 'スローターシェッド',
        'Snaking Kick': 'スネークキック',
        'Splattershed': 'スプラッターシェッド',
        'Split Scourge': 'スプリットスカージ',
        'Staging': '模倣細胞',
        'Straightforward Thunder II': 'バーチカルサンダラ',
        'Temporal Curtain': '空間裂断',
        'The Fixer': 'フィクサー・オブ・アルカディア',
        'Timeless Spite': 'ネザーレイジ・ウェーブ',
        'Top-tier Slam': 'トップティアスラム',
        'Twisted Vision': '心象投影',
        'Venomous Scourge': 'ドロップスカージ',
        'Visceral Burst': 'ヴィセラルバースト',
        'Wailing Wave': 'ネザーワールド・ウェーブ',
        'Winged Scourge': 'ウィングドスカージ',
      },
    },
    {
      'locale': 'cn',
      'replaceSync': {
        'Blood Vessel': '连环细胞',
        'Lindschrat': '人形分身',
        'Lindwurm': '林德布鲁姆',
        'Mana Sphere': '魔力晶球',
        'Understudy': '模仿细胞',
      },
      'replaceText': {
        '--bind--': '--止步--',
        '--untargetable\\?--': '--不可选中?--',
        '--clones move': '--分身移动',
        '--clones(?! move| on)': '--分身',
        '--locked tethers--': '--连线锁定--',
        '--boss clones': '--boss 分身',
        '--tether(?!s)': '--连线',
        '--tethers--': '--连线--',
        '--black holes--': '--黑洞--',
        '--shapes--': '--碎片--',
        '--close shapes eaten--': '--近碎片被吃--',
        '--far shapes eaten--': '--远碎片被吃--',
        '--soaked shapes eaten--': '--挨撞碎片被吃--',
        '--Hot-blooded': '--热病',
        '--Doom': '--死宣',
        '--clone takes portal--': '--分身开始传送--',
        '--clones on platform--': '--分身抵达平台--',
        '--n/s clones--': '--南/北分身--',
        '--ne/sw clones--': '--东北/西南分身--',
        '--e/w clones--': '--东/西分身--',
        '--se/nw clones--': '--东南/西北分身--',
        '\\(boss': '(boss',
        '\\(clones': '(分身',
        '\\(cast\\)': '(咏唱)',
        '\\(castbar\\)': '(咏唱栏)',
        '\\(Enrage\\)': '(狂暴)',
        '\\(Enrage\\)\\?': '(狂暴)?',
        'Arcadia Aflame': '境中奇焰',
        'Arcadian Arcanum': '境中奇奥',
        'Arcadian Hell': '境中奇狱',
        'Black Hole(?!s)': '黑洞',
        'Blood Mana': '魔力球',
        'Blood Wakening': '魔力球苏醒',
        'Bloody Burst': '魔力扩散',
        'Bring Down the House': '震场',
        '(?<! )Burst': '大爆炸',
        'Cell Shedding': '细胞亡语',
        '(?<!-)Clone(?!s)': '分身',
        'Constrictor': '巨蟒绞缠',
        'Cosmic Kiss': '轰击',
        'Cruel Coil': '残暴拘束',
        'Double Sobat': '双重飞踢',
        'Down for the Count': '倒地不起',
        'Downfall': '陨落',
        'Dramatic Lysis': '细胞爆炸',
        'Esoteric Finisher': '魔力连击',
        'Feral Fission': '野性分裂',
        'Firefall Splash': '落火飞溅',
        'Fourth-wall Fusion': '细胞轰炸',
        'Grand Entrance': '盛大登场',
        'Grotesquerie(?!:)': '细胞附身',
        'Grotesquerie: Act 1': '细胞附身·早期',
        'Grotesquerie: Act 2': '细胞附身·中期',
        'Grotesquerie: Act 3': '细胞附身·晚期',
        'Grotesquerie: Curtain Call': '细胞附身·末期',
        'Heavy Slam': '重猛击',
        'Hemorrhagic Projection': '指向性冲击波',
        'Idyllic Dream': '境中奇梦',
        'Left': '左',
        'Lindwurm\'s Dark II': '林德布鲁姆昏暗',
        'Lindwurm\'s Glare': '林德布鲁姆闪耀',
        'Lindwurm\'s Meteor': '林德布鲁姆陨石',
        'Lindwurm\'s Stone III': '林德布鲁姆垒石',
        'Lindwurm\'s Thunder II': '林德布鲁姆震雷',
        'Mana Burst': '魔力爆发',
        'Metamitosis': '细胞飞散',
        'Mighty Magic': '强力魔法',
        'Mortal Slayer': '致命灾变',
        'Mutating Cells': '变异细胞',
        'Netherworld Far': '阴界远景',
        'Netherworld Near': '阴界近景',
        'Netherwrath Far': '远界阴怒',
        'Netherwrath Near': '近界阴怒',
        'Northeast': '东北',
        'Northwest': '西北',
        'Phagocyte Spotlight': '细胞落地',
        'Power Gusher': '力量喷涌',
        'Raptor Knuckles': '追猎重击',
        'Ravenous Reach': '极饿伸展',
        'Reenactment': '时空重现',
        'Refreshing Overkill': '过愈过伤',
        'Replication': '自我复制',
        'Right': '右',
        'Roiling Mass': '细胞变异',
        'Scalding Waves': '炎波',
        'Serpentine Scourge': '灾变吐息',
        'Skinsplitter': '蜕鳞',
        'Slaughtershed': '喋血',
        'Snaking Kick': '回旋蛇踢',
        'Splattershed': '溅血',
        'Split Scourge': '分裂灾变',
        'Staging': '模仿细胞',
        'Temporal Curtain': '空间裂断',
        'The Fixer': '补天之手',
        'Timeless Spite': '阴怒波',
        'Top-tier Slam': '天顶猛击',
        'Twisted Vision': '心象投影',
        'Venomous Scourge': '滴液灾变',
        'Visceral Burst': '脏腑爆裂',
        'Wailing Wave': '阴界波',
        'Winged Scourge': '有翼灾变',
      },
    },
    {
      'locale': 'ko',
      'replaceSync': {
        'Blood Vessel': '연환세포',
        'Lindschrat': '인간형 분열체',
        'Lindwurm': '린드블룸',
        'Mana Sphere': '마나 구체',
        'Understudy': '모방세포',
      },
      'replaceText': {
        '--bind--': '--속박--',
        '--untargetable\\?--': '--타겟 불가능?--',
        '--clones move': '--분신 이동',
        '--clones x': '--분신 x',
        '--locked tethers--': '--선 고정--',
        '--boss clones': '--보스 분신',
        '--tether (\\d)--': '--선 $1--',
        '--tethers--': '--선--',
        '--n/s clones--': '--북/남 분신--',
        '--ne/sw clones--': '--북동/남서 분신--',
        '--e/w clones--': '--동/서 분신--',
        '--se/nw clones--': '--남동/북서 분신--',
        '--black holes--': '--블랙홀--',
        '--shapes--': '--도형--',
        '--close shapes eaten--': '--가까운 도형 흡수--',
        '--far shapes eaten--': '--먼 도형 흡수--',
        '--soaked shapes eaten--': '--밟은 도형 흡수--',
        '--Hot-blooded': '--열기',
        '--Doom': '--죽음의 선고',
        '--clone takes portal--': '--분신이 포탈로--',
        '--clones on platform--': '--분신이 플랫폼에--',
        '\\(boss': '(보스',
        '\\(clones': '(분신',
        '\\(cast\\)': '(시전)',
        '\\(castbar\\)': '(시전바)',
        'Arcadia Aflame': '아르카디아의 화염',
        'Arcadian Arcanum': '아르카디아의 신비',
        'Arcadian Hell': '아르카디아의 지옥',
        'Black Hole(?!s)': '블랙홀',
        'Blood Mana': '마나 구체',
        'Blood Wakening': '마나 구체 각성',
        'Bloody Burst': '마력 발산',
        'Bring Down the House': '장내를 흔드는 갈채',
        '(?<! )Burst': '대폭발',
        'Cell Shedding': '세포 소실',
        'Clone( \\d)? Heavy Slam': '분신$1 묵직한 내려찍기',
        'Clone( \\d)? Mana Burst': '분신$1 마나 폭발',
        'Constrictor': '옥죄기',
        'Cosmic Kiss': '착탄',
        'Cruel Coil': '잔혹한 똬리',
        'Double Sobat': '연속 후려차기',
        'Down for the Count': '행동 불가',
        'Downfall': '운석 추락',
        'Dramatic Lysis': '세포 폭발',
        'Esoteric Finisher': '마나 연격',
        'Feral Fission': '야성적인 분열',
        'Firefall Splash': '불꽃 하강',
        'Fourth-wall Fusion': '세포 겹폭발',
        'Grand Entrance': '화려한 등장',
        'Grotesquerie(?!:)': '세포 부착',
        'Grotesquerie: Act 1': '세포 부착: 초기',
        'Grotesquerie: Act 2': '세포 부착: 중기',
        'Grotesquerie: Act 3': '세포 부착: 후기',
        'Grotesquerie: Curtain Call': '세포 부착: 말기',
        '(?<! )Heavy Slam': '묵직한 내려찍기',
        'Hemorrhagic Projection': '방향성 충격파',
        'Idyllic Dream': '아르카디아의 꿈',
        'Left': '왼쪽',
        'Lindwurm\'s Dark II': '린드블룸 다라',
        'Lindwurm\'s Glare': '린드블룸 글레어',
        'Lindwurm\'s Meteor': '린드블룸 메테오',
        'Lindwurm\'s Stone III': '린드블룸 스톤가',
        'Lindwurm\'s Thunder II': '린드블룸 선더라',
        '(?<! )Mana Burst': '마나 폭발',
        'Metamitosis': '세포 살포',
        'Mighty Magic': '만능 마법',
        'Mortal Slayer': '죽음의 재앙',
        'Mutating Cells': '변이세포',
        'Netherworld Near/Netherworld Far': '지하세계: 근거리/원거리',
        'Netherwrath Near/Netherwrath Far': '지하의 분노: 근거리/원거리',
        'Northeast': '북동쪽',
        'Northwest': '북서쪽',
        'Phagocyte Spotlight': '세포 낙하',
        'Power Gusher': '강력 분출',
        'Raptor Knuckles': '맹수의 주먹',
        'Ravenous Reach': '탐욕스러운 손길',
        'Reenactment': '재현',
        'Refreshing Overkill': '과잉치료, 과잉치사',
        'Replication': '자가 복제',
        'Right': '오른쪽',
        'Roiling Mass': '세포 변이',
        'Scalding Waves': '화염 파도',
        'Serpentine Scourge': '재앙의 숨결',
        'Skinsplitter': '뱀껍질 균열',
        'Slaughtershed': '살육의 허물',
        'Snaking Kick': '뱀발 후려차기',
        'Splattershed': '유혈의 허물',
        'Split Scourge': '분열된 재앙',
        'Staging': '모방세포',
        'Temporal Curtain': '공간 절단',
        'The Fixer': '아르카디아의 배후자',
        'Timeless Spite': '지하의 분노: 파동',
        'Top-tier Slam': '정상급 내려찍기',
        'Twisted Vision': '심상 투영',
        'Venomous Scourge': '재앙 투하',
        'Visceral Burst': '내장 파열',
        'Wailing Wave': '지하세계: 파동',
        'Winged Scourge': '날개 돋친 재앙',
      },
    },
  ],
};

export default triggerSet;
